import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule, DOCUMENT, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { Subscription } from 'rxjs';
import fileDownload from 'js-file-download';
import {
  AmbientLight,
  Box3,
  CanvasTexture,
  DoubleSide,
  LoadingManager,
  MathUtils,
  Mesh,
  MeshPhongMaterial,
  MOUSE,
  PerspectiveCamera,
  PointLight,
  Scene,
  Sphere,
  SRGBColorSpace,
  Vector3,
  WebGLRenderer,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { ColladaLoader } from 'three/examples/jsm/loaders/ColladaLoader.js';
// three.js ships types for the public examples/jsm API (via @types/three) but not for this
// internal collada/ subfolder. A local `declare module` augmentation doesn't work here either -
// TypeScript resolves the .js file on disk regardless of allowJs, so it treats the module as
// already-existing-but-untyped and refuses to let anything "augment" it (TS2665). Suppressing the
// single diagnostic below leaves the import as an implicit `any`, which is exactly what
// buildMaterial(data: any) below needs anyway.
// @ts-expect-error - ColladaComposer.js is an internal three.js module with no shipped types.
import { ColladaComposer } from 'three/examples/jsm/loaders/collada/ColladaComposer.js';

// three.js's ColladaComposer.buildMaterial() throws a plain, unguarded TypeError when a material's
// sampler references a surface id that doesn't resolve (a broken/dangling reference inside Box.dae,
// not just a texture file 404ing over the network - see sweepBrokenTextures() above for that other
// case). ColladaLoader.load() catches that exception at the top level and treats it as the WHOLE
// Box.dae failing to load - even though only one material's reference is actually broken, and even
// when that material is a cosmetic one (e.g. edges) with no bearing on whether the box is usable.
// Patched here, once per module load, so a single broken material reference degrades to a plain
// fallback material instead of taking down parsing (and therefore the avatar, facePanels, and panel
// hover, which all depend on parse() actually completing) for the whole box.
const originalBuildMaterial = ColladaComposer.prototype.buildMaterial;
ColladaComposer.prototype.buildMaterial = function (data: any) {
  try {
    return originalBuildMaterial.call(this, data);
  } catch (e) {
    console.error(`3D preview: material "${data?.name}" has a broken texture reference - using a plain fallback.`, e);
    const fallback = new MeshPhongMaterial();
    fallback.name = data?.name || '';
    return fallback;
  }
};

import {
  Canvas,
  Circle,
  CircleBrush,
  Control,
  FabricImage,
  FabricObject,
  InteractiveFabricObject,
  Path,
  PencilBrush,
  Point,
  Polygon,
  Rect,
  SprayBrush,
  Textbox,
  Triangle,
  TSVGExportOptions,
  util,
} from 'fabric';

// Shared control styling for every object added to the canvas (Rect/Circle/Triangle/Textbox/
// FabricImage all read from this same static default set) - set once at module load, matching
// Fabric's own documented way to recolor controls globally instead of per-object.
InteractiveFabricObject.ownDefaults.cornerColor = '#888';
InteractiveFabricObject.ownDefaults.cornerStrokeColor = '#888';
InteractiveFabricObject.ownDefaults.borderColor = '#888';
// Gives rotation "weight" at every 45deg mark - pulls the angle to the nearest multiple
// once within snapThreshold degrees of it, so 45/90/135/180 etc are easy to land on exactly.
InteractiveFabricObject.ownDefaults.snapAngle = 45;
InteractiveFabricObject.ownDefaults.snapThreshold = 5;
import { CMYKColor, ENVIRONMENT, ERROR_BODY, ERROR_TITLE, FillValue, Subcategory, User } from '@company-box/models';
import {
  AiDecalError,
  AiDecalResponse,
  AiDecalScope,
  AiDecalSide,
  BoxService,
  ContainerService,
  DesignService,
  IdleSessionService,
  ImagesService,
  LocalStorageService,
  UserService,
} from '@company-box/services';
import { cmykToHex, ConfirmationModalComponent, hexToCmyk, InfiniteScrollSentinelDirective, isMacPlatform, NotFoundModalComponent, stripUndefinedParams } from '@company-box/utils';
import {
  AiDecalChatMessage,
  AiDesignChatComponent,
  AiImageModalComponent,
  CanvasShapeComponent,
  ColorPickerChange,
  ColorPickerComponent,
  GradientPickerComponent,
  ImageUploadComponent,
  PatternLibraryComponent,
  PixabayModalComponent,
  ShapeLibraryComponent,
} from '@company-box/design-components';
import { DESIGN_3D_AUTH_GATE, DESIGN_3D_IDLE_LOGOUT, DESIGN_3D_REVIEW_HANDOFF } from './design-3d.tokens';
import { extractPatternColors, replaceFillColor, svgToDataUri } from './lib/pattern-svg.util';
import { GeometricPatternStyle, svgPatternTile } from './lib/geometric-pattern.util';
import { svgGradient } from './lib/gradient.util';
import { buildTicks, RulerTick } from './lib/ruler.util';
import { buildDesignRecord, getEffectiveUserId, getPaperTypeID } from './lib/design-record.util';
import {
  boardColorFor,
  computeFinishOptions,
  computeMaterialOptions,
  computePrintingOptions,
  parsePrintingSelection,
} from './lib/material-finish.util';
import {
  computeDefaultPanelDirection,
  computeFaceBoundaryUV,
  computeFaceGeometry,
  computeFaceUVBounds,
  FacePanel,
  fittedDistanceForAspect,
  findPanelAt,
  getPanelBoundaryPixelPoints,
  getPanelPixelBounds,
  percentForDistance,
} from './lib/scene-geometry.util';
import { PaginatedCollectionLoader } from './lib/paginated-collection-loader';
import {
  cropRectTransformFor,
  cropResultFor,
  dimMaskPathData,
  ImageCropTransform,
  resolveCropRect,
} from './lib/image-crop.util';

type ToolCategory = 'ai' | 'patterns' | 'shapes' | 'text' | 'images' | 'material' | 'draw';

type CanvasTool = 'select' | 'move' | 'draw';

type BrushType = 'pencil' | 'circle' | 'spray';

const CATEGORY_LABELS: Record<ToolCategory, string> = {
  ai: 'AI Design',
  patterns: 'Patterns',
  shapes: 'Shapes',
  text: 'Text',
  images: 'Images',
  material: 'Material & Finish',
  draw: 'Draw & Fill',
};

const BRUSH_CLASSES: Record<BrushType, typeof PencilBrush | typeof CircleBrush | typeof SprayBrush> = {
  pencil: PencilBrush,
  circle: CircleBrush,
  spray: SprayBrush,
};

// 2D canvas zoom (see zoomIn()/zoomOut()) - a multiplier on top of the auto-fit scale computed in
// updateCanvas2dScale(), not a replacement for it, so "100% zoom" always means "fit the column"
// regardless of box size or viewport. MIN below 1 lets the box shrink to see more surrounding
// margin; MAX above 1 is capped well short of pixelation on a design that's already print-resolution.
const CANVAS_2D_ZOOM_STEP = 1.25;
const CANVAS_2D_ZOOM_MIN = 0.5;
const CANVAS_2D_ZOOM_MAX = 3;
// Step size (out of cameraZoomPercent's 0-100 range) the 3D viewer's zoom-in/zoom-out buttons move
// per click - see stepCameraZoom().
const CAMERA_ZOOM_SLIDER_STEP = 10;
// Extra elevation focusPanel() adds on top of a panel's own (negated) normal when focusing it on
// the inside canvas - looking dead-on into the negated normal often reads as a flat, edge-on view
// of an inside/underside surface, so tilting up gives a clearer angle on it.
const INSIDE_FOCUS_ELEVATION_BOOST_RADIANS = MathUtils.degToRad(10);
// Fixed on-screen ruler thickness (see updateCanvas2dScale()'s own comment) - independent of
// canvas2dFitScale, so the ruler stays this same visual size whether the design comfortably fills
// the column or needs to shrink to fit a narrow one. 40, not something thinner still, leaves enough
// room for a 2-digit major-tick label (e.g. 14") next to its own notch without the label's own text
// getting clipped by the ruler strip's overflow: hidden (see .ruler-x/.ruler-y's own comment).
const RULER_SCREEN_THICKNESS_PX = 40;
// Upper bound on canvas2dFitScale (see updateCanvas2dScale()'s own comment) - without this, a small
// enough box (or wide enough column) would scale the design up arbitrarily far past its native
// print-resolution pixels to fill the available space, going soft/blurry well before that.
const CANVAS_2D_FIT_SCALE_MAX = 2;

// Custom (non-Fabric) properties stashed directly on a pattern's FabricImage instance - riding
// along through extraHistoryProperties (see pushHistory()/restoreHistory()) so a pattern's recolor
// state survives undo/redo and reselection without a separate, easily-desynced component-level
// cache keyed by object identity (which loadFromJSON's always-fresh instances would break anyway).
interface PatternImageProps {
  isPattern?: boolean;
  patternSvgTemplate?: string;
  patternOriginalBackgroundColor?: string;
  patternOriginalAccentColors?: string[];
  patternBackgroundColor?: string;
  patternAccentColors?: string[];
  // Cosmetic CMYK companions to the hex fields above (see [[design3d_cmyk_color_picker]]) - always
  // kept in sync with their hex counterpart, whether the color came from the CMYK sliders or was
  // back-computed from a hex/native pick, so the design JSON never has a hex value with no CMYK.
  patternBackgroundColorCmyk?: CMYKColor;
  patternAccentColorsCmyk?: CMYKColor[];
}

// Custom (non-Fabric) properties for a manually- or AI-placed geometric pattern (stripes/
// checkerboard/dots/diagonal-lines/grid - see lib/geometric-pattern.util.ts). Deliberately a
// separate discriminator (isGeometricPattern) and separate field prefix from PatternImageProps
// above, not a reuse of isPattern/patternBackgroundColor/patternAccentColors - those drive
// rebuildPatternImage()'s catalog-shaped recolor logic (fetched SVG template, live find/replace),
// which would misbehave run against a procedurally generated tile. fillAngle is shared with
// gradient fills (see FillValue) - both rotate a generated fill by the same concept, so there's no
// reason for two field names. Field names here match exactly what project-service's
// ContainerDecalDesignService stamps onto an AI-placed GEOMETRIC_PATTERN element, so one is
// editable here with no translation layer - see docs/plans/design3d-geometric-pattern-plan.md.
interface GeometricPatternImageProps {
  isGeometricPattern?: boolean;
  geometricPatternStyle?: GeometricPatternStyle;
  geometricPatternScale?: number;
  geometricPatternBackgroundColor?: string;
  geometricPatternAccentColors?: string[];
  geometricPatternBackgroundColorCmyk?: CMYKColor;
  geometricPatternAccentColorsCmyk?: CMYKColor[];
  fillAngle?: number;
}

// Custom (non-Fabric) property for a fill-based object (canvas background, fill-tool stamp, or a
// gradient-filled shape) baked to a flat image via lib/gradient.util.ts's svgGradient() - the
// FillValue "recipe" cached so the image can be regenerated in place on edit, the same role
// GeometricPatternImageProps/PatternImageProps' own recipe fields play for their fill kinds. Field
// names match project-service's AI-placed GRADIENT element exactly (isGradient/gradientType/
// gradientFromColor/gradientToColor/fillAngle) - see docs/plans/design3d-gradient-fills-plan.md's
// "Backend symmetry" section - `fillValue` itself is this app's own richer recipe (adds center/
// radius for radial), read back on selection via fillValueFromProps() for objects that don't carry
// one (an AI-placed or pre-fillValue-era gradient).
interface GradientImageProps {
  isGradient?: boolean;
  gradientType?: 'LINEAR' | 'RADIAL';
  gradientFromColor?: string;
  gradientFromColorCmyk?: CMYKColor;
  gradientToColor?: string;
  gradientToColorCmyk?: CMYKColor;
  fillAngle?: number;
  fillValue?: FillValue;
}

interface CameraAnimation {
  fromPosition: Vector3;
  toPosition: Vector3;
  fromTarget: Vector3;
  toTarget: Vector3;
  start: number;
  duration: number;
}

// bold/italic reflect only whether converter-service has that exact weight file bundled
// (src/main/resources/fonts/{category}/ in the converter-service repo) - not whether Google Fonts
// offers it. A font missing one gets a faux weight in the browser preview but silently falls back
// to a base-14 substitute in the exported PDF, so the Bold/Italic toolbar buttons are gated on
// these flags (see fontSupportsBold()/fontSupportsItalic()) rather than always being available.
interface FontOption {
  readonly name: string;
  readonly bold: boolean;
  readonly italic: boolean;
}

interface FontGroup {
  readonly label: string;
  readonly fonts: readonly FontOption[];
}

// Which face is currently being edited on the 2D canvas / shown in the bottom toolbar. Everything
// that used to be implicitly "the canvas's" state (the Fabric instance itself, its undo/redo
// history, background color/opacity, active pattern, live box texture, die-line guide) is bundled
// per-side below and exposed back through same-named getters/setters (see the "---- Per-side
// design state ----" section) so the ~40+ existing call sites that read/write e.g. `this.fabricCanvas`
// or `this.canvasBackgroundColor` keep working unchanged - they always meant "the active side's".
type Side = 'outside' | 'inside';

interface SideState {
  fabricCanvas?: Canvas;
  designTexture?: CanvasTexture;
  textureRefreshHandle?: number;
  history: string[];
  historyIndex: number;
  suppressHistory: boolean;
  canvasBackgroundFill: FillValue;
  canvasBackgroundOpacity: number;
  // Solid: a Rect (unchanged). Linear/radial: a FabricImage baked from svgGradient() - see
  // applyCanvasBackground(). Either way this IS fabricCanvas.backgroundImage, kept as its own
  // field too since Fabric's own Canvas type doesn't narrow backgroundImage down to "the thing
  // applyCanvasBackground() manages" for callers that need it typed.
  canvasBackgroundRect?: Rect | FabricImage;
  activePattern?: FabricImage;
  // The one whole-box custom (procedurally generated) geometric pattern, if any - a distinct
  // singleton slot from activePattern above (catalog), but mutually exclusive with it in practice:
  // both are a full-bleed opaque layer sent to the back of the stack, so having both at once would
  // just hide whichever was placed first behind the other (see addGeometricPatternWholeBox()'s own
  // comment). A single-panel custom pattern (FACE scope) is NOT tracked here - like any other
  // panel-scoped stamp (see fillPanelAt()), any number of those can coexist as regular objects.
  activeGeometricPattern?: FabricImage;
  gridlinesAvailable: boolean;
  gridlinesImage?: FabricImage;
}

// Applied to both the outside and inside canvases whenever no other background color is set - a
// near-white the 3D preview's board materials retint against, rather than a pure #ffffff that read
// as a flat, artificial white on the rendered box faces.
const DEFAULT_CANVAS_BACKGROUND_COLOR = '#fcfcfc';
const DEFAULT_CANVAS_BACKGROUND_COLOR_CMYK: CMYKColor = { c: 0, m: 0, y: 0, k: 1 };
const DEFAULT_CANVAS_BACKGROUND_FILL: FillValue = {
  type: 'solid',
  hex: DEFAULT_CANVAS_BACKGROUND_COLOR,
  cmyk: DEFAULT_CANVAS_BACKGROUND_COLOR_CMYK,
};
const DEFAULT_SHAPE_FILL: FillValue = { type: 'solid', hex: '#000000', cmyk: { c: 0, m: 0, y: 0, k: 100 } };

function createSideState(): SideState {
  return {
    history: [],
    historyIndex: -1,
    suppressHistory: false,
    canvasBackgroundFill: DEFAULT_CANVAS_BACKGROUND_FILL,
    canvasBackgroundOpacity: 1,
    gridlinesAvailable: true,
  };
}

// Phase 1 of the three.js builder rewrite: a static, read-only viewer proving
// out Box.dae loading, camera framing, and inside/outside materials for one
// box at a time. Ported from upload-preview.component.ts's thumbnail preview,
// scaled up to fill the page and kept alive/disposed across navigations
// instead of a one-shot render.
//
// Phase 2 adds the 2D design canvas (Fabric.js) alongside it - shapes, text,
// image upload, undo/redo, and a ruler scaled to the box's real dimensions.
// It is intentionally NOT wired to the 3D view yet: that texture bridge is
// phase 3. Panel-click camera turns and tool parity are later phases still -
// see the design-3d rewrite plan.
@Component({
  selector: 'app-design-3d',
  templateUrl: './design-3d.component.html',
  styleUrls: ['./design-3d.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    MatSliderModule,
    MatTooltipModule,
    MatSnackBarModule,
    CanvasShapeComponent,
    ShapeLibraryComponent,
    PatternLibraryComponent,
    ImageUploadComponent,
    PixabayModalComponent,
    AiImageModalComponent,
    AiDesignChatComponent,
    InfiniteScrollSentinelDirective,
    PopoverModule,
    ColorPickerComponent,
    GradientPickerComponent,
    NotFoundModalComponent,
  ],
})
export class Design3dComponent implements OnInit, OnDestroy {
  // The container-type: inline-size ancestor .design-2d-canvas-block/.design-3d-column's own cqw
  // flex-basis is relative to (SCSS) - measured directly in onDividerPointerDown()/Move() so the
  // divider's pointer-to-percentage math uses the exact same width those two columns size off,
  // rather than .design-2d-content-row-track's (which includes the flyout's extra 275px).
  @ViewChild('contentRow', { static: true }) contentRowRef!: ElementRef<HTMLDivElement>;
  @ViewChild('container', { static: true }) containerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('canvas2dOutside', { static: true }) canvas2dOutsideRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('canvas2dInside', { static: true }) canvas2dInsideRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('canvasColumn', { static: true }) canvasColumnRef!: ElementRef<HTMLDivElement>;
  // The actual scroll viewport around just the design - see .design-2d-canvas-scroll's own comment
  // for why this is a separate element from canvasColumnRef rather than the column scrolling
  // directly: it's what keeps the ruler strips/corner-button chrome (outside this element entirely)
  // physically unable to move on scroll, rather than needing JS to fight a scroll they're part of.
  @ViewChild('canvasScroll', { static: true }) canvasScrollRef!: ElementRef<HTMLDivElement>;
  // Not static - only in the template while showRulers is true (see toggleRulers()).
  @ViewChild('crosshairVertical') crosshairVerticalRef?: ElementRef<HTMLDivElement>;
  @ViewChild('crosshairHorizontal') crosshairHorizontalRef?: ElementRef<HTMLDivElement>;
  @ViewChild('crosshairLabel') crosshairLabelRef?: ElementRef<HTMLDivElement>;
  // Always in the template (unlike the crosshair, not gated behind showRulers) - the rotate/resize
  // readout is about the object being manipulated, not the ruler. See updateTransformHud().
  @ViewChild('transformHud', { static: true }) transformHudRef!: ElementRef<HTMLDivElement>;
  // Always in the template, same reasoning as transformHudRef above - panel hover isn't tied to
  // showRulers. See updatePanelHover().
  @ViewChild('panelNameTooltip', { static: true }) panelNameTooltipRef!: ElementRef<HTMLDivElement>;
  @ViewChild('panelHoverOutline', { static: true }) panelHoverOutlineRef!: ElementRef<SVGSVGElement>;
  @ViewChild('panelHoverOutlinePath', { static: true }) panelHoverOutlinePathRef!: ElementRef<SVGPathElement>;
  // --left/--right are always in the template; --bottom is behind the canvas2dZoom > 1 @if (only
  // shown once zoomed in). Positioned by pinViewportChrome() - see its own comment.
  @ViewChild('cornerGroupLeft', { static: true }) cornerGroupLeftRef!: ElementRef<HTMLDivElement>;
  @ViewChild('cornerGroupRight', { static: true }) cornerGroupRightRef!: ElementRef<HTMLDivElement>;
  @ViewChild('cornerGroupBottom') cornerGroupBottomRef?: ElementRef<HTMLDivElement>;
  // Ruler strips themselves (ruler-x/ruler-y) are static CSS grid children now, needing no JS
  // positioning at all - only the tick marks within them need to track scroll, via these inner
  // wrappers (see updateRulerScrollOffset()). Both behind showRulers same as the strips themselves.
  @ViewChild('rulerXTicks') rulerXTicksRef?: ElementRef<HTMLDivElement>;
  @ViewChild('rulerYTicks') rulerYTicksRef?: ElementRef<HTMLDivElement>;
  @ViewChild('userIdle') userIdleTemplate!: TemplateRef<any>;
  @ViewChild('notFoundModal') notFoundModalTemplate!: TemplateRef<any>;

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private location = inject(Location);
  private ngZone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);
  private http = inject(HttpClient);
  private environment = inject(ENVIRONMENT);
  private designService = inject(DesignService);
  private boxService = inject(BoxService);
  private userService = inject(UserService);
  protected idleSession = inject(IdleSessionService);
  private imagesService = inject(ImagesService);
  private bsModalService = inject(BsModalService);
  private snackBar = inject(MatSnackBar);
  private containerService = inject(ContainerService);
  private sanitizer = inject(DomSanitizer);
  private reviewHandoff = inject(DESIGN_3D_REVIEW_HANDOFF);
  private authGate = inject(DESIGN_3D_AUTH_GATE);
  private idleLogout = inject(DESIGN_3D_IDLE_LOGOUT);
  private localStorageService = inject(LocalStorageService);
  private document = inject(DOCUMENT);

  private readonly apiPath = '/project/container/';
  private readonly previewName = 'Design3DPreview';

  containerID = '';
  private category = '';
  private subcategory = '';
  private categoryName = '';
  private dimensions: any;
  // Full stripped route-params bag (same object the old /design page calls productDetails) -
  // kept alongside the per-field destructuring below so requestAddToCart() can read fields
  // (Preset, quantity, BoardStrength, editingLineItemId, isRemoteAdmin, ...) that only that page
  // used, without adding a dedicated component field for each one.
  private productDetails: any = {};

  // ---- Material & Finish (phase 5) ----
  //
  // The display* flags gating which selects even show up are real per-subcategory metadata
  // (Subcategory.displayWhite/displayPremiumWhite/displayKraft/displayTwoSided/displayLuxe/
  // displayStandard from BoxService.getSubcategory()) - not derived from pricing - so this can be
  // ported directly from design.component.ts's updatePrintingOptions()/updateMaterialOptions()/
  // updateFinishOptions() without pulling in any pricing/validation logic. printingSelected()'s
  // two-sided-printing confirmation-modal flow (promptTwoSidedPrinting()/addTwoSidePrinting()/
  // confirmCancelTwoSidedPrinting()/cancelTwoSidedPrinting() below) IS ported now that an
  // editable inside surface exists - see [[unity_builder_two_sided_printing]].
  private subcategoryMeta?: Subcategory;
  isOtherProduct = false;
  isTissue = false;
  isPaddedMailer = false;

  get displayTwoSided(): boolean {
    return !!this.subcategoryMeta?.displayTwoSided;
  }

  // Mimics the "My Box/Tissue/Mailer Details" block at the top of the Material & Finish flyout
  // on the old /design page (design.component.html) - see boxDetailsSizeLabel for the
  // preset-label-vs-raw-dimensions fallback it replicates.
  get boxDetailsTitle(): string {
    if (this.isTissue) {
      return 'My Tissue Details';
    }
    if (this.isPaddedMailer) {
      return 'My Mailer Details';
    }
    return 'My Box Details';
  }

  get boxDetailsCategoryName(): string {
    return this.categoryName;
  }

  get boxDetailsSizeLabel(): string {
    if (this.productDetails.Preset) {
      try {
        const preset = JSON.parse(this.productDetails.Preset);
        if (preset?.label) {
          return preset.label;
        }
      } catch {
        // malformed preset JSON - fall through to raw dimensions below
      }
    }
    return this.productDetails.BoxDimensions ? `${this.productDetails.BoxDimensions} (in)` : '';
  }

  printing = '';
  printingOptions: string[] = [];
  // Support fields for the two-sided printing confirm-modal flow - see printingSelected() and
  // the prompt/confirm methods further down.
  private pendingPrinting?: string;
  private printingBeforeTwoSided?: string;
  outerMaterial = '';
  outerMaterialOptions: string[] = [];
  innerMaterial = '';
  innerMaterialOptions: string[] = [];
  finish = '';
  finishOptions: string[] = ['Matte'];
  private luxe = false;
  private standard = false;
  // Public (unlike luxe/standard above) - the bottom toolbar's Outside/Inside gating reads these
  // directly from the template, same as the old builder's equivalent bottom-actions bar.
  twoSided = false;
  insideOnly = false;

  // Download PDF (see downloadPDF()) - set once the backend conversion resolves, then rendered
  // through a hidden iframe in the template. bypassSecurityTrustResourceUrl is safe here: the URL
  // is entirely built from our own environment.apiHost/containerID/the backend's own response, no
  // user-controlled input.
  downloadUrl?: SafeResourceUrl;

  private scene = new Scene();
  private camera = new PerspectiveCamera();
  private renderer!: WebGLRenderer;
  private controls!: OrbitControls;
  private resizeObserver?: ResizeObserver;
  private animationFrameId?: number;
  private facePanels: FacePanel[] = [];
  // Keyed by FacePanel.name (the DAE group name, e.g. "Face0") - fetched from
  // /project/panel-names/{type}.json once container.json's own "type" is known (see
  // loadPanelNames()). A face with no assigned name at all (seams/creases) maps to null in that
  // file, so it resolves here too - those panels still get a hover outline, just no tooltip text.
  // A named face carries a separate name per side (confirmed live: the inside name is currently
  // always a placeholder like "4 (stub)", filtered by hideStubPanelNames - see
  // showPanelNameTooltip()), since the outside and inside 2D canvases are hovered independently.
  private panelNames?: Record<string, Partial<Record<Side, { name: string; tags?: string[] }>> | null>;
  // The FacePanel currently under the pointer on the 2D canvas - tracked so updatePanelHover()
  // only touches the outline/tooltip DOM on an actual panel change, not on every mouse:move pixel.
  private hoveredPanel?: FacePanel;
  // Collada material wrappers (from library.materials) for the outside/inside board - kept around
  // so applyBoardColor() can retint them whenever outerMaterial/innerMaterial changes, not just at
  // initial load.
  private outsideBoxMaterial: any;
  private insideBoxMaterial: any;
  private cameraAnimation?: CameraAnimation;
  // The loaded avatar's own bounding-sphere radius (set once in fitCameraToAvatar()) - kept around
  // so compensateZoomForSplit() can re-derive a fitted distance for a new aspect ratio without
  // re-measuring the model's geometry.
  private avatarBoundingRadius = 0;
  // The camera-to-orbitTarget distance that counts as "not zoomed" - set by fitCameraToAvatar()
  // and rescaled by compensateZoomForSplit() (same ratio it applies to the live distance), so a
  // panel resize alone never flips viewChanged. resetView() below dollies back to this distance.
  private defaultDistance = 0;
  // The fixed orbit pivot - captured once from OrbitControls' own default target at setup, so
  // every panel-focus turn rotates the camera around this same point instead of re-centering on
  // whichever panel was last clicked. See initScene() and focusPanel().
  private readonly orbitTarget = new Vector3();
  // The camera-to-orbitTarget unit direction that counts as "not rotated" - set by faceDefaultPanel()
  // at load and re-baselined by every deliberate panel-focus turn (focusPanel()), so navigating to a
  // different panel by clicking the 2D canvas doesn't itself flip viewChanged - only a free rotate-
  // drag away from wherever the app last pointed the camera does. resetView() below dollies back to
  // this direction, same "current baseline" role defaultDistance already plays for zoom.
  private readonly defaultDirection = new Vector3();
  // True once an Alt-drag has panned controls.target off orbitTarget, a scroll/pinch zoom has moved
  // the camera off defaultDistance, or a free rotate-drag has turned it off defaultDirection - drives
  // the "Reset view" button floating over the preview (see the controls 'change' listener in
  // initScene() and resetView() below). Recomputed from the live pan/zoom/rotate gap rather than just
  // flipped on drag-start, so it also clears itself once a panel-focus click or resetView() itself
  // settles the view back to default.
  viewChanged = false;
  // Drives the zoom range input under the 3D preview - 0 at controls.maxDistance (zoomed all the
  // way out), 100 at controls.minDistance (zoomed all the way in). Recomputed from the live camera
  // distance in the controls 'change' listener (initScene()) so it stays in sync with scroll/pinch
  // zoom, panel-focus dollying, and resetView(), not just slider drags.
  cameraZoomPercent = 0;
  // Persistent left-mouse-button mode for the 3D viewer, toggled by the rotate/pan buttons floating
  // top-right of the canvas (template, setViewerTool()). Alt-drag (updatePanModifierState below)
  // still works as a momentary override on top of whichever mode is active here.
  viewerTool: 'rotate' | 'pan' = 'rotate';

  readonly SPLIT_MIN = 20;
  readonly SPLIT_MAX = 80;
  readonly SPLIT_DEFAULT = 65;
  private readonly SPLIT_STORAGE_KEY = 'design3d-split-ratio';

  // Matches .design-2d-flyout's own steady-state width (_flyout.scss) - only actually takes
  // space from .design-2d-panels at the FLYOUT_RESIZE_BREAKPOINT below; toggleCategory() uses
  // both to compensate the 3D camera's distance the same way compensateZoomForSplit() already
  // does for a splitRatio change (see compensateZoomForFlyoutToggle()'s own comment).
  private readonly FLYOUT_WIDTH_PX = 275;
  // Matches _sidebar.scss's own `@media (min-width: 1024px)` - below it the flyout still slides
  // over the panels (translateX) instead of resizing them, so there's no camera to compensate.
  private readonly FLYOUT_RESIZE_BREAKPOINT = '(min-width: 1024px)';

  // How much of the row's width the 2D designer column (.design-2d-canvas-block) gets, as a
  // percentage; the 3D preview column (.design-3d-column) always gets the remainder. Both panels
  // stay mounted and in place; only their flex-basis (template, bound in cqw) changes, so the 2D
  // canvas column's and the 3D container's own ResizeObservers (updateCanvas2dScale()/onResize())
  // pick up the new size on their own without any extra handling here. Draggable between SPLIT_MIN
  // and SPLIT_MAX (onDividerPointerMove()); resetSplit() jumps it straight back to SPLIT_DEFAULT.
  // Restored from SPLIT_STORAGE_KEY in ngOnInit() so a chosen split sticks across visits.
  splitRatio = this.SPLIT_DEFAULT;

  // True for the duration of an active divider drag (onDividerPointerDown() through
  // onDividerPointerUp()) - disables the flex-basis transition (design-3d.component.scss) so the
  // panels track the pointer 1:1 instead of lagging a CSS-eased 0.4s behind it, and switches
  // compensateZoomForSplit() from resetSplit()'s animated tween to an instant per-move update.
  isDraggingDivider = false;
  // Baseline captured once at drag-start (onDividerPointerDown()) and read fresh on every
  // subsequent pointermove - an absolute "from" state to recompute the target distance against on
  // each move, rather than compounding a ratio step-by-step across many small moves.
  private dividerDragStartSplit = this.SPLIT_DEFAULT;
  private dividerDragStartDistance = 0;
  private dividerDragStartDefaultDistance = 0;

  loading = true;
  valid = true;

  // 2D design canvas (phase 2)
  private readonly maxCanvasPx = 480;
  private ppi = 60;

  // ---- Per-side design state ----
  //
  // Outside/inside each get their own Fabric canvas, undo/redo history, background color/opacity,
  // active pattern and live box texture - see the Side/SideState/createSideState() declarations
  // above. activeSide picks which one the getters/setters directly below resolve to, so every
  // existing `this.fabricCanvas`/`this.history`/etc. call site elsewhere in this file keeps meaning
  // exactly what it always meant ("the canvas currently being edited") without needing to change.
  activeSide: Side = 'outside';
  private readonly sideState: Record<Side, SideState> = {
    outside: createSideState(),
    inside: createSideState(),
  };

  private get fabricCanvas(): Canvas {
    return this.sideState[this.activeSide].fabricCanvas!;
  }
  private set fabricCanvas(canvas: Canvas) {
    this.sideState[this.activeSide].fabricCanvas = canvas;
  }

  private get designTexture(): CanvasTexture | undefined {
    return this.sideState[this.activeSide].designTexture;
  }
  private set designTexture(texture: CanvasTexture | undefined) {
    this.sideState[this.activeSide].designTexture = texture;
  }

  private get textureRefreshHandle(): number | undefined {
    return this.sideState[this.activeSide].textureRefreshHandle;
  }
  private set textureRefreshHandle(handle: number | undefined) {
    this.sideState[this.activeSide].textureRefreshHandle = handle;
  }

  private get history(): string[] {
    return this.sideState[this.activeSide].history;
  }
  private set history(history: string[]) {
    this.sideState[this.activeSide].history = history;
  }

  private get historyIndex(): number {
    return this.sideState[this.activeSide].historyIndex;
  }
  private set historyIndex(index: number) {
    this.sideState[this.activeSide].historyIndex = index;
  }

  private get suppressHistory(): boolean {
    return this.sideState[this.activeSide].suppressHistory;
  }
  private set suppressHistory(value: boolean) {
    this.sideState[this.activeSide].suppressHistory = value;
  }

  // The Fabric canvas is bigger than the printable area - canvasWidthPx/canvasHeightPx stay the
  // design area's own size (still what ppi/ruler ticks/gridlines-overlay scale/the 3D texture crop
  // are all based on), and canvasMargin is the blank border around it on every side. Everything
  // placed on the canvas is offset by canvasMargin so it lands inside the design area by default;
  // the margin itself just gives patterns/images room to visibly extend past the die-line (they're
  // still clipped out of the actual 3D texture via the toCanvasElement() crop in
  // refreshDesignTexture()/loadBox()).
  readonly canvasMargin = 40;
  // The ruler strip's own thickness (native px, i.e. pre-fit-scale) - RULER_SCREEN_THICKNESS_PX /
  // canvas2dFitScale when rulers are shown, 0 when they're not (see updateCanvas2dScale()), so it
  // renders at that same fixed screen thickness regardless of fitScale.
  rulerThicknessPx = 100;
  // True for the duration of an active rotate/resize drag - suppresses the plain position crosshair
  // (see mouse:move above) so it doesn't sit on top of transformHud's own readout at the same point.
  private transformHudActive = false;
  // .design-2d-ruler-grid's own native (pre-fit-scale) size - see updateCanvas2dScale()'s own
  // comment for why this is the available column space divided back out of fitScale, not the
  // design's own native footprint.
  rulerGridWidthPx = 0;
  rulerGridHeightPx = 0;
  canvasWidthPx = 0;
  canvasHeightPx = 0;
  rulerTicksX: RulerTick[] = [];
  rulerTicksY: RulerTick[] = [];
  canUndo = false;
  canRedo = false;

  // Responsive display scale (see updateCanvas2dScale()) - a pure CSS transform applied to the
  // ruler/canvas block in the template, layered on top of the fixed canvasWidthPx/canvasHeightPx
  // working resolution above rather than replacing it, so it can track the 65%-column's actual
  // available size without disturbing any of the systems keyed off that fixed resolution.
  canvas2dScale = 1;
  // User-controlled multiplier on top of the auto-fit scale (see CANVAS_2D_ZOOM_STEP above and
  // updateCanvas2dScale()) - persists across column resizes (window resize, flyout open/close)
  // since those recompute the fit scale, not the user's chosen zoom relative to it.
  canvas2dZoom = 1;
  canZoomIn = true;
  canZoomOut = true;
  canvas2dFitScale = 1;
  // Below zoom 1, .design-2d-canvas-wrap's fixed-size box no longer fills the scroll cell it
  // shrinks inside of (see updateCanvas2dScale()) - top-left-anchored scaling alone would shrink
  // the design toward the box's top-left corner instead of staying centered in the view. These
  // recenter it by shifting the wrap's own scale (template) the rest of the way, and the ruler
  // ticks/crosshair/transform HUD (which otherwise assume the design's origin sits exactly at the
  // scroll viewport's own top-left) by the same amount, so they stay in sync with what's on screen.
  // Zero at zoom >= 1, where the existing top-left anchor (design's own (0, 0) origin pinned to the
  // ruler's "0 inch" mark - see .design-2d-canvas-wrap's own comment) is still correct.
  canvas2dCenterOffsetX = 0;
  canvas2dCenterOffsetY = 0;
  private canvasColumnResizeObserver?: ResizeObserver;

  // Die-line guide overlay - a per-container PNG (cut lines/bleed/fold lines/registration mark),
  // fetched the same way as Box.dae. Uses Fabric's native overlayImage + controlsAboveOverlay
  // (not a DOM <img>): design objects render first, then the overlay on top of them, then
  // selection controls on top of the overlay - so the guide never hides a resize handle, but
  // still shows through over whatever's been placed. Because overlayImage is part of the fabric
  // canvas's own render pipeline, it would otherwise get baked into the toCanvasElement() snapshot
  // that becomes the box texture - refreshDesignTexture()/loadBox() null it out around that call.
  // Each side loads its own guide image (gridlines.png / gridlines_inside.png - see
  // gridlinesUrlFor()) once at init, so switching sides needs no re-fetch.
  showGridlines = true;
  showRulers = true;

  private gridlinesUrlFor(side: Side): string {
    const filename = side === 'outside' ? 'gridlines.png' : 'gridlines_inside.png';
    return `${this.environment.apiHost}${this.apiPath}${this.containerID}/${filename}`;
  }

  // Tool sidebar (phase 5) - single "which category is open" state, matching the flyout pattern
  // already used by the existing Unity designer's side menu (design.component.ts's activeButton
  // array), just as one nullable field instead of a boolean array. Patterns/Material panels are
  // placeholders until those pieces get wired in; Shapes/Text/Images just host the buttons that
  // already worked in the old top toolbar.
  activeCategory: ToolCategory | null = null;

  // ---- AI Design Chat ----
  //
  // Mirrors the Unity designer's AI Design tab (design.component.ts) end-to-end: same
  // DesignService.generateAiDecal() call, same AiDesignChatComponent widget, same
  // decalElementsByReference multi-round reference-code tracking (a follow-up prompt only targets
  // an earlier element when it names that element's short code, e.g. "a1" - see
  // extractReferencedGuids()). The only real difference from the Unity flow is how the canvas is
  // synced before/after the call: Unity needs a SendMessage round-trip through its WebGL bridge
  // (silentSaveCanvasState()/a Design-record reload, both there to work around that bridge's
  // payload-size limit) - this Fabric canvas is in-process, so both directions are just
  // serializeSide()/applyDesignJsonFor() calls (see onAiDecalPromptSubmitted()).
  aiDesignChatEnabled = !!this.environment.features?.aiDesignChat;
  aiDecalMessages: AiDecalChatMessage[] = [];
  aiDecalGenerating = false;
  decalElementsByReference: Record<
    string,
    { objectGuid: string; side: AiDecalSide; face: string | null; scope: AiDecalScope }
  > = {};
  // Set only right after the backend asks a clarifying question instead of placing anything (see
  // generateAiDecalFromPrompt()) - the backend call is stateless and remembers nothing about the
  // question it just asked, so the original prompt has to be carried here across the one follow-up
  // turn and merged back in (see onAiDecalPromptSubmitted()) before resubmitting.
  private pendingClarification?: { originalPrompt: string };

  // ---- Fill tool (panel flood-fill) ----
  //
  // A "stamp" tool rather than a drag/drop one: while active, any click on the 2D canvas resolves
  // to whichever panel it landed on (via the same UV-bounds lookup used by handlePanelFocusClick()/
  // getFaceBoundsForObject()) and drops a Rect sized to exactly cover that panel. skipTargetFind
  // (see setFillToolActive()) makes every click behave like a background click regardless of what's
  // already sitting on the canvas, so filling works even over existing shapes/images. One-shot -
  // placing a shape deactivates the tool again (see fillPanelAt()), so it doesn't stay armed to
  // catch an unrelated next click on the canvas.
  fillToolActive = false;
  // Same glyph as the sidebar's fa-fill-drip button (see fill-drip-cursor.svg's own comment) -
  // hotspot sits at the drip tip (bottom-right of the icon) since that's the "active" pixel a user
  // aims with. Also bound on .design-2d-canvas-block in the template (see design-3d.component.scss's
  // matching override), so the cursor follows the pointer across the whole 2D canvas panel - rulers
  // and corner buttons included - not just Fabric's own canvas element, but stops at the panel's
  // edge rather than following the pointer over the sidebar/toolbar too.
  readonly fillToolCursor = 'url(/assets/cb/images/fill-drip-cursor.svg) 28 30, crosshair';

  // Same one-shot "click a panel to stamp" model as fillToolActive above, but for the Custom
  // Pattern tab's single-panel placement (see fillGeometricPatternPanelAt()) - reuses the exact
  // same mouse:down routing and cursor mechanics, just a different stamp shape.
  geometricPatternToolActive = false;

  // ---- Select/Move canvas tool (shown once zoomed in - see updateCanvas2dScale()) ----
  //
  // Select is just this component's existing default interaction (click to select/drag objects,
  // click empty canvas to focus the 3D camera - see the mouse:down handler in initCanvas2d()).
  // Move repurposes the same selection/skipTargetFind/cursor knobs setFillToolActive() already
  // uses, but drags the canvas column's own scroll position (beginCanvasPan() below) instead of
  // any object or Fabric viewport transform - once zoomed past fit, the column already scrolls
  // natively (overflow: auto), so panning is just driving that scroll from a click-drag instead of
  // requiring the trackpad/scrollbar. Mutually exclusive with the fill tool (see setCanvasTool()/
  // setFillToolActive()) since both claim the same cursor/selection state.
  canvasTool: CanvasTool = 'select';
  private canvasPanState?: { lastX: number; lastY: number };

  // ---- Draw tool (freehand pencil/marker/spray) ----
  //
  // A third canvasTool state (see above) rather than its own boolean like fillToolActive - drawing
  // is continuous like Move/Select, not fillToolActive's one-shot stamp, so it shares that
  // mutual-exclusion machinery instead of duplicating it. Brush type/color/width are configured here
  // and pushed into fabricCanvas.freeDrawingBrush by applyFreeDrawingBrush() - Fabric doesn't need
  // any of this saved separately, since every completed stroke becomes a plain object on the canvas
  // (object:added already pushes history for it) and picks up undo/redo/selection for free.
  brushType: BrushType = 'pencil';
  brushColor = '#000000';
  brushColorCmyk: CMYKColor = { c: 0, m: 0, y: 0, k: 100 };
  brushWidth = 4;
  // One cursor for every brush type (Pencil/Marker/Spray) - it marks the Draw tool being active, not
  // which brush is selected, same role fillToolCursor plays for Fill. Applied via
  // fabricCanvas.freeDrawingCursor (see setCanvasTool()'s 'draw' branch), which Fabric only reads
  // while isDrawingMode is true, so unlike fillToolCursor this doesn't need a host-level binding.
  readonly brushToolCursor = 'url(/assets/cb/images/pencil-cursor.svg) 4 28, crosshair';

  // ---- Selected-element toolbar (phase 6) ----
  //
  // Tracks whatever is currently selected on the 2D canvas so the toolbar above the 3D panel can
  // show the right controls for it. Font list ported from the Unity designer's
  // FontAssetsUtil.cs (builder-cb repo) - all Google Fonts, loaded via ensureFontStylesheetLoaded()
  // (see ngOnInit) rather than a <link> in index.html, so the ~95-family stylesheet only loads for
  // visitors who actually reach the designer, not on every page of the host app.
  // A few names were corrected to match Google's actual family names (Unity's internal names
  // dropped spaces / used the font's filename): NovaFlat -> Nova Flat, NovaMono -> Nova Mono,
  // ShareTech Mono -> Share Tech Mono, PTM55FT -> PT Mono. Grouped and gated to match
  // converter-service's bundled font set exactly (src/main/resources/fonts/{category}/ in the
  // converter-service repo, verified 2026-08-23) - a font or weight not listed there renders fine
  // on screen but silently falls back to a base-14 substitute in the exported PDF.
  selectedElementType: 'text' | 'shape' | 'pattern' | 'geometricPattern' | 'image' | null = null;
  readonly fontGroups: FontGroup[] = [
    {
      label: 'Sans Serif',
      fonts: [
        { name: 'Actor', bold: false, italic: false },
        { name: 'Aldrich', bold: false, italic: false },
        { name: 'Amiko', bold: false, italic: false },
        { name: 'DM Sans', bold: true, italic: false },
        { name: 'Inter', bold: true, italic: true },
        { name: 'Josefin Sans', bold: false, italic: true },
        { name: 'Julius Sans One', bold: false, italic: false },
        { name: 'Jura', bold: false, italic: false },
        { name: 'Lato', bold: true, italic: true },
        { name: 'Marmelad', bold: false, italic: false },
        { name: 'Maven Pro', bold: false, italic: false },
        { name: 'Michroma', bold: false, italic: false },
        { name: 'Montserrat', bold: false, italic: true },
        { name: 'Nunito Sans', bold: false, italic: true },
        { name: 'Open Sans', bold: false, italic: true },
        { name: 'Poppins', bold: false, italic: true },
        { name: 'Quicksand', bold: false, italic: false },
        { name: 'Raleway', bold: true, italic: false },
        { name: 'Rubik', bold: false, italic: true },
        { name: 'Rubik Mono One', bold: false, italic: false },
        { name: 'Work Sans', bold: true, italic: false },
      ],
    },
    {
      label: 'Serif',
      fonts: [
        { name: 'BioRhyme', bold: true, italic: false },
        { name: 'Bitter', bold: true, italic: true },
        { name: 'Copse', bold: false, italic: false },
        { name: 'Cormorant', bold: false, italic: true },
        { name: 'Cutive', bold: false, italic: false },
        { name: 'EB Garamond', bold: true, italic: false },
        { name: 'Gabriela', bold: false, italic: false },
        { name: 'Josefin Slab', bold: false, italic: true },
        { name: 'Libre Baskerville', bold: true, italic: true },
        { name: 'Lora', bold: true, italic: true },
        { name: 'Merriweather', bold: true, italic: false },
        { name: 'Neuton', bold: true, italic: true },
        { name: 'Playfair Display', bold: true, italic: true },
        { name: 'Rasa', bold: false, italic: false },
        { name: 'Sanchez', bold: false, italic: true },
        { name: 'Scope One', bold: false, italic: false },
        { name: 'Suez One', bold: false, italic: false },
        { name: 'Trocchi', bold: false, italic: false },
        { name: 'Vesper Libre', bold: true, italic: false },
      ],
    },
    {
      label: 'Display',
      fonts: [
        { name: 'Abril Fatface', bold: false, italic: false },
        { name: 'Alfa Slab One', bold: false, italic: false },
        { name: 'Anton', bold: false, italic: false },
        { name: 'Bangers', bold: false, italic: false },
        { name: 'Bebas Neue', bold: false, italic: false },
        { name: 'Black Ops One', bold: false, italic: false },
        { name: 'Bungee', bold: false, italic: false },
        { name: 'Bungee Shade', bold: false, italic: false },
        { name: 'Cinzel Decorative', bold: true, italic: false },
        { name: 'Contrail One', bold: false, italic: false },
        { name: 'Farsan', bold: false, italic: false },
        { name: 'Fruktur', bold: false, italic: false },
        { name: 'Graduate', bold: false, italic: false },
        { name: 'Gravitas One', bold: false, italic: false },
        { name: 'Lalezar', bold: false, italic: false },
        { name: 'Lobster', bold: false, italic: false },
        { name: 'Mogra', bold: false, italic: false },
        { name: 'Monoton', bold: false, italic: false },
        { name: 'Nova Flat', bold: false, italic: false },
        { name: 'Patua One', bold: false, italic: false },
        { name: 'Plaster', bold: false, italic: false },
        { name: 'Poiret One', bold: false, italic: false },
        { name: 'Rakkas', bold: false, italic: false },
        { name: 'Shrikhand', bold: false, italic: false },
        { name: 'Special Elite', bold: false, italic: false },
        { name: 'Supermercado One', bold: false, italic: false },
        { name: 'Wallpoet', bold: false, italic: false },
        { name: 'Yatra One', bold: false, italic: false },
      ],
    },
    {
      label: 'Handwriting',
      fonts: [
        { name: 'Amatic SC', bold: true, italic: false },
        { name: 'Architects Daughter', bold: false, italic: false },
        { name: 'Berkshire Swash', bold: false, italic: false },
        { name: 'Calligraffitti', bold: false, italic: false },
        { name: 'Caveat', bold: true, italic: false },
        { name: 'Clicker Script', bold: false, italic: false },
        { name: 'Cookie', bold: false, italic: false },
        { name: 'Courgette', bold: false, italic: false },
        { name: 'Dancing Script', bold: true, italic: false },
        { name: 'Engagement', bold: false, italic: false },
        { name: 'Felipa', bold: false, italic: false },
        { name: 'Kaushan Script', bold: false, italic: false },
        { name: 'Kavivanar', bold: false, italic: false },
        { name: 'Marck Script', bold: false, italic: false },
        { name: 'Merienda One', bold: false, italic: false },
        { name: 'Pacifico', bold: false, italic: false },
        { name: 'Permanent Marker', bold: false, italic: false },
        { name: 'Sacramento', bold: false, italic: false },
        { name: 'Shadows Into Light', bold: false, italic: false },
      ],
    },
    {
      label: 'Monospace',
      fonts: [
        { name: 'Cutive Mono', bold: false, italic: false },
        { name: 'JetBrains Mono', bold: true, italic: true },
        { name: 'Nova Mono', bold: false, italic: false },
        { name: 'PT Mono', bold: false, italic: false },
        { name: 'Roboto Mono', bold: false, italic: true },
        { name: 'Share Tech Mono', bold: false, italic: false },
        { name: 'Source Code Pro', bold: false, italic: false },
      ],
    },
  ];
  // O(1) weight-capability lookup for fontSupportsBold()/fontSupportsItalic() - avoids scanning
  // fontGroups on every toolbar render.
  private readonly fontLookup = new Map<string, FontOption>(
    this.fontGroups.flatMap((group) => group.fonts.map((font) => [font.name, font] as const)),
  );
  // Same Google Fonts CSS2 request every host app's index.html used to hardcode as a <link> -
  // now injected once by ensureFontStylesheetLoaded() (see ngOnInit) instead, so it's fetched only
  // by visitors who reach the designer. Keep the family list in sync with fontGroups above by hand;
  // it's not derived from fontGroups because the CSS2 API's ital/wght axis syntax varies per family
  // (bold-only vs italic-only vs both) in ways not worth round-tripping through a URL builder.
  private static readonly FONT_STYLESHEET_URL =
    'https://fonts.googleapis.com/css2?family=Abril+Fatface&family=Actor&family=Aldrich&family=Alfa+Slab+One&family=Amatic+SC:wght@400;700&family=Amiko&family=Anton&family=Architects+Daughter&family=Bangers&family=Bebas+Neue&family=Berkshire+Swash&family=BioRhyme:wght@400;700&family=Bitter:ital,wght@0,400;0,700;1,400;1,700&family=Black+Ops+One&family=Bungee&family=Bungee+Shade&family=Calligraffitti&family=Caveat:wght@400;700&family=Cinzel+Decorative:wght@400;700&family=Clicker+Script&family=Contrail+One&family=Cookie&family=Copse&family=Cormorant:ital,wght@0,400;1,400&family=Courgette&family=Cutive&family=Cutive+Mono&family=Dancing+Script:wght@400;700&family=DM+Sans:wght@400;700&family=EB+Garamond:wght@400;700&family=Engagement&family=Farsan&family=Felipa&family=Fruktur&family=Gabriela&family=Graduate&family=Gravitas+One&family=Inter:ital,wght@0,400;0,700;1,400;1,700&family=JetBrains+Mono:ital,wght@0,400;0,700;1,400;1,700&family=Josefin+Sans:ital,wght@0,400;1,400&family=Josefin+Slab:ital,wght@0,400;1,400&family=Julius+Sans+One&family=Jura&family=Kaushan+Script&family=Kavivanar&family=Lalezar&family=Lato:ital,wght@0,400;0,700;1,400;1,700&family=Libre+Baskerville:ital,wght@0,400;0,700;1,400;1,700&family=Lobster&family=Lora:ital,wght@0,400;0,700;1,400;1,700&family=Marck+Script&family=Marmelad&family=Maven+Pro&family=Merienda+One&family=Merriweather:wght@400;700&family=Michroma&family=Mogra&family=Monoton&family=Montserrat:ital,wght@0,400;1,400&family=Neuton:ital,wght@0,400;0,700;1,400;1,700&family=Nova+Flat&family=Nova+Mono&family=Nunito+Sans:ital,wght@0,400;1,400&family=Open+Sans:ital,wght@0,400;1,400&family=Pacifico&family=Patua+One&family=Permanent+Marker&family=Plaster&family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&family=Poiret+One&family=Poppins:ital,wght@0,400;1,400&family=PT+Mono&family=Quicksand&family=Rakkas&family=Raleway:wght@400;700&family=Rasa&family=Roboto+Mono:ital,wght@0,400;1,400&family=Rubik:ital,wght@0,400;1,400&family=Rubik+Mono+One&family=Sacramento&family=Sanchez:ital,wght@0,400;1,400&family=Scope+One&family=Shadows+Into+Light&family=Share+Tech+Mono&family=Shrikhand&family=Source+Code+Pro&family=Special+Elite&family=Suez+One&family=Supermercado+One&family=Trocchi&family=Vesper+Libre:wght@400;700&family=Wallpoet&family=Work+Sans:wght@400;700&family=Yatra+One&display=swap';
  // Stable default independent of fontGroups' order/grouping - previously this was fontOptions[0].
  readonly defaultFontFamily = 'Abril Fatface';
  // Size dropdown's full range - every integer 4-50, not a curated common-sizes list, since that's
  // what was asked for.
  readonly fontSizeOptions: number[] = Array.from({ length: 47 }, (_, index) => index + 4);
  // Shared by the text Outline and shape Stroke width dropdowns - every integer 0-15.
  readonly strokeWidthOptions: number[] = Array.from({ length: 16 }, (_, index) => index);
  // Formats every Opacity mat-slider's discrete value label as a percentage - opacity is stored as
  // a 0-1 fraction (matching Fabric's own `opacity` property), but "42%" reads better mid-drag than
  // "0.42".
  readonly opacityDisplayWith = (value: number): string => `${Math.round(value * 100)}%`;
  elementOpacity = 1;
  textFontFamily = this.defaultFontFamily;
  textFontSize = 20;
  textColor = '#000000';
  textColorCmyk: CMYKColor = { c: 0, m: 0, y: 0, k: 100 };
  textBold = false;
  textItalic = false;
  textUnderline = false;
  textStrikethrough = false;
  textOverline = false;
  textJustify: 'left' | 'center' | 'right' = 'left';
  textLineHeight = 1.16;
  textCharSpacing = 0;
  textHighlightEnabled = false;
  textBackgroundColor = '#ffff00';
  textBackgroundColorCmyk: CMYKColor = { c: 0, m: 0, y: 100, k: 0 };
  textStrokeColor = '#000000';
  textStrokeColorCmyk: CMYKColor = { c: 0, m: 0, y: 0, k: 100 };
  textStrokeWidth = 0;
  patternBackgroundColor = '#ffffff';
  patternBackgroundColorCmyk: CMYKColor = { c: 0, m: 0, y: 0, k: 0 };
  // Shown instead of an element's own controls when nothing is selected (see design-3d-column in
  // the template). Applied via applyCanvasBackground() below rather than written to
  // fabricCanvas.backgroundColor directly, since opacity isn't a real property on it. Per-side (see
  // "---- Per-side design state ----" above) - each face keeps its own background independently.
  get canvasBackgroundFill(): FillValue {
    return this.sideState[this.activeSide].canvasBackgroundFill;
  }
  set canvasBackgroundFill(value: FillValue) {
    this.sideState[this.activeSide].canvasBackgroundFill = value;
  }

  get canvasBackgroundOpacity(): number {
    return this.sideState[this.activeSide].canvasBackgroundOpacity;
  }
  set canvasBackgroundOpacity(opacity: number) {
    this.sideState[this.activeSide].canvasBackgroundOpacity = opacity;
  }

  // Fabric's own backgroundImage/overlayImage properties render beneath every canvas object
  // unconditionally (see _renderBackground()), independent of add()/sendObjectToBack() order -
  // unlike a plain object, so it can't end up on the wrong side of a pattern's own
  // sendObjectToBack() call (see addPattern()). Sized/positioned to the design area only
  // (canvasMargin offset, canvasWidthPx x canvasHeightPx), not the full canvasMargin-inclusive
  // canvas, matching applyDesignAreaClipPath()'s own rectangle-clip definition of "outside the
  // gridlines" - gridlines.png has no fill mask to clip against precisely (see that comment).
  private get canvasBackgroundRect(): Rect | FabricImage | undefined {
    return this.sideState[this.activeSide].canvasBackgroundRect;
  }
  private set canvasBackgroundRect(rect: Rect | FabricImage | undefined) {
    this.sideState[this.activeSide].canvasBackgroundRect = rect;
  }
  patternAccentColors: string[] = [];
  patternAccentColorsCmyk: CMYKColor[] = [];
  patternFlipX = false;
  patternFlipY = false;
  // Which sub-tab the Patterns flyout is showing - the existing CSR-curated catalog grid, or the
  // Custom (procedurally generated) pattern controls below. See docs/plans/design3d-geometric-
  // pattern-plan.md's "Decided" section for why this lives as a second mode here rather than
  // folded into app-gradient-picker (patterns are already a distinct concept from a flat/gradient
  // fill in this app).
  patternTabMode: 'library' | 'custom' = 'library';
  // Custom geometric pattern controls - both the "pending" params shown in the Patterns flyout's
  // Custom tab before anything is placed, AND (re-synced via syncGeometricPatternControlsFromObject())
  // the currently-selected instance's own params when editing one already on the canvas. Field
  // names mirror the backend's own AI-placed element (see GeometricPatternImageProps) except for
  // the Cmyk companions, cosmetic-only here.
  geometricPatternStyle: GeometricPatternStyle = 'STRIPES';
  geometricPatternBackgroundColor = '#ffffff';
  geometricPatternBackgroundColorCmyk: CMYKColor = { c: 0, m: 0, y: 0, k: 0 };
  geometricPatternAccentColors: string[] = ['#000000'];
  geometricPatternAccentColorsCmyk: CMYKColor[] = [{ c: 0, m: 0, y: 0, k: 100 }];
  geometricPatternScale = 0.15;
  geometricPatternAngle = 0;
  // Shape Settings panel's fill - see setShapeFill(). Independent of pendingFillValue below (the
  // Fill tool's next-stamp fill) even though both used to share one field (shapeFillColor) - see
  // docs/plans/design3d-gradient-fills-plan.md section 3/4 for why they were split.
  shapeFill: FillValue = DEFAULT_SHAPE_FILL;
  // Fill tool's next-stamp fill - see setFillValue()/fillPanelAt().
  pendingFillValue: FillValue = DEFAULT_SHAPE_FILL;
  shapeStrokeColor = '#000000';
  shapeStrokeColorCmyk: CMYKColor = { c: 0, m: 0, y: 0, k: 100 };
  shapeStrokeWidth = 0;
  shapeFlipX = false;
  shapeFlipY = false;

  get categoryLabel(): string {
    return this.activeCategory ? CATEGORY_LABELS[this.activeCategory] : '';
  }

  toggleCategory(category: ToolCategory): void {
    // Sidebar isn't gated on loading/valid in the template (only the 3D viewport is), so this can
    // fire before initCanvas2d() has created fabricCanvas for either side - every category below
    // eventually touches it (e.g. addText(), the AI chat's silentSaveCanvasState()).
    if (this.loading || !this.valid) {
      return;
    }
    const previousCategory = this.activeCategory;
    if (this.cropModeActive) {
      this.cancelImageCrop();
    }
    this.activeCategory = this.activeCategory === category ? null : category;
    // Leaving the Draw & Fill category turns off whichever of drawing mode/Fill was active, same as
    // picking Select/Move directly - otherwise the canvas would stay armed with no visible way left
    // (the flyout that showed which one was active is gone) to tell it's still on. Fill lives in
    // this same category now (see the "draw" case's template), so both need the same treatment.
    if (previousCategory === 'draw' && this.activeCategory !== 'draw') {
      if (this.canvasTool === 'draw') {
        this.setCanvasTool('select');
      }
      if (this.fillToolActive) {
        this.setFillToolActive(false);
      }
    }
    if (this.activeCategory === 'shapes' && !this.shapesLoaded) {
      this.shapesLoaded = true;
      this.loadShapeCollections();
    }
    if (this.activeCategory === 'patterns' && !this.patternsLoaded) {
      this.patternsLoaded = true;
      this.loadPatternCollections();
    }
    if (this.activeCategory === 'images' && !this.imagesLoaded) {
      this.imagesLoaded = true;
      this.loadUser().then(() => this.getImages());
    }
    // Only a real open (null -> category) or close (category -> null) actually resizes
    // .design-3d-column - switching between two already-open categories just swaps the flyout's
    // content, not its width. Has to run synchronously, before anything below lets the browser
    // reflow the width change this toggle is about to cause, so compensateZoomForFlyoutToggle()
    // reads the column's still-PRE-toggle width (see its own comment).
    if (!previousCategory !== !this.activeCategory) {
      this.compensateZoomForFlyoutToggle(!previousCategory);
    }
    // Opening/closing the flyout changes the 2D column's available width, which should rescale
    // the canvas - the ResizeObserver watching that column (see updateCanvas2dScale()) is the
    // primary mechanism for this, but was confirmed live to sometimes miss a resize notification
    // on this exact transition (verified via ng.getComponent() debug access: canvas2dScale stayed
    // stale after closing the flyout even though the column had genuinely resized). rAF guarantees
    // a recompute once the browser has actually finished the layout this toggle triggers, as a
    // reliable backstop regardless of what's causing the observer to miss it.
    requestAnimationFrame(() => this.updateCanvas2dScale());
  }

  toggleFillTool(): void {
    this.setFillToolActive(!this.fillToolActive);
  }

  // Shared by the toolbar toggle and fillPanelAt() - a fill click is a one-shot "stamp", not a
  // sticky mode like a real paint-bucket tool, so placing one shape deactivates the tool again.
  private setFillToolActive(active: boolean): void {
    if (active) {
      this.setGeometricPatternToolActive(false);
    }
    this.fillToolActive = active;
    if (active) {
      // Fill, Move and Draw all claim defaultCursor/hoverCursor/selection/skipTargetFind (Draw via
      // isDrawingMode) - keep them mutually exclusive so activating one doesn't leave another's
      // state (and its "active" highlight) stuck on underneath.
      this.canvasTool = 'select';
      this.fabricCanvas.isDrawingMode = false;
      this.fabricCanvas.discardActiveObject();
      this.fabricCanvas.selection = false;
      this.fabricCanvas.skipTargetFind = true;
      this.fabricCanvas.defaultCursor = this.fillToolCursor;
      this.fabricCanvas.hoverCursor = this.fillToolCursor;
    } else {
      this.fabricCanvas.selection = true;
      this.fabricCanvas.skipTargetFind = false;
      this.fabricCanvas.defaultCursor = 'default';
      this.fabricCanvas.hoverCursor = 'move';
    }
    this.fabricCanvas.requestRenderAll();
  }

  toggleGeometricPatternTool(): void {
    this.setGeometricPatternToolActive(!this.geometricPatternToolActive);
  }

  // Same one-shot "stamp" model as setFillToolActive() above, for the Custom Pattern tab's
  // single-panel placement - see fillGeometricPatternPanelAt().
  private setGeometricPatternToolActive(active: boolean): void {
    if (active) {
      this.setFillToolActive(false);
    }
    this.geometricPatternToolActive = active;
    if (active) {
      this.canvasTool = 'select';
      this.fabricCanvas.isDrawingMode = false;
      this.fabricCanvas.discardActiveObject();
      this.fabricCanvas.selection = false;
      this.fabricCanvas.skipTargetFind = true;
      this.fabricCanvas.defaultCursor = this.fillToolCursor;
      this.fabricCanvas.hoverCursor = this.fillToolCursor;
    } else {
      this.fabricCanvas.selection = true;
      this.fabricCanvas.skipTargetFind = false;
      this.fabricCanvas.defaultCursor = 'default';
      this.fabricCanvas.hoverCursor = 'move';
    }
    this.fabricCanvas.requestRenderAll();
  }

  // Select is the plain default state (see setFillToolActive()'s else-branch, duplicated here so
  // Select doesn't depend on Fill's internals) - Move swaps in a grab cursor and hands mouse:down
  // off to beginCanvasPan() instead of Fabric's own selection/drag handling.
  setCanvasTool(tool: CanvasTool): void {
    if (this.canvasTool === tool) {
      return;
    }
    const wasDrawing = this.canvasTool === 'draw';
    this.canvasTool = tool;
    if (wasDrawing) {
      this.fabricCanvas.isDrawingMode = false;
    }
    if (tool === 'move') {
      if (this.fillToolActive) {
        this.setFillToolActive(false);
      }
      this.fabricCanvas.discardActiveObject();
      this.fabricCanvas.selection = false;
      this.fabricCanvas.skipTargetFind = true;
      this.fabricCanvas.defaultCursor = 'grab';
      this.fabricCanvas.hoverCursor = 'grab';
    } else if (tool === 'draw') {
      if (this.fillToolActive) {
        this.setFillToolActive(false);
      }
      this.fabricCanvas.discardActiveObject();
      this.fabricCanvas.isDrawingMode = true;
      this.fabricCanvas.freeDrawingCursor = this.brushToolCursor;
      this.applyFreeDrawingBrush();
    } else {
      this.fabricCanvas.selection = true;
      this.fabricCanvas.skipTargetFind = false;
      this.fabricCanvas.defaultCursor = 'default';
      this.fabricCanvas.hoverCursor = 'move';
    }
    this.fabricCanvas.requestRenderAll();
  }

  // Picking a brush type both arms drawing mode (if it isn't already) and swaps the active brush
  // class - separate from setCanvasTool() itself because setCanvasTool('draw') no-ops when the tool
  // is already 'draw' (see its own early return), which would otherwise swallow a brush-type change
  // made while already drawing. Clicking the already-active brush again turns drawing mode off,
  // matching the toggle behavior of the other tool buttons.
  selectBrushType(type: BrushType): void {
    if (this.canvasTool === 'draw' && this.brushType === type) {
      this.setCanvasTool('select');
      return;
    }
    this.brushType = type;
    if (this.canvasTool !== 'draw') {
      this.setCanvasTool('draw');
    } else {
      this.applyFreeDrawingBrush();
    }
  }

  setBrushColor(color: ColorPickerChange): void {
    this.brushColor = color.hex;
    this.brushColorCmyk = color.cmyk;
    this.applyFreeDrawingBrush();
  }

  // Configures the Fill tool's next stamp fill - unlike setShapeFill() (which recolors whatever's
  // currently selected on the canvas), this just updates the pending value Fill reads when a panel
  // gets stamped (see fillPanelAt()), so it works with nothing selected yet.
  setFillValue(value: FillValue): void {
    this.pendingFillValue = value;
  }

  setBrushWidth(width: number): void {
    if (width < 1) {
      return;
    }
    this.brushWidth = width;
    this.applyFreeDrawingBrush();
  }

  // Every mat-slider thumb in this toolbar calls this on pointerup - Material keeps its focus ring/
  // state-layer showing after a mouse release (focus is set programmatically for drag handling, and
  // nothing else claims it afterward), which reads as the slider staying "selected" once you're
  // done with it. pointerup only fires for actual pointer interaction, not keyboard, so arrow-key
  // adjustments still keep their focus ring for accessibility - only the mouse/touch case is blurred.
  blurSliderThumb(event: Event): void {
    (event.target as HTMLElement).blur();
  }

  // Rebuilds freeDrawingBrush from brushType/brushColor/brushWidth - PencilBrush/CircleBrush/
  // SprayBrush all take the same (canvas) constructor and share the same color/width properties off
  // BaseBrush, so one method covers every brush type without a per-type switch. A no-op while
  // drawing mode isn't active, so changing color/width ahead of picking a brush type (or after
  // leaving Draw) doesn't reach into a canvas that isn't listening for strokes.
  private applyFreeDrawingBrush(): void {
    if (this.canvasTool !== 'draw') {
      return;
    }
    const canvas = this.fabricCanvas;
    const brush = new BRUSH_CLASSES[this.brushType](canvas);
    brush.color = this.brushColor;
    brush.width = this.brushWidth;
    canvas.freeDrawingBrush = brush;
  }

  // Drags canvasScrollRef's own scroll position instead of any Fabric-level viewport transform -
  // see canvasTool's own comment for why. Keyed off clientX/clientY deltas, not scenePoint, since
  // this is screen-space scrolling, unrelated to canvas2dScale or design-area coordinates. Window-
  // level listeners (not Fabric's mouse:move/mouse:up) so the drag keeps tracking even once the
  // pointer leaves the canvas or column bounds, matching a standard grab-to-pan tool.
  private beginCanvasPan(e: MouseEvent | TouchEvent | PointerEvent): void {
    const point = e instanceof TouchEvent ? e.touches[0] : e;
    this.canvasPanState = { lastX: point.clientX, lastY: point.clientY };
    this.fabricCanvas.upperCanvasEl.style.cursor = 'grabbing';
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('pointermove', this.handleCanvasPanMove);
      window.addEventListener('pointerup', this.endCanvasPan);
      window.addEventListener('pointercancel', this.endCanvasPan);
    });
  }

  private handleCanvasPanMove = (e: PointerEvent): void => {
    if (!this.canvasPanState) {
      return;
    }
    const scrollEl = this.canvasScrollRef.nativeElement;
    scrollEl.scrollLeft -= e.clientX - this.canvasPanState.lastX;
    scrollEl.scrollTop -= e.clientY - this.canvasPanState.lastY;
    this.canvasPanState.lastX = e.clientX;
    this.canvasPanState.lastY = e.clientY;
  };

  private endCanvasPan = (): void => {
    if (!this.canvasPanState) {
      return;
    }
    this.canvasPanState = undefined;
    this.fabricCanvas.upperCanvasEl.style.cursor = 'grab';
    window.removeEventListener('pointermove', this.handleCanvasPanMove);
    window.removeEventListener('pointerup', this.endCanvasPan);
    window.removeEventListener('pointercancel', this.endCanvasPan);
  };

  private loadSubcategoryMeta(): void {
    this.boxService.getSubcategory(this.subcategory).then(
      (subcategory) => {
        this.subcategoryMeta = subcategory;
        this.updatePrintingOptions();
        this.updateMaterialOptions();
      },
      () => this.showNotFoundModal(),
    );
  }

  // Feeds the 2D canvas hover outline/tooltip (updatePanelHover()) - kept independent of the main
  // /info -> loadBox() chain below since a missing container.json or panel-names file should never
  // block the box itself from loading, only leave hover panels unnamed. container.json's own "type"
  // (e.g. "ELM") is the join key into /project/panel-names/{type}.json, which in turn is keyed by
  // the same DAE group name (e.g. "Face0") FacePanel.name already carries - see buildFacePanels().
  private loadPanelNames(): void {
    if (!this.environment.features?.panelNames) {
      return;
    }
    this.http.get<{ type?: string }>(`${this.environment.apiHost}${this.apiPath}${this.containerID}/container.json`).subscribe({
      next: (container) => {
        if (!container.type) {
          return;
        }
        this.http
          .get<Record<string, Partial<Record<Side, { name: string; tags?: string[] }>> | null>>(
            `${this.environment.apiHost}/project/panel-names/${container.type}.json`,
          )
          .subscribe({
            next: (panelNames) => (this.panelNames = panelNames),
            // Not every box type has a panel-names file yet - hover just shows the outline with no
            // tooltip text until one exists, rather than surfacing an error to the user.
            error: () => undefined,
          });
      },
      error: () => undefined,
    });
  }

  get showOuterMaterialSelect(): boolean {
    return !this.isOtherProduct;
  }

  get showInnerAndFinishSelects(): boolean {
    return !this.isPaddedMailer && !this.isOtherProduct;
  }

  private updatePrintingOptions(): void {
    const meta = this.subcategoryMeta;
    if (!meta) {
      return;
    }
    this.printingOptions = computePrintingOptions(meta);
    if (!this.printing && this.printingOptions.length > 0) {
      this.printing = this.printingOptions[0];
    }
  }

  // Ported from design.component.ts's printingSelected() - defers to a confirmation modal when
  // the selection would turn two-sided printing on (promptTwoSidedPrinting(), an upsell prompt)
  // or off (confirmCancelTwoSidedPrinting(), a data-loss warning: it discards the inside design).
  // Shared with the bottom toolbar's own Add/Cancel Two-Side Printing shortcut (see the
  // template), which calls those same two prompt methods directly.
  printingSelected(printing: string): void {
    const willBeTwoSided = printing.endsWith('2-Sided');
    if (!this.twoSided && willBeTwoSided) {
      // Deferred until the user confirms in the modal - leaving this.printing untouched lets the
      // dropdown revert to the prior selection for free if they cancel.
      this.pendingPrinting = printing;
      this.promptTwoSidedPrinting();
      return;
    }
    if (this.twoSided && !willBeTwoSided) {
      this.pendingPrinting = printing;
      this.confirmCancelTwoSidedPrinting();
      return;
    }
    this.applyPrinting(printing);
  }

  private applyPrinting(printing: string): void {
    this.printing = printing;
    const selection = parsePrintingSelection(printing);
    this.standard = selection.standard;
    this.luxe = selection.luxe;
    this.insideOnly = selection.insideOnly;
    this.twoSided = selection.twoSided;
    this.updateFinishOptions();
    this.syncProductDetailsUrl();
  }

  // Bottom toolbar's "+ Two-Side Printing" shortcut calls this directly (see the template) - same
  // upsell prompt as picking a "* | 2-Sided" option from the Material & Finish dropdown.
  promptTwoSidedPrinting(): void {
    if (!this.displayTwoSided) {
      this.pendingPrinting = undefined;
      return;
    }
    this.modalRef = this.bsModalService.show(this.twoSidedWarningTemplate);
    // Catch dismissal via backdrop click / Escape, not just the modal's own Yes/No buttons.
    this.modalRef.onHidden?.subscribe(() => this.cancelPendingPrinting());
  }

  addTwoSidePrinting(): void {
    if (!this.displayTwoSided) {
      this.pendingPrinting = undefined;
      return;
    }
    // pendingPrinting is set when this was triggered by picking a "* | 2-Sided" option in the
    // dropdown; otherwise it came from the bottom toolbar's own shortcut, so infer the target
    // from the current family.
    const targetPrinting = this.pendingPrinting ?? (this.luxe ? 'Luxe | 2-Sided' : 'Standard | 2-Sided');
    this.pendingPrinting = undefined;
    this.printingBeforeTwoSided = this.printing;
    this.applyPrinting(targetPrinting);
  }

  // Bottom toolbar's "Cancel two-sided print" link calls this directly (see the template).
  confirmCancelTwoSidedPrinting(): void {
    if (!this.twoSided) {
      this.pendingPrinting = undefined;
      return;
    }
    this.modalRef = this.bsModalService.show(this.cancelTwoSidedWarningTemplate);
    this.modalRef.onHidden?.subscribe(() => this.cancelPendingPrinting());
  }

  cancelTwoSidedPrinting(): void {
    if (!this.twoSided) {
      this.pendingPrinting = undefined;
      return;
    }
    // pendingPrinting is set when this was triggered by picking a single-sided option in the
    // dropdown; otherwise it came from the bottom toolbar's own link, so fall back to whatever
    // printing was active before two-sided printing was turned on.
    const restoredPrinting =
      this.pendingPrinting ?? this.printingBeforeTwoSided ?? (this.luxe ? 'Luxe | Outside' : 'Standard | Outside');
    this.pendingPrinting = undefined;
    this.printingBeforeTwoSided = undefined;
    this.applyPrinting(restoredPrinting);
    // The confirmation modal promises this is irreversible - actually discard the inside design
    // rather than leaving it in sideState.inside to silently reappear if two-sided printing gets
    // turned back on later.
    this.discardInsideDesign();
    this.toggleShowingSide(this.insideOnly ? 'inside' : 'outside');
  }

  cancelPendingPrinting(): void {
    this.pendingPrinting = undefined;
  }

  private updateMaterialOptions(): void {
    const meta = this.subcategoryMeta;
    if (!meta) {
      return;
    }
    const { outerMaterialOptions, innerMaterialOptions } = computeMaterialOptions({
      meta,
      isPaddedMailer: this.isPaddedMailer,
      categoryName: this.categoryName,
      subcategory: this.subcategory,
      innerMaterial: this.innerMaterial,
      outerMaterial: this.outerMaterial,
    });
    this.outerMaterialOptions = outerMaterialOptions;
    this.innerMaterialOptions = innerMaterialOptions;

    if (!this.outerMaterial && this.outerMaterialOptions.length > 0) {
      this.outerMaterial = this.outerMaterialOptions[0];
    }
    if (!this.innerMaterial && this.innerMaterialOptions.length > 0) {
      this.innerMaterial = this.innerMaterialOptions[0];
    }
    this.updateFinishOptions();
  }

  private updateFinishOptions(): void {
    const { finish, finishOptions } = computeFinishOptions({
      standard: this.standard,
      luxe: this.luxe,
      printing: this.printing,
      innerMaterial: this.innerMaterial,
      outerMaterial: this.outerMaterial,
    });
    if (finish !== undefined) {
      this.finish = finish;
    }
    this.finishOptions = finishOptions;
  }

  // The one piece of this panel that actually does something visible in 3D right now (see
  // [[design3d_kraft_vs_white_material]]) - everything else here is metadata bookkeeping. Re-applies
  // whenever outer/inner material changes, including the very first load.
  onMaterialFinishChanged(): void {
    this.updateMaterialOptions();
    this.applyBoardColor();
    this.syncProductDetailsUrl();
  }

  // Keeps the URL's matrix params (the ones ngOnInit's route.params.subscribe reads back on
  // load - see goToBuilder() in box-options.component.ts for how they first get there) in step
  // with printing/material choices made after the initial navigation, so reloading the page keeps
  // two-sided printing and the chosen paper types instead of reverting to whatever was in the URL
  // at load time. Uses Location.replaceState() rather than router.navigate() - navigating would
  // re-emit route.params and re-run the heavy scene/canvas bootstrap in that subscription.
  private syncProductDetailsUrl(): void {
    const rest: Record<string, unknown> = { ...(this.productDetails ?? {}) };
    delete rest['category'];
    delete rest['subcategory'];
    const updatedParams = {
      ...rest,
      print: this.printing,
      outerMaterial: this.outerMaterial,
      innerMaterial: this.innerMaterial,
      finish: this.finish,
      luxe: String(this.luxe),
      twoSided: String(this.twoSided),
      insideOnly: String(this.insideOnly),
    };
    this.productDetails = { ...this.productDetails, ...updatedParams };
    const urlTree = this.router.createUrlTree(['.', updatedParams], { relativeTo: this.route });
    this.location.replaceState(this.router.serializeUrl(urlTree));
  }

  // Tints the board's base material color - multiplies with the outside map (the live design
  // texture) and the inside map (still the DAE placeholder), so a kraft choice reads as kraft on
  // the box instead of the fixed white it rendered as before. Doesn't attempt to simulate how ink
  // actually prints differently on kraft vs white stock - a flat tint, not a print simulation.
  private applyBoardColor(): void {
    if (this.outsideBoxMaterial) {
      this.outsideBoxMaterial.build.color.set(boardColorFor(this.outerMaterial));
      this.outsideBoxMaterial.build.needsUpdate = true;
    }
    if (this.insideBoxMaterial) {
      this.insideBoxMaterial.build.color.set(boardColorFor(this.innerMaterial));
      this.insideBoxMaterial.build.needsUpdate = true;
    }
  }

  // ---- Shape library (phase 5) ----
  //
  // ShapeLibraryComponent, CanvasShapeComponent, and InfiniteScrollSentinelDirective are reused
  // unmodified from the Unity designer (confirmed Unity-agnostic in earlier research) - the
  // catalog-loading logic below mirrors design.component.ts's getShapeCollections()/
  // loadShapePage()/ensureShapesLoaded(), minus its background "prewarm" scheduler (a page-load
  // performance optimization, not functionally required - this loads lazily on first open instead).
  @ViewChild('shapeLibrary') shapeLibraryTemplate!: TemplateRef<any>;

  private modalRef?: BsModalRef;
  // Guards against showNotFoundModal() stacking a second modal on top of itself - the container
  // /info fetch, Box.dae fetch, subcategory-meta fetch and existing-design fetch can all fail
  // independently (some run in parallel), but the user only needs to see one "couldn't load" modal.
  private notFoundModalShown = false;
  // Resolves once a real logged-in user exists - either found already logged in at load, or after
  // the DESIGN_3D_AUTH_GATE provider's ensureAuthenticated() resolves. loadExistingDesign() awaits
  // this rather than just calling loadUser() itself, because getEffectiveUserId() silently falls
  // back to a guest id (0) for a signed-out visitor - a lookup against that guest id 401s, which
  // used to surface as a spurious "empty box" error modal stacked on top of the login modal on
  // every signed-out page load.
  private resolveUserReady!: () => void;
  private readonly userReady = new Promise<void>((resolve) => {
    this.resolveUserReady = resolve;
  });
  private idleSubscriptions = new Subscription();
  private shapesLoaded = false;
  private readonly shapeLoader = new PaginatedCollectionLoader<any, any>(50, (id, offset, pageSize) =>
    this.designService.getShapes(id, offset, pageSize),
  );

  get shapeCollections(): any[] {
    return this.shapeLoader.collections;
  }

  get shapes(): any[] {
    return this.shapeLoader.items;
  }

  private loadShapeCollections(): void {
    this.designService.getShapeCollections().then((collections: any[]) => {
      void this.shapeLoader.loadCollections(collections.filter((collection: { shapes: number }) => collection.shapes > 0));
    });
  }

  private ensureAllShapesLoaded(): Promise<void[]> {
    return this.shapeLoader.ensureAllLoaded();
  }

  loadMoreShapes(): void {
    this.shapeLoader.loadMore();
  }

  getCollectionShapes(collectionId: number): any[] {
    return this.shapeLoader.getForCollection(collectionId);
  }

  trackByFn(_index: number, item: any): any {
    return item?.id ?? null;
  }

  openShapeLibrary(): void {
    void this.ensureAllShapesLoaded();
    this.modalRef = this.bsModalService.show(this.shapeLibraryTemplate, { class: 'view-entire-library' });
  }

  // Shared by every modal this component opens (shape/pattern library, image upload/pixabay/AI
  // generate) - only one is ever open at a time, so one BsModalRef field covers all of them.
  closeModal(): void {
    this.modalRef?.hide();
  }

  // ---- Pattern library (phase 5) ----
  //
  // PatternLibraryComponent is reused unmodified (same pure pass-through shell as
  // ShapeLibraryComponent). Unity's addPattern() gives zero reusable application logic - it's a
  // bare SendMessage with the whole pattern object serialized - so this needed a from-scratch
  // choice. Verified live in browser: pattern assets (cdn.companybox.com/patterns/...) are
  // self-contained SVGs where the "repeat" is already baked into the artwork (e.g. dots/circles_2.svg
  // is itself a full grid of dots), not a small repeat-unit tile. A Fabric `Pattern` fill at native
  // pixel size (the first approach tried) rendered as one giant blown-up crop instead. addPattern()
  // now scales and places the SVG like any other image via addImageUrlToCanvas() instead.
  @ViewChild('patternLibrary') patternLibraryTemplate!: TemplateRef<any>;

  private patternsLoaded = false;
  private readonly patternLoader = new PaginatedCollectionLoader<any, any>(50, (id, offset, pageSize) =>
    this.designService.getPatterns(id, offset, pageSize),
  );

  get patternCollections(): any[] {
    return this.patternLoader.collections;
  }

  get patterns(): any[] {
    return this.patternLoader.items;
  }

  private loadPatternCollections(): void {
    this.designService.getPatternCollections().then((collections: any[]) => {
      void this.patternLoader.loadCollections(collections.filter((collection: { patterns: number }) => collection.patterns > 0));
    });
  }

  private ensureAllPatternsLoaded(): Promise<void[]> {
    return this.patternLoader.ensureAllLoaded();
  }

  loadMorePatterns(): void {
    this.patternLoader.loadMore();
  }

  getCollectionPatterns(collectionId: number): any[] {
    return this.patternLoader.getForCollection(collectionId);
  }

  openPatternLibrary(): void {
    void this.ensureAllPatternsLoaded();
    this.modalRef = this.bsModalService.show(this.patternLibraryTemplate, { class: 'view-entire-library' });
  }

  // Only one pattern is ever active - unlike shapes/text/images, which can be layered freely,
  // adding a new pattern replaces whichever one is currently on the canvas. A pattern is a
  // full-bleed fill rather than a placed graphic, so it's scaled (uniformly - no skew) until its
  // *shorter* side reaches the canvas's own diagonal, then centered on it: the smallest
  // aspect-preserving size that still fully covers the canvas at every rotation angle (the longer
  // side ends up oversized, extending well past canvas bounds, which is fine - the canvas clips
  // whatever's outside it). The only thing left adjustable is rotation itself: movement and
  // resizing are locked (and their now-inert corner/edge handles hidden), since either would
  // reintroduce a gap the sizing was there to rule out.
  private get activePattern(): FabricImage | undefined {
    return this.sideState[this.activeSide].activePattern;
  }
  private set activePattern(pattern: FabricImage | undefined) {
    this.sideState[this.activeSide].activePattern = pattern;
  }

  private get activeGeometricPattern(): FabricImage | undefined {
    return this.sideState[this.activeSide].activeGeometricPattern;
  }
  private set activeGeometricPattern(pattern: FabricImage | undefined) {
    this.sideState[this.activeSide].activeGeometricPattern = pattern;
  }

  // The geometric-pattern object currently being edited via the Custom Pattern panel - whichever
  // one is selected on the canvas, whole-box singleton (activeGeometricPattern above) or one of
  // possibly-many single-panel instances alike. Unlike activeGeometricPattern (placement-time
  // bookkeeping, whole-box only), this is purely selection-driven, the same way activeShape is for
  // shape primitives.
  private get selectedGeometricPattern(): FabricImage | undefined {
    const object = this.fabricCanvas?.getActiveObject();
    return object instanceof FabricImage && (object as unknown as GeometricPatternImageProps).isGeometricPattern ? object : undefined;
  }

  addPattern(pattern: any): void {
    if (!pattern?.srcUrl) {
      return;
    }
    // addImageUrlToCanvas()'s canvas.add() fires object:added, which pushes a history entry
    // immediately - at that point the pattern is still at its plain-image 60%-fit size, since
    // everything below (the actual full-bleed resize, locks, controls, clip) happens afterward via
    // plain .set() calls that don't themselves push history. Left alone, that first entry is wrong
    // (undo could land on a small, non-covering pattern) - suppressing history for the whole setup
    // and pushing exactly one entry once it's fully configured keeps "add this pattern" a single,
    // correct undo step.
    this.suppressHistory = true;
    // Recoloring (see rebuildPatternImage()) needs the raw SVG text, not just the rendered image -
    // the fetched text is the template every recolor substitutes into fresh, so it's fetched once
    // up front and stashed on the object itself (see PatternImageProps) rather than re-fetched per
    // recolor. The API's own pattern.backgroundColor/accentColors are NOT used as the recolor
    // source: spot-checking live pattern files found them unreliable - e.g. dots/circles_1.svg
    // reports 4 accentColors in the API but its actual SVG only defines one CSS class color
    // (#5a76a1) that doesn't match any of the 4. extractPatternColors() below reads the real,
    // currently-rendered colors out of the SVG itself instead, so recoloring always targets colors
    // that actually exist in the file regardless of whether the API metadata is in sync with it.
    this.http.get(pattern.srcUrl, { responseType: 'text' }).subscribe({
      next: (svgText) => {
        const [originalBackgroundColor = '#ffffff', ...originalAccentColors] = extractPatternColors(svgText);
        this.addImageUrlToCanvas(svgToDataUri(svgText)).then((fabricImage) => {
          if (this.activePattern && this.fabricCanvas.getObjects().includes(this.activePattern)) {
            this.fabricCanvas.remove(this.activePattern);
          }
          // Only one whole-box pattern layer at a time, catalog or custom (geometric) - see
          // activeGeometricPattern's own comment for why leaving both around would just hide
          // whichever was placed first.
          if (this.activeGeometricPattern && this.fabricCanvas.getObjects().includes(this.activeGeometricPattern)) {
            this.fabricCanvas.remove(this.activeGeometricPattern);
            this.activeGeometricPattern = undefined;
          }

          const diagonal = Math.hypot(this.canvasWidthPx, this.canvasHeightPx);
          const scale = diagonal / Math.min(fabricImage.width || 1, fabricImage.height || 1);
          const scaledWidth = (fabricImage.width || 1) * scale;
          const scaledHeight = (fabricImage.height || 1) * scale;
          fabricImage.set({
            left: this.canvasMargin + (this.canvasWidthPx - scaledWidth) / 2,
            top: this.canvasMargin + (this.canvasHeightPx - scaledHeight) / 2,
            // Fabric's default origin is 'center' (not 'left'/'top' - see loadGridlinesOverlay()), so
            // without this the image's center - not its corner - lands at (left, top) above, throwing
            // off the centering math by half the scaled size.
            originX: 'left',
            originY: 'top',
            scaleX: scale,
            scaleY: scale,
            lockMovementX: true,
            lockMovementY: true,
            lockScalingX: true,
            lockScalingY: true,
            // The app-wide default (see InteractiveFabricObject.ownDefaults near the top of this file)
            // draws every corner/handle as a hollow outline - fine at normal sizes, but with only the
            // rotate handle left visible here it reads as barely-there. Filled makes it unambiguous.
            transparentCorners: false,
          });
          // Serialized/restored through undo history via the extraHistoryProperties list (see
          // pushHistory()) so a restored pattern is still identifiable and still locked down - plain
          // custom properties like this one aren't part of Fabric's default serialized property set.
          Object.assign(fabricImage as unknown as PatternImageProps, {
            isPattern: true,
            patternSvgTemplate: svgText,
            patternOriginalBackgroundColor: originalBackgroundColor,
            patternOriginalAccentColors: originalAccentColors,
            patternBackgroundColor: originalBackgroundColor,
            patternAccentColors: [...originalAccentColors],
          });
          this.applyPatternRotateControl(fabricImage);
          this.applyDesignAreaClipPath(fabricImage);
          // A pattern is a background fill, not a placed graphic - always the bottom-most layer, so
          // anything added before or after (shapes/text/images, which stack normally on top via
          // canvas.add()) stays visible over it rather than getting buried underneath.
          this.fabricCanvas.sendObjectToBack(fabricImage);
          this.fabricCanvas.requestRenderAll();

          // Re-resolve the panel-focus turn against the recentered position - addImageUrlToCanvas()
          // already triggered one, but against the pre-recenter (60%, top-left) placement.
          this.handlePanelFocusClick({
            x: this.canvasMargin + this.canvasWidthPx / 2,
            y: this.canvasMargin + this.canvasHeightPx / 2,
          });

          this.activePattern = fabricImage;
          // addImageUrlToCanvas() already made this object active, which already fired
          // selection:created - but that fired before the Object.assign() above set isPattern, so
          // updateSelectionState() read it as a plain image that first time. Nothing else re-fires
          // selection:created for this same object reference, so it's re-synced explicitly here.
          this.updateSelectionState();
          this.suppressHistory = false;
          this.pushHistory();
        }).catch(() => {
          this.suppressHistory = false;
        });
      },
      error: () => {
        this.suppressHistory = false;
      },
    });
  }

  // Guards against overlapping rebuilds (e.g. a color-input drag firing several 'input' events
  // before the first FabricImage.fromURL() decode finishes, or two different color pickers
  // changed in quick succession) - each call captures the current token, and its async result is
  // discarded rather than applied if a newer rebuild has since started. Without this, two in-flight
  // rebuilds can resolve out of order and the stale one silently wins, sitting in front of (and
  // fully hiding, since a pattern image is opaque) the correct final result.
  private patternRebuildToken = 0;

  // Rebuilds the pattern's image from its cached SVG template (see addPattern()) with new
  // background/accent colors substituted in, replacing the current FabricImage in place - patterns
  // are rasterized once loaded, so recoloring means re-rendering from source rather than mutating
  // the existing canvas object the way a text/shape color change can.
  private rebuildPatternImage(): void {
    const previous = this.activePattern;
    if (!previous) {
      return;
    }
    const props = previous as unknown as PatternImageProps;
    const template = props.patternSvgTemplate;
    const originalBackgroundColor = props.patternOriginalBackgroundColor;
    const originalAccentColors = props.patternOriginalAccentColors;
    if (!template || !originalBackgroundColor || !originalAccentColors) {
      return;
    }

    let svg = replaceFillColor(template, originalBackgroundColor, this.patternBackgroundColor);
    originalAccentColors.forEach((original, index) => {
      svg = replaceFillColor(svg, original, this.patternAccentColors[index]);
    });

    const token = ++this.patternRebuildToken;
    this.suppressHistory = true;
    FabricImage.fromURL(svgToDataUri(svg), { crossOrigin: 'anonymous' })
      .then((fabricImage) => {
        if (token !== this.patternRebuildToken) {
          this.suppressHistory = false;
          return;
        }
        fabricImage.set({
          left: previous.left,
          top: previous.top,
          // Fabric's default origin is 'center' (not 'left'/'top' - see loadGridlinesOverlay()), so
          // without this the rebuilt image's center - not its corner - lands where previous.left/top
          // (a top-left-origin corner) was, shifting it on every recolor.
          originX: 'left',
          originY: 'top',
          scaleX: previous.scaleX,
          scaleY: previous.scaleY,
          angle: previous.angle,
          flipX: previous.flipX,
          flipY: previous.flipY,
          lockMovementX: true,
          lockMovementY: true,
          lockScalingX: true,
          lockScalingY: true,
          transparentCorners: false,
        });
        Object.assign(fabricImage as unknown as PatternImageProps, {
          isPattern: true,
          patternSvgTemplate: template,
          patternOriginalBackgroundColor: originalBackgroundColor,
          patternOriginalAccentColors: originalAccentColors,
          patternBackgroundColor: this.patternBackgroundColor,
          patternAccentColors: [...this.patternAccentColors],
          patternBackgroundColorCmyk: this.patternBackgroundColorCmyk,
          patternAccentColorsCmyk: [...this.patternAccentColorsCmyk],
        });
        this.applyPatternRotateControl(fabricImage);
        this.applyDesignAreaClipPath(fabricImage);

        this.fabricCanvas.remove(previous);
        this.fabricCanvas.add(fabricImage);
        this.fabricCanvas.sendObjectToBack(fabricImage);
        this.activePattern = fabricImage;
        this.fabricCanvas.setActiveObject(fabricImage);
        this.fabricCanvas.requestRenderAll();

        this.suppressHistory = false;
        this.pushHistory();
      })
      .catch(() => {
        this.suppressHistory = false;
      });
  }

  setPatternBackgroundColor(color: ColorPickerChange): void {
    if (!this.activePattern) {
      return;
    }
    this.patternBackgroundColor = color.hex;
    this.patternBackgroundColorCmyk = color.cmyk;
    this.rebuildPatternImage();
  }

  setCanvasBackgroundFill(value: FillValue): void {
    this.canvasBackgroundFill = value;
    this.applyCanvasBackground();
  }

  setCanvasBackgroundOpacity(opacity: number): void {
    this.canvasBackgroundOpacity = opacity;
    this.applyCanvasBackground();
  }

  // Guards overlapping rebuilds the same way patternRebuildToken does for rebuildPatternImage() -
  // e.g. dragging the linear angle slider fires several rebuilds before the first FabricImage
  // decode resolves.
  private canvasBackgroundBakeToken = 0;

  // Blends each color stop toward white as opacity drops, instead of toward transparent - the
  // board's material color multiplies with this background (see applyBoardColor()/
  // [[design3d_kraft_vs_white_material]]), and white is that multiply's neutral value, so a low
  // opacity lets the kraft/white board color show through rather than just fading to black. Solid
  // stays a Rect (unchanged); linear/radial bakes to a FabricImage via svgGradient() (see the
  // correction note atop docs/plans/design3d-gradient-fills-plan.md) - canvasBackgroundRect tears
  // down and rebuilds as the right Fabric type whenever the fill's own type changes.
  private applyCanvasBackground(): void {
    const value = this.canvasBackgroundFill;
    if (value.type === 'solid') {
      const fill = this.blendWithWhite(value.hex, this.canvasBackgroundOpacity);
      if (this.canvasBackgroundRect instanceof Rect) {
        this.canvasBackgroundRect.set({ fill });
      } else {
        // backgroundImage is not a normal canvas object (not in _objects[]) - reassigning it below
        // is enough to drop the old one, the same way the original solid-only code never called
        // canvas.remove() on it either.
        this.canvasBackgroundRect = new Rect({
          left: this.canvasMargin,
          top: this.canvasMargin,
          originX: 'left',
          originY: 'top',
          width: this.canvasWidthPx,
          height: this.canvasHeightPx,
          fill,
        });
        this.fabricCanvas.backgroundImage = this.canvasBackgroundRect;
      }
      this.updateBackgroundGradientInteractivity();
      this.fabricCanvas.requestRenderAll();
      this.pushHistory();
      return;
    }

    // The rendered SVG blends each stop toward white by opacity (same reasoning as the solid
    // branch's blendWithWhite() above); the cached recipe (gradientFromColor/gradientToColor/
    // fillValue, stamped by buildGradientFabricImage() itself) stays the user's true chosen colors,
    // unblended, so re-opening this fill later shows what was actually picked, not the blended
    // render.
    const renderFrom = this.blendWithWhite(value.from.hex, this.canvasBackgroundOpacity);
    const renderTo = this.blendWithWhite(value.to.hex, this.canvasBackgroundOpacity);
    const token = ++this.canvasBackgroundBakeToken;
    this.buildGradientFabricImage(value, this.canvasWidthPx, this.canvasHeightPx, renderFrom, renderTo).then((fabricImage) => {
      if (token !== this.canvasBackgroundBakeToken) {
        return;
      }
      fabricImage.set({ left: this.canvasMargin, top: this.canvasMargin, originX: 'left', originY: 'top' });
      this.canvasBackgroundRect = fabricImage;
      this.fabricCanvas.backgroundImage = fabricImage;
      this.updateBackgroundGradientInteractivity();
      this.fabricCanvas.requestRenderAll();
      this.pushHistory();
    });
  }

  // canvasBackgroundRect is fabricCanvas.backgroundImage, not a normal selectable canvas object
  // (see its own comment) - not part of _objects[], so it never shows up via findTarget()/normal
  // clicks. Made selectable+evented here ONLY while Background Settings is the shown panel (nothing
  // else selected) AND the fill is linear/radial, so the drag handles from
  // attachRadialGradientControls()/attachLinearGradientControl() become reachable - movement/
  // scaling/rotation stay locked and the normal corner/edge/rotate handles stay hidden the whole
  // time, so the only thing dragging it can ever do is move the gradient's own center/radius/angle,
  // never relocate or resize the background itself. Call this after anything that could change
  // either condition: applyCanvasBackground() (fill type/value changed) and updateSelectionState()
  // (selection changed) - both already do.
  //
  // setActiveObject() below fires selection:created/updated, which re-enters updateSelectionState()
  // - the `object === this.canvasBackgroundRect` guard at that function's top short-circuits before
  // it could recurse back into this method.
  private updateBackgroundGradientInteractivity(): void {
    const rect = this.canvasBackgroundRect;
    if (!rect) {
      return;
    }
    // applyCanvasBackground() replaces canvasBackgroundRect with a brand-new Fabric object every
    // time the fill's own type changes (Rect <-> FabricImage, or a fresh bake) - the OLD instance
    // can still be sitting as Fabric's active object with nothing live backing it any more (not
    // canvas.backgroundImage, not in getObjects()), left rendering its now-orphaned gradient
    // controls as ghost handles with no border to contain them. Clear it before deciding this
    // call's own interactivity state below, regardless of which branch that ends up taking.
    const activeObject = this.fabricCanvas.getActiveObject();
    if (activeObject && activeObject !== rect && !this.fabricCanvas.getObjects().includes(activeObject)) {
      this.fabricCanvas.discardActiveObject();
    }
    const fill = this.canvasBackgroundFill;
    const shouldBeInteractive = !this.selectedElementType && (fill.type === 'linear' || fill.type === 'radial');
    if (!shouldBeInteractive) {
      rect.set({ selectable: false, evented: false });
      if (this.fabricCanvas.getActiveObject() === rect) {
        this.fabricCanvas.discardActiveObject();
      }
      // Mirrors _onObjectRemoved()'s own cleanup below - undoes the canvas back-reference this
      // branch's own `_set` call establishes, so a non-interactive background rect matches every
      // other object never added via canvas.add().
      rect._set('canvas', undefined);
      this.fabricCanvas.requestRenderAll();
      return;
    }
    // canvasBackgroundRect is assigned straight to fabricCanvas.backgroundImage, never through
    // canvas.add() - only add() (via _onObjectAdded()) wires up this back-reference, which
    // getViewportTransform()/calcOCoords()/rendering all need to place and hit-test controls at
    // all. Without it, setCoords() silently produces no oCoords and nothing draws or responds to
    // clicks, even though selectable/evented and the controls above are all otherwise correct.
    rect._set('canvas', this.fabricCanvas);
    rect.set({
      selectable: true,
      evented: true,
      lockMovementX: true,
      lockMovementY: true,
      lockScalingX: true,
      lockScalingY: true,
      lockRotation: true,
      // hasControls (the corner/edge/rotate handles) is separately hidden per-control below via
      // setControlsVisibility - hasBorders is Fabric's OTHER, independent "draw the selection
      // outline" flag, defaulting to true regardless. Left on, this draws a border around nearly
      // the whole 2D canvas (the background rect spans it) the moment Background Settings' fill
      // goes linear/radial - exactly the design-2d-canvas-wrap border the user is asking about.
      hasBorders: false,
    });
    rect.setControlsVisibility({ ml: false, mr: false, mt: false, mb: false, tl: false, tr: false, bl: false, br: false, mtr: false });
    const commit = (value: FillValue) => this.setCanvasBackgroundFill(value);
    if (fill.type === 'radial') {
      this.attachRadialGradientControls(rect, fill, commit);
    } else {
      this.attachLinearGradientControl(rect, fill, commit);
    }
    if (this.fabricCanvas.getActiveObject() !== rect) {
      this.fabricCanvas.setActiveObject(rect);
    }
    this.fabricCanvas.requestRenderAll();
  }

  private blendWithWhite(hex: string, opacity: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const blend = (channel: number) => Math.round(channel * opacity + 255 * (1 - opacity));
    return `rgb(${blend(r)}, ${blend(g)}, ${blend(b)})`;
  }

  setPatternAccentColor(index: number, color: ColorPickerChange): void {
    if (!this.activePattern) {
      return;
    }
    this.patternAccentColors[index] = color.hex;
    this.patternAccentColorsCmyk[index] = color.cmyk;
    this.rebuildPatternImage();
  }

  togglePatternFlipX(): void {
    if (!this.activePattern) {
      return;
    }
    this.patternFlipX = !this.patternFlipX;
    this.activePattern.set({ flipX: this.patternFlipX });
    this.fabricCanvas.requestRenderAll();
    this.pushHistory();
  }

  togglePatternFlipY(): void {
    if (!this.activePattern) {
      return;
    }
    this.patternFlipY = !this.patternFlipY;
    this.activePattern.set({ flipY: this.patternFlipY });
    this.fabricCanvas.requestRenderAll();
    this.pushHistory();
  }

  // ---- Custom (geometric) pattern - see docs/plans/design3d-geometric-pattern-plan.md ----
  //
  // Unlike a catalog pattern (fetched, recolored by find/replace against its own source text - see
  // addPattern()/rebuildPatternImage()), this is generated from scratch via svgPatternTile() at
  // exactly the target pixel size every time - simpler placement math (no diagonal-based uniform
  // scale/center/rotate-only-lock/clip-path needed, see addGeometricPatternWholeBox()'s own
  // comment) at the cost of needing an explicit rebuild step whenever any control changes, rather
  // than one applied lazily on next paint.

  private geometricPatternBakeToken = 0;

  readonly geometricPatternStyleOptions: { value: GeometricPatternStyle; label: string }[] = [
    { value: 'STRIPES', label: 'Stripes' },
    { value: 'CHECKERBOARD', label: 'Checkerboard' },
    { value: 'DOTS', label: 'Dots' },
    { value: 'DIAGONAL_LINES', label: 'Diagonal Lines' },
    { value: 'GRID', label: 'Grid' },
  ];

  setPatternTabMode(mode: 'library' | 'custom'): void {
    this.patternTabMode = mode;
  }

  setGeometricPatternStyle(style: GeometricPatternStyle): void {
    this.geometricPatternStyle = style;
    this.rebuildSelectedGeometricPattern();
  }

  setGeometricPatternBackgroundColor(color: ColorPickerChange): void {
    this.geometricPatternBackgroundColor = color.hex;
    this.geometricPatternBackgroundColorCmyk = color.cmyk;
    this.rebuildSelectedGeometricPattern();
  }

  setGeometricPatternAccentColor(color: ColorPickerChange): void {
    this.geometricPatternAccentColors = [color.hex];
    this.geometricPatternAccentColorsCmyk = [color.cmyk];
    this.rebuildSelectedGeometricPattern();
  }

  setGeometricPatternScale(scale: number): void {
    this.geometricPatternScale = scale;
    this.rebuildSelectedGeometricPattern();
  }

  private geometricPatternSvgDataUri(width: number, height: number): string {
    const svg = svgPatternTile(
      this.geometricPatternStyle,
      this.geometricPatternBackgroundColor,
      this.geometricPatternAccentColors,
      this.geometricPatternScale,
      this.geometricPatternAngle,
      Math.max(1, Math.round(width)),
      Math.max(1, Math.round(height)),
    );
    return svgToDataUri(svg);
  }

  private stampGeometricPatternProps(target: FabricImage): void {
    Object.assign(target as unknown as GeometricPatternImageProps, {
      isGeometricPattern: true,
      geometricPatternStyle: this.geometricPatternStyle,
      geometricPatternScale: this.geometricPatternScale,
      geometricPatternBackgroundColor: this.geometricPatternBackgroundColor,
      geometricPatternAccentColors: [...this.geometricPatternAccentColors],
      geometricPatternBackgroundColorCmyk: this.geometricPatternBackgroundColorCmyk,
      geometricPatternAccentColorsCmyk: [...this.geometricPatternAccentColorsCmyk],
      fillAngle: this.geometricPatternAngle,
    });
  }

  // Adds (or replaces - see the mutual-exclusivity comment on activeGeometricPattern) the one
  // whole-box custom pattern - diagonal-oversized and centered, same covers-the-canvas-at-any-
  // rotation sizing as addPattern()'s catalog placement (see that method's own comment), generated
  // directly at the target size rather than scaled up to it since geometricPatternSvgDataUri()
  // already takes an explicit pixel size. Movement and scaling stay locked (repositioning or
  // resizing would reintroduce the gap the oversizing rules out), but rotation is now free -
  // applyPatternRotateControl() hides the now-inert corner/edge handles and keeps the rotate
  // handle's reach constant regardless of angle, and applyDesignAreaClipPath() keeps the oversized
  // image from spilling into the margin around the net at any rotation.
  addGeometricPatternWholeBox(): void {
    if (this.activePattern && this.fabricCanvas.getObjects().includes(this.activePattern)) {
      this.fabricCanvas.remove(this.activePattern);
      this.activePattern = undefined;
    }
    if (this.activeGeometricPattern && this.fabricCanvas.getObjects().includes(this.activeGeometricPattern)) {
      this.fabricCanvas.remove(this.activeGeometricPattern);
    }
    this.suppressHistory = true;
    const diagonal = Math.hypot(this.canvasWidthPx, this.canvasHeightPx);
    FabricImage.fromURL(this.geometricPatternSvgDataUri(diagonal, diagonal), { crossOrigin: 'anonymous' })
      .then((fabricImage) => {
        fabricImage.set({
          left: this.canvasMargin + (this.canvasWidthPx - diagonal) / 2,
          top: this.canvasMargin + (this.canvasHeightPx - diagonal) / 2,
          originX: 'left',
          originY: 'top',
          lockMovementX: true,
          lockMovementY: true,
          lockScalingX: true,
          lockScalingY: true,
          transparentCorners: false,
        });
        this.stampGeometricPatternProps(fabricImage);
        this.applyPatternRotateControl(fabricImage);
        this.applyDesignAreaClipPath(fabricImage);
        this.fabricCanvas.add(fabricImage);
        this.fabricCanvas.sendObjectToBack(fabricImage);
        this.activeGeometricPattern = fabricImage;
        this.fabricCanvas.setActiveObject(fabricImage);
        this.fabricCanvas.requestRenderAll();
        this.suppressHistory = false;
        this.pushHistory();
      })
      .catch(() => {
        this.suppressHistory = false;
      });
  }

  // Single-panel custom pattern - a one-shot "stamp" tool, same click-to-place model as
  // fillPanelAt(), just a generated tile image instead of a flat Rect. Unlike the whole-box
  // variant above, this is a normal, freely draggable/resizable/rotatable object (like any other
  // fillPanelAt() stamp) - any number of these can coexist across different panels.
  private fillGeometricPatternPanelAt(scenePoint: { x: number; y: number }): void {
    const panel = findPanelAt(
      scenePoint,
      this.canvasWidthPx,
      this.canvasHeightPx,
      this.canvasMargin,
      this.facePanels,
      this.activeSide === 'inside',
    );
    if (!panel) {
      return;
    }
    const bounds = getPanelPixelBounds(
      panel,
      this.canvasWidthPx,
      this.canvasHeightPx,
      this.canvasMargin,
      this.activeSide === 'inside',
    );
    const width = bounds.right - bounds.left;
    const height = bounds.bottom - bounds.top;
    FabricImage.fromURL(this.geometricPatternSvgDataUri(width, height), { crossOrigin: 'anonymous' }).then((fabricImage) => {
      fabricImage.set({ left: bounds.left, top: bounds.top, originX: 'left', originY: 'top' });
      this.stampGeometricPatternProps(fabricImage);
      this.fabricCanvas.add(fabricImage);
      this.fabricCanvas.setActiveObject(fabricImage);
      this.setGeometricPatternToolActive(false);
      this.fabricCanvas.requestRenderAll();
    });
  }

  // Regenerates whichever geometric pattern instance is currently selected (whole-box or one of
  // possibly-many single-panel ones - see selectedGeometricPattern's own comment) from the current
  // control values, replacing it in place - same "rasterized once loaded, so recoloring means
  // re-rendering from scratch" reasoning as rebuildPatternImage(), and a no-op (not an error) when
  // nothing selected is a geometric pattern, since every setter above calls this unconditionally
  // whether or not one happens to be selected right now (it doubles as "update the pending params
  // for the next whole-box/panel placement").
  private rebuildSelectedGeometricPattern(): void {
    const previous = this.selectedGeometricPattern;
    if (!previous) {
      return;
    }
    const token = ++this.geometricPatternBakeToken;
    const nativeWidth = (previous.width || 1) * (previous.scaleX || 1);
    const nativeHeight = (previous.height || 1) * (previous.scaleY || 1);
    this.suppressHistory = true;
    FabricImage.fromURL(this.geometricPatternSvgDataUri(nativeWidth, nativeHeight), { crossOrigin: 'anonymous' })
      .then((fabricImage) => {
        if (token !== this.geometricPatternBakeToken) {
          this.suppressHistory = false;
          return;
        }
        fabricImage.set({
          left: previous.left,
          top: previous.top,
          originX: previous.originX,
          originY: previous.originY,
          angle: previous.angle,
          flipX: previous.flipX,
          flipY: previous.flipY,
          lockMovementX: previous.lockMovementX,
          lockMovementY: previous.lockMovementY,
          lockScalingX: previous.lockScalingX,
          lockScalingY: previous.lockScalingY,
          lockRotation: previous.lockRotation,
          transparentCorners: previous.transparentCorners,
          // The image was generated at exactly the previous object's own rendered (native x scale)
          // size, so scale resets to 1 here - carrying the old scaleX/scaleY forward too would
          // double-apply it.
          scaleX: 1,
          scaleY: 1,
        });
        this.stampGeometricPatternProps(fabricImage);
        const index = this.fabricCanvas.getObjects().indexOf(previous);
        this.fabricCanvas.remove(previous);
        this.fabricCanvas.insertAt(index, fabricImage);
        if (previous === this.activeGeometricPattern) {
          this.activeGeometricPattern = fabricImage;
          // Control visibility/position and the clip path aren't part of the .set() above (they're
          // not plain properties) and this is a fresh object, not the one addGeometricPatternWholeBox()
          // already ran them on - without reapplying here, a rebuild triggered by any Custom Pattern
          // Settings control (Style/Background/Accent/Scale/Angle) would silently drop back to
          // Fabric's default handles and let the oversized image spill past the die-line again.
          this.applyPatternRotateControl(fabricImage);
          this.applyDesignAreaClipPath(fabricImage);
        }
        this.fabricCanvas.setActiveObject(fabricImage);
        this.fabricCanvas.requestRenderAll();
        this.suppressHistory = false;
        this.pushHistory();
      })
      .catch(() => {
        this.suppressHistory = false;
      });
  }

  private syncGeometricPatternControlsFromObject(props: GeometricPatternImageProps): void {
    this.geometricPatternStyle = props.geometricPatternStyle || 'STRIPES';
    this.geometricPatternScale = props.geometricPatternScale ?? 0.15;
    this.geometricPatternBackgroundColor = props.geometricPatternBackgroundColor || '#ffffff';
    this.geometricPatternBackgroundColorCmyk = props.geometricPatternBackgroundColorCmyk ?? hexToCmyk(this.geometricPatternBackgroundColor);
    this.geometricPatternAccentColors = props.geometricPatternAccentColors?.length ? [...props.geometricPatternAccentColors] : ['#000000'];
    this.geometricPatternAccentColorsCmyk = props.geometricPatternAccentColorsCmyk?.length
      ? [...props.geometricPatternAccentColorsCmyk]
      : this.geometricPatternAccentColors.map((hex) => hexToCmyk(hex));
    this.geometricPatternAngle = props.fillAngle ?? 0;
  }

  // ---- Images (phase 5) ----
  //
  // ImageUploadComponent (already wired since phase 2's basic upload, now via the real component),
  // PixabayModalComponent, and AiImageModalComponent are all reused unmodified - all three are
  // Unity-agnostic, requiring only containerID/user as inputs. design-3d had no auth wired in at
  // all before this; loadUser() is new.
  //
  // The login-required-on-load and idle-session warning/logout below (ngOnInit/startIdleSession())
  // mirror design.component.ts's own gate, but stricter: the login modal here is non-dismissible
  // (ignoreBackdropClick/keyboard:false) - unlike design.component.ts's, which the user can back out
  // of and still design/save anonymously under a guest container ID (see getEffectiveUserId()).
  @ViewChild('uploadImage') uploadImageTemplate!: TemplateRef<any>;
  @ViewChild('uploadPixabay') uploadPixabayTemplate!: TemplateRef<any>;
  @ViewChild('aiImageGenerate') aiImageGenerateTemplate!: TemplateRef<any>;
  @ViewChild('twoSidedWarning') twoSidedWarningTemplate!: TemplateRef<any>;
  @ViewChild('cancelTwoSidedWarning') cancelTwoSidedWarningTemplate!: TemplateRef<any>;
  @ViewChild('clearAllWarning') clearAllWarningTemplate!: TemplateRef<any>;
  @ViewChild('previewHelp') previewHelpTemplate!: TemplateRef<any>;
  @ViewChild('backgroundHelp') backgroundHelpTemplate!: TemplateRef<any>;
  @ViewChild('textHelp') textHelpTemplate!: TemplateRef<any>;
  @ViewChild('patternHelp') patternHelpTemplate!: TemplateRef<any>;
  @ViewChild('shapeHelp') shapeHelpTemplate!: TemplateRef<any>;
  @ViewChild('imageHelp') imageHelpTemplate!: TemplateRef<any>;
  @ViewChild('designAreaHelp') designAreaHelpTemplate!: TemplateRef<any>;
  @ViewChild('aiCategoryHelp') aiCategoryHelpTemplate!: TemplateRef<any>;
  @ViewChild('drawCategoryHelp') drawCategoryHelpTemplate!: TemplateRef<any>;
  @ViewChild('patternsCategoryHelp') patternsCategoryHelpTemplate!: TemplateRef<any>;
  @ViewChild('shapesCategoryHelp') shapesCategoryHelpTemplate!: TemplateRef<any>;
  @ViewChild('imagesCategoryHelp') imagesCategoryHelpTemplate!: TemplateRef<any>;
  @ViewChild('materialCategoryHelp') materialCategoryHelpTemplate!: TemplateRef<any>;
  @ViewChild('tutorialVideo') tutorialVideoTemplate!: TemplateRef<any>;
  @ViewChild('nameDesign') nameDesignTemplate!: TemplateRef<any>;

  private imagesLoaded = false;
  user?: User;
  images: any[] = [];
  aiImageGenerationEnabled = !!this.environment.features?.aiImageGeneration;

  private loadUser(): Promise<void> {
    return this.userService.getCurrentUser().then(
      (user: User) => {
        this.user = user;
      },
      () => {
        this.user = undefined;
      }
    );
  }

  // Mirrors design.component.ts's idle-session wiring: warn before the session expires, then force
  // a logout + redirect if the user never dismisses the warning. Only meaningful once actually
  // logged in, so this is only started after loadUser() finds a user (see ngOnInit).
  private startIdleSession(): void {
    this.idleSession.start();
    this.idleSubscriptions.add(
      this.idleSession.warningStarted$.subscribe(() => {
        this.modalRef = this.bsModalService.show(this.userIdleTemplate);
      })
    );
    this.idleSubscriptions.add(
      this.idleSession.expired$.subscribe(() => {
        this.closeModal();
        this.idleLogout.handleIdleExpired();
      })
    );
  }

  continueSession(): void {
    this.idleSession.reset();
    this.closeModal();
  }

  // Mirrors design.component.ts's getImages(): logged-in users see their saved image library;
  // anonymous users see whatever images already live in this container.
  getImages(): void {
    const request = this.user
      ? this.imagesService.getUserImages(this.user.id ?? 0).then((images: any[]) =>
          images.map((image) => ({
            ...image,
            src: `${this.environment.apiHost}/project/image/${this.user!.id}/${image.name}?v=${image.updated}`,
          }))
        )
      : // getContainerFiles() resolves with the raw {container, success, files} response, not a
        // bare array (confirmed against production's design.component.ts, which unwraps the same
        // way) - filtering the wrapper itself threw "files.filter is not a function" for every
        // anonymous/unauthenticated viewer, silently breaking the "your uploaded images" grid.
        this.imagesService.getContainerFiles(this.containerID).then((container: { files: any[] }) =>
          (container.files || [])
            .filter((file) => file.contentType?.type === 'image')
            .map((file) => ({
              ...file,
              src: `${this.environment.apiHost}${this.apiPath}${this.containerID}/${file.name}/?v=${file.updated}`,
            }))
        );
    request.then((images) => {
      this.images = images;
    });
  }

  openImageModal(template: TemplateRef<any>): void {
    this.modalRef = this.bsModalService.show(template);
  }

  addImage(image: any): void {
    const imageUrl = this.user ? image.src : `${this.environment.apiHost}${this.apiPath}${this.containerID}/${image.name}/`;
    void this.addImageUrlToCanvas(imageUrl);
  }

  // AiImageModalComponent's imageGenerated output (as opposed to imageCreated, which just refreshes
  // the saved-library list) - the "generate and place immediately" path.
  addGeneratedImage(image: { src?: string }): void {
    if (!image?.src) {
      return;
    }
    void this.addImageUrlToCanvas(image.src);
    this.getImages();
  }

  private addImageUrlToCanvas(url: string): Promise<FabricImage> {
    // crossOrigin: 'anonymous' keeps the fabric canvas untainted - patterns/uploads/Pixabay/AI
    // images all load from a different origin than the UI, and both cdn.companybox.com and
    // api.companybox.com already send matching CORS headers. Without this, the on-screen 2D
    // canvas still renders fine, but WebGL's texImage2D silently refuses to upload pixel data
    // from a tainted canvas, so the 3D box texture never picks up the change.
    return FabricImage.fromURL(url, { crossOrigin: 'anonymous' }).then((fabricImage) => {
      const scale = Math.min(
        (this.canvasWidthPx * 0.6) / (fabricImage.width || 1),
        (this.canvasHeightPx * 0.6) / (fabricImage.height || 1),
        1
      );
      fabricImage.set({
        left: this.canvasMargin + this.canvasWidthPx / 2,
        top: this.canvasMargin + this.canvasHeightPx / 2,
        // Center origin means (left, top) above is the object's own center, so it lands centered
        // in the design area regardless of its size - see loadGridlinesOverlay() for why Fabric's
        // 'center' default can't just be left alone everywhere else in this file.
        originX: 'center',
        originY: 'center',
        scaleX: scale,
        scaleY: scale,
      });
      this.fabricCanvas.add(fabricImage);
      this.fabricCanvas.setActiveObject(fabricImage);

      // This net has many small print panels, most of which aren't the panel currently facing the
      // camera - a pattern/image dropped at the default top-left position is applied correctly (the
      // 3D texture sync itself is fine, confirmed in browser via direct WebGL/texture inspection),
      // but silently lands on a panel the camera isn't currently showing, reading as "it didn't
      // work" from the 2D side. Reuse the same panel-focus turn a manual panel click triggers, aimed
      // at the new object's own center, so the camera comes around to show what was just added.
      this.handlePanelFocusClick({
        x: fabricImage.left,
        y: fabricImage.top,
      });

      return fabricImage;
    });
  }

  // Unity's addShape() throws away most of the shape's own data for triangle/rect/circle (bare
  // SendMessage, no payload - Unity owns hardcoded defaults for those internally) and only carries
  // real geometry for "poly" (4 hardcoded named point sets) and the default/generic branch (raw SVG
  // path data in shape.content). canvas-shape.component.ts's thumbnail renderer is the only place
  // with real Fabric geometry for all five branches, so this mirrors its switch/shape configs with
  // the thumbnail-only interaction-locking properties and 25-50px thumbnail sizing replaced by real
  // canvas defaults.
  addShape(shape: any): void {
    const type = (shape?.type || '').toLowerCase();
    let object: Rect | Circle | Triangle | Polygon | Path | null = null;

    switch (type) {
      case 'triangle':
        object = new Triangle({ width: 80, height: 80, fill: '#3b6fd6' });
        break;
      case 'rect':
        object = new Rect({ width: 80, height: 80, fill: '#3b6fd6' });
        break;
      case 'circle':
        object = new Circle({ radius: 40, fill: '#3b6fd6' });
        break;
      case 'poly':
        object = this.buildPolygonShape(shape);
        break;
      default:
        object = this.buildPathShape(shape);
        break;
    }

    if (!object) {
      return;
    }
    // Fabric defaults every object's strokeWidth to 1, not 0 - left alone, a freshly-added shape's
    // toolbar would show "1" despite having no stroke color set (invisible either way), which reads
    // as wrong once the user actually starts raising it from a real zero.
    object.set({
      left: this.canvasMargin + this.canvasWidthPx / 2,
      top: this.canvasMargin + this.canvasHeightPx / 2,
      // Center origin means (left, top) above is the shape's own center, so it lands centered in
      // the design area regardless of its size - see loadGridlinesOverlay() for why Fabric's
      // 'center' default can't just be left alone everywhere else in this file.
      originX: 'center',
      originY: 'center',
      strokeWidth: 0,
    });
    this.fabricCanvas.add(object);
    this.fabricCanvas.setActiveObject(object);
  }

  // Canonical point data confirmed identical in both design.component.ts's handlePolygon() and
  // canvas-shape.component.ts's handlePolygon() - these 4 named shapes are the only "poly" shapes
  // in the catalog with real geometry; anything else falls through with no shape added (Unity's own
  // equivalent silently mis-renders a 4th shape for unknown names - not worth replicating that bug).
  private buildPolygonShape(shape: any): Polygon | null {
    const named: Record<string, { x: number; y: number }[]> = {
      'trapezoid 1': [
        { x: -100, y: -50 },
        { x: 100, y: -50 },
        { x: 150, y: 50 },
        { x: -150, y: 50 },
      ],
      'emerald 1': [
        { x: 0, y: -125 },
        { x: 108, y: -62.5 },
        { x: 108, y: 62.5 },
        { x: 0, y: 125 },
        { x: -108, y: 62.5 },
        { x: -108, y: -62.5 },
      ],
      'star 1': [
        { x: 350, y: 75 },
        { x: 380, y: 160 },
        { x: 470, y: 160 },
        { x: 400, y: 215 },
        { x: 423, y: 301 },
        { x: 350, y: 250 },
        { x: 277, y: 301 },
        { x: 303, y: 215 },
        { x: 231, y: 161 },
        { x: 321, y: 161 },
      ],
      'star 2': [
        { x: 0, y: 0 },
        { x: 100, y: 50 },
        { x: 200, y: 0 },
        { x: 150, y: 100 },
        { x: 200, y: 200 },
        { x: 100, y: 150 },
        { x: 0, y: 200 },
        { x: 50, y: 100 },
        { x: 0, y: 0 },
      ],
    };
    const points = named[(shape?.name || '').toLowerCase()];
    if (!points) {
      return null;
    }
    const polygon = new Polygon(points, { fill: '#3b6fd6' });
    const targetSize = 100;
    const scale = targetSize / Math.max(polygon.width || 1, polygon.height || 1);
    polygon.set({ scaleX: scale, scaleY: scale });
    return polygon;
  }

  private buildPathShape(shape: any): Path | null {
    if (!shape?.content) {
      return null;
    }
    const path = new Path(shape.content, { fill: '#3b6fd6' });
    const targetSize = 100;
    const scale = targetSize / Math.max(path.width || 1, path.height || 1);
    path.set({ scaleX: scale, scaleY: scale });
    return path;
  }

  // Injects the Font dropdown's Google Fonts stylesheet the first time the designer loads, instead
  // of the host app's index.html loading it (render-blocking) on every page. Guarded on the href so
  // navigating away and back to the designer - or a second Design3dComponent instance - doesn't
  // append a duplicate <link>.
  private ensureFontStylesheetLoaded(): void {
    const href = Design3dComponent.FONT_STYLESHEET_URL;
    if (this.document.head.querySelector(`link[href="${href}"]`)) {
      return;
    }
    const link = this.document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    this.document.head.appendChild(link);
  }

  ngOnInit(): void {
    this.ensureFontStylesheetLoaded();
    const storedSplit = Number(this.localStorageService.getItem(this.SPLIT_STORAGE_KEY));
    if (Number.isFinite(storedSplit) && storedSplit >= this.SPLIT_MIN && storedSplit <= this.SPLIT_MAX) {
      this.splitRatio = storedSplit;
    }

    // Independent of the route/box loading below (which still proceeds underneath) - the
    // host app's DESIGN_3D_AUTH_GATE provider is what actually keeps a signed-out visitor from
    // using the builder (cb-ui blocks behind a non-dismissible login modal), not a gate on this
    // loading itself. fedex-ui's shell already guarantees a user before this component loads, so
    // its provider resolves immediately.
    this.loadUser().then(() => {
      if (this.user) {
        this.startIdleSession();
        this.resolveUserReady();
      } else {
        this.authGate.ensureAuthenticated().then(() =>
          this.loadUser().then(() => {
            this.startIdleSession();
            this.resolveUserReady();
          })
        );
      }
    });

    this.route.params.subscribe((rawParams) => {
      const params = stripUndefinedParams(rawParams);
      this.productDetails = params;
      this.containerID = params['ContainerName'];
      this.category = params['category'] || params['Category'];
      this.subcategory = params['subcategory'] || params['Subcategory'];
      this.categoryName = params['categoryName'] || this.category;

      if (!this.containerID) {
        this.loading = false;
        this.valid = false;
        return;
      }

      // Already-chosen values from box-options - carried through in the route's productDetails the
      // same way for both /design and /design-3d (see goToBuilder() in box-options.component.ts).
      this.printing = params['print'] || '';
      this.outerMaterial = params['outerMaterial'] || '';
      this.innerMaterial = params['innerMaterial'] || '';
      this.finish = params['finish'] || '';
      this.luxe = params['luxe'] === 'true';
      this.standard = !this.luxe;
      this.twoSided = params['twoSided'] === 'true';
      this.insideOnly = params['insideOnly'] === 'true';
      this.isOtherProduct = ['tape', 'part', 'tissue', 'crinkle'].includes(this.subcategory);
      this.isTissue = this.subcategory === 'tissue';
      this.isPaddedMailer = this.subcategory.startsWith('RPM');
      this.loadSubcategoryMeta();
      this.loadPanelNames();

      this.http.get(`${this.environment.apiHost}${this.apiPath}${this.containerID}/info`).subscribe({
        next: async (info: any) => {
          this.dimensions = info.info;
          this.initScene();
          this.initCanvas2d();
          await this.loadBox();
          this.loading = false;
          this.animate();
          // Deferred to run only once a real user exists (see userReady) - the box/canvas above
          // still loads and renders immediately, underneath the non-dismissible login modal, same
          // as before. Once the design comes in, the textures loadBox() already built from the
          // still-blank canvas are resynced to show it.
          void this.loadExistingDesign().then(() => {
            this.refreshDesignTextureFor('outside');
            this.refreshDesignTextureFor('inside');
          });
        },
        error: () => {
          this.loading = false;
          this.valid = false;
          this.showNotFoundModal();
        },
      });
    });
  }

  // Shown when the container's dimensions, its 3D model, its box config, or its previously-saved
  // design fails to load - same "We're sorry, you've found an empty box" modal the old Unity
  // builder shows on any of its equivalent box/subcategory/preset load failures (design.component.ts's
  // getPresets()/getDHC()/etc). Guarded by notFoundModalShown since several of those loads can fail
  // independently (some run in parallel) and only one modal should ever be shown.
  private showNotFoundModal(): void {
    if (this.notFoundModalShown) {
      return;
    }
    this.notFoundModalShown = true;
    this.bsModalService.show(this.notFoundModalTemplate, { class: 'modal-lg' });
  }

  ngOnDestroy(): void {
    this.idleSubscriptions.unsubscribe();
    this.idleSession.stop();
    if (this.animationFrameId !== undefined) {
      cancelAnimationFrame(this.animationFrameId);
    }
    this.resizeObserver?.disconnect();
    this.canvasColumnResizeObserver?.disconnect();
    this.canvasScrollRef?.nativeElement.removeEventListener('scroll', this.updateRulerScrollOffset);
    // Not endCanvasPan() - that touches fabricCanvas.upperCanvasEl, which dispose() below may have
    // already torn down. A mid-drag unmount just needs the window listeners gone.
    this.canvasPanState = undefined;
    window.removeEventListener('pointermove', this.handleCanvasPanMove);
    window.removeEventListener('pointerup', this.endCanvasPan);
    window.removeEventListener('pointercancel', this.endCanvasPan);
    window.removeEventListener('keydown', this.updatePanModifierState);
    window.removeEventListener('keyup', this.updatePanModifierState);
    window.removeEventListener('blur', this.resetPanModifierState);
    this.controls?.dispose();
    this.renderer?.dispose();
    (['outside', 'inside'] as Side[]).forEach((side) => {
      const state = this.sideState[side];
      if (state.textureRefreshHandle !== undefined) {
        cancelAnimationFrame(state.textureRefreshHandle);
      }
      state.fabricCanvas?.dispose();
      state.designTexture?.dispose();
    });
  }

  // ---- 2D design canvas (phase 2) ----

  private initCanvas2d(): void {
    const width = this.dimensions.width || 1;
    const height = this.dimensions.height || 1;
    this.ppi = this.maxCanvasPx / Math.max(width, height);
    this.canvasWidthPx = Math.round(width * this.ppi);
    this.canvasHeightPx = Math.round(height * this.ppi);

    // Outside and inside each get their own Fabric canvas (see the "---- Per-side design state
    // ----" fields above) - both built up front, at the same fixed resolution, regardless of which
    // one activeSide starts on, so toggleShowingSide() is just a visibility swap with no reload.
    this.initCanvas2dFor('outside', this.canvas2dOutsideRef.nativeElement);
    this.initCanvas2dFor('inside', this.canvas2dInsideRef.nativeElement);

    this.updateCanvas2dScale();
    this.canvasColumnResizeObserver = new ResizeObserver(() => this.updateCanvas2dScale());
    this.canvasColumnResizeObserver.observe(this.canvasColumnRef.nativeElement);

    // Keeps the ruler tick marks tracking whatever part of the design canvasScrollRef currently
    // shows - see updateRulerScrollOffset(). Outside the zone and a direct DOM write, same reasoning
    // as the crosshair above: scroll fires on every pixel too.
    this.ngZone.runOutsideAngular(() => {
      this.canvasScrollRef.nativeElement.addEventListener('scroll', this.updateRulerScrollOffset, {
        passive: true,
      });
    });
    this.pinViewportChrome();
    this.updateRulerScrollOffset();
  }

  private initCanvas2dFor(side: Side, canvasEl: HTMLCanvasElement): void {
    const canvas = new Canvas(canvasEl, {
      width: this.canvasWidthPx + this.canvasMargin * 2,
      height: this.canvasHeightPx + this.canvasMargin * 2,
      backgroundColor: '#ffffff',
      // Fabric's default (false) always draws the active/selected object on top regardless of its
      // real position in the stack - harmless for everything else here, but it meant clicking the
      // pattern (e.g. to grab its rotate handle) visibly brought it back in front of whatever
      // sendObjectToBack() had already put it behind. true makes rendering respect actual z-order
      // even while an object is selected.
      preserveObjectStacking: true,
    });
    // Draw the active object's controls after the overlay, not before - so the die-line guide
    // shows through over anything placed on the canvas, but never covers a resize handle.
    canvas.controlsAboveOverlay = true;
    this.sideState[side].fabricCanvas = canvas;
    this.loadGridlinesOverlayFor(side, canvas);

    // These three fire only from real user edits on this specific canvas - side is captured in the
    // closure (not read off activeSide), so they push into the right side's history even while it's
    // the inactive one being set up here at init.
    canvas.on('object:added', () => this.pushHistoryFor(side));
    canvas.on('object:modified', () => this.pushHistoryFor(side));
    canvas.on('object:removed', (e) => {
      if (e.target === this.sideState[side].activePattern) {
        this.sideState[side].activePattern = undefined;
      }
      this.pushHistoryFor(side);
    });

    // Selection/pan/fill/crosshair/panel-focus all drive shared (not per-side) UI state - guarded
    // so only the currently-visible canvas can affect it. In practice the inactive canvas is
    // pointer-events:none (see .design-2d-canvas-wrap), so these guards are a safety net, not the
    // primary mechanism.
    // Guarded against cropModeActive too - selecting the temporary crop rect/dim overlay (see
    // startImageCrop()) must not flip the toolbar away from Image Settings/Crop while it's open.
    canvas.on('selection:created', () => {
      if (this.activeSide === side && !this.cropModeActive) {
        this.updateSelectionState();
      }
    });
    canvas.on('selection:updated', () => {
      if (this.activeSide === side && !this.cropModeActive) {
        this.updateSelectionState();
      }
    });
    canvas.on('selection:cleared', () => {
      if (this.activeSide !== side) {
        return;
      }
      // A click on empty canvas during crop mode would otherwise silently drop the crop rect's own
      // handles (Fabric clearing "active object" doesn't remove it) - re-pin it as active so the
      // handles stay grabbable until Apply/Cancel explicitly ends the session.
      if (this.cropModeActive && this.cropRect) {
        canvas.setActiveObject(this.cropRect);
        canvas.requestRenderAll();
        return;
      }
      if (!this.cropModeActive) {
        this.selectedElementType = null;
        this.updateBackgroundGradientInteractivity();
      }
    });

    // One-shot like Fill (see setFillToolActive()'s own comment), not sticky like Move/Select -
    // Fabric fires this once per finished stroke, for every brush type (Pencil/Circle/Spray alike),
    // right after the stroke's own object:added has already pushed history for it above. Reverting
    // to Select here (not just turning off isDrawingMode directly) reuses setCanvasTool()'s existing
    // cleanup - selection/skipTargetFind/cursor all get put back the same way leaving Move does.
    canvas.on('path:created', () => {
      if (this.activeSide === side && this.canvasTool === 'draw') {
        this.setCanvasTool('select');
      }
    });

    // Phase 3: keep the live box texture in sync with every canvas repaint, not just
    // history-worthy changes (drag/resize in progress fires after:render on every frame but
    // doesn't fire object:modified until released). Unlike selection above, this runs for both
    // sides regardless of which is active - the box mesh's inside and outside material slots both
    // need their live texture kept current at all times.
    canvas.on('after:render', () => this.scheduleDesignTextureRefreshFor(side));

    // Phase 4: any click on the 2D canvas turns the 3D camera to face the corresponding panel - see
    // handlePanelFocusClick() - whether it lands on empty canvas or on a design object (image,
    // shape, text, pattern). This runs alongside Fabric's own selection/drag/resize handling, not
    // instead of it, so clicking an object still selects/drags it as normal. The Move tool preempts
    // both this and the fill tool - a pan-drag is neither a panel-focus click nor a fill stamp.
    canvas.on('mouse:down', (opt) => {
      if (this.activeSide !== side) {
        return;
      }
      if (this.canvasTool === 'move') {
        this.beginCanvasPan(opt.e);
        return;
      }
      if (this.canvasTool === 'draw') {
        return;
      }
      if (this.fillToolActive) {
        this.fillPanelAt(opt.scenePoint);
        return;
      }
      if (this.geometricPatternToolActive) {
        this.fillGeometricPatternPanelAt(opt.scenePoint);
        return;
      }
      this.handlePanelFocusClick(opt.scenePoint);
    });

    // Ruler crosshair - fires on every pixel of movement, so it stays outside Angular's zone and
    // writes styles straight to the DOM rather than going through change detection. See
    // updateCrosshair().
    this.ngZone.runOutsideAngular(() => {
      canvas.on('mouse:move', (opt) => {
        // Suppressed while a rotate/resize is in progress - transformHud (below) is showing at this
        // same pointer position then, and the two used to sit right on top of each other.
        if (this.activeSide === side && !this.transformHudActive) {
          this.updateCrosshair(opt.scenePoint);
        }
        if (this.activeSide === side) {
          this.updatePanelHover(opt.scenePoint);
        }
      });
      canvas.on('mouse:out', () => {
        if (this.activeSide === side) {
          this.hideCrosshair();
          this.hidePanelHover();
        }
      });

      // Angle/size readout next to the pointer while actively rotating or resizing - same
      // outside-the-zone, direct-DOM-write reasoning as the crosshair above (rotating/scaling fire
      // on every pixel of drag too). Both numbers always come from the target's own current state
      // (not just whichever gesture is active), so rotating and scaling share one combined "angle -
      // size" readout instead of each showing its own half and the two overlapping on screen. See
      // updateTransformHud().
      canvas.on('object:rotating', (opt) => {
        if (this.activeSide === side) {
          this.updateTransformHud(opt.pointer, opt.target);
        }
      });
      canvas.on('object:scaling', (opt) => {
        if (this.activeSide === side) {
          this.updateTransformHud(opt.pointer, opt.target);
        }
      });
      canvas.on('mouse:up', () => this.hideTransformHud());
    });

    // Auto-fit: a Textbox grows/shrinks its width to its content as the user types (see
    // autoFitTextbox()) up to the design area's width, then wraps instead of growing further.
    // Dragging the side resize handle fires 'object:resizing' (Textbox's own width-drag control,
    // distinct from the corner controls' 'object:scaling') - once a user has done that, their
    // chosen width is intentional, so auto-fit stops touching that box from then on.
    canvas.on('text:changed', (opt) => {
      if (opt.target instanceof Textbox) {
        this.autoFitTextbox(opt.target);
      }
    });
    canvas.on('object:resizing', (opt) => {
      if (opt.target instanceof Textbox) {
        (opt.target as unknown as { autoFit?: boolean }).autoFit = false;
      }
    });

    this.pushHistoryFor(side);
  }

  // Fits the fixed-resolution ruler/canvas block (see canvas2dScale's own comment) to whatever
  // space the 65% column currently has, minus its own padding - recomputed whenever that column
  // resizes (flyout opening/closing, window resize, sidebar changes) or the ruler toggle changes
  // the block's native size.
  //
  // Ruler thickness is a fixed screen-px constant (RULER_SCREEN_THICKNESS_PX) now, not
  // canvasMargin * fitScale - a small, low-risk box in a big column used to leave fitScale capped
  // at 1 (never grow past native pixel size) specifically to keep that product bounded, since
  // canvasMargin is a fixed NATIVE amount that would otherwise blow the ruler up to several hundred
  // SCREEN px right along with any uncapped growth. That cap meant the design itself never grew to
  // fill genuinely spare column space either - only design-2d-canvas-scroll (the empty area around
  // it) did. Decoupling the two lets fitScale grow to actually fill the space (up to
  // CANVAS_2D_FIT_SCALE_MAX, so a tiny box doesn't blow up to a blurry, pixelated extreme) while the
  // ruler stays a small, constant thickness - the deliberate trade-off is that the ruler may no
  // longer fully cover canvasMargin's own bleed-margin band at a large fitScale, unlike before.
  private updateCanvas2dScale(): void {
    const column = this.canvasColumnRef?.nativeElement;
    if (!column || !this.canvasWidthPx || !this.canvasHeightPx) {
      return;
    }
    const columnPadding = 24;
    const availableWidth = column.clientWidth - columnPadding;
    const availableHeight = column.clientHeight - columnPadding;
    const rulerScreenThickness = this.showRulers ? RULER_SCREEN_THICKNESS_PX : 0;
    const designNativeWidth = this.canvasWidthPx + this.canvasMargin * 2;
    const designNativeHeight = this.canvasHeightPx + this.canvasMargin * 2;
    if (availableWidth <= rulerScreenThickness || availableHeight <= rulerScreenThickness) {
      return;
    }
    // Solving fitScale = availableWidth / (designNativeWidth + rulerThicknessPx) together with
    // rulerThicknessPx = rulerScreenThickness / fitScale (ruler thickness is fixed in screen px, so
    // its NATIVE size - what actually drives the grid's own layout - has to shrink as fitScale
    // grows) for fitScale directly avoids that circularity: fitScale * designNativeWidth =
    // availableWidth - rulerScreenThickness.
    this.canvas2dFitScale = Math.min(
      CANVAS_2D_FIT_SCALE_MAX,
      (availableWidth - rulerScreenThickness) / designNativeWidth,
      (availableHeight - rulerScreenThickness) / designNativeHeight,
    );
    this.rulerThicknessPx = rulerScreenThickness / this.canvas2dFitScale;
    this.rulerGridWidthPx = designNativeWidth + this.rulerThicknessPx;
    this.rulerGridHeightPx = designNativeHeight + this.rulerThicknessPx;
    // Combined value, kept only for buildTicks()'s tick-density decision (effectivePpi) - never
    // applied as a transform itself. The template applies canvas2dFitScale to the outer ruler-grid
    // block (ruler chrome + corner-button insets below) and canvas2dZoom separately to just
    // .design-2d-canvas-wrap, so zooming grows/shrinks the design without resizing or moving the
    // rulers/icons around it.
    this.canvas2dScale = this.canvas2dFitScale * this.canvas2dZoom;
    // See canvas2dCenterOffsetX/Y's own comment above. designNativeWidth/Height above is exactly
    // the wrap's own fixed box size (template's width.px/height.px binding), so (1 - zoom) of it,
    // halved, is how far the shrunk content needs to shift right/down to sit centered in that box.
    this.canvas2dCenterOffsetX = this.canvas2dZoom < 1 ? (designNativeWidth * (1 - this.canvas2dZoom)) / 2 : 0;
    this.canvas2dCenterOffsetY = this.canvas2dZoom < 1 ? (designNativeHeight * (1 - this.canvas2dZoom)) / 2 : 0;
    this.canZoomIn = this.canvas2dZoom < CANVAS_2D_ZOOM_MAX - 1e-6;
    this.canZoomOut = this.canvas2dZoom > CANVAS_2D_ZOOM_MIN + 1e-6;
    // The Select/Move buttons only show past 100% zoom (template's @if) - falling back to (or
    // starting below) that leaves Move with no visible way back to Select, so force it back here
    // rather than stranding the user in pan mode with hidden controls.
    if (this.canvas2dZoom <= 1 && this.canvasTool === 'move') {
      this.setCanvasTool('select');
    }
    // Tick subdivision depends on the resulting on-screen ppi (see buildTicks()), so this has to
    // be rebuilt on every rescale, not just once at load.
    this.rulerTicksX = buildTicks(this.dimensions.width || 1, this.ppi, this.canvas2dScale, this.canvasMargin);
    this.rulerTicksY = buildTicks(this.dimensions.height || 1, this.ppi, this.canvas2dScale, this.canvasMargin);
    this.pinViewportChrome();
  }

  // Positions the corner-button groups - called after every fit-scale change (here, and
  // updateCanvas2dScale()/toggleRulers()). Scrolling canvasColumnRef used to be how panning worked,
  // which meant the ruler strips and this chrome needed constant JS correction just to hold still -
  // canvasScrollRef (see its own comment) now contains all of that scrolling instead, so this chrome
  // (and the ruler strips - see .ruler-x/.ruler-y's own comment) sits entirely outside it and simply
  // never moves on scroll, needing no correction and no re-run on every scroll event at all. The
  // remaining, scroll-driven piece - shifting the ruler tick marks and the crosshair to track
  // whatever's currently scrolled into view - lives in updateRulerScrollOffset()/updateCrosshair().
  //
  // canvasScrollRef's own rect is static (canvas2dZoom only grows .design-2d-canvas-wrap, a
  // descendant of it, not canvasScrollRef's own layout box), so plain fit-scale-letterbox math is
  // enough here - no clamping or scroll-awareness needed, unlike an earlier version of this that
  // measured the design's actual (zoom- and scroll-affected) rendered edge instead.
  private pinViewportChrome(): void {
    const column = this.canvasColumnRef?.nativeElement;
    const scrollEl = this.canvasScrollRef?.nativeElement;
    if (!column || !scrollEl) {
      return;
    }
    const colRect = column.getBoundingClientRect();
    const cellRect = scrollEl.getBoundingClientRect();

    const top = cellRect.top - colRect.top;
    const left = cellRect.left - colRect.left;
    const right = colRect.right - cellRect.right;
    const bottom = colRect.bottom - cellRect.bottom;

    const leftGroupStyle = this.cornerGroupLeftRef.nativeElement.style;
    leftGroupStyle.top = `${top + 20}px`;
    leftGroupStyle.left = `${left + 20}px`;
    const rightGroupStyle = this.cornerGroupRightRef.nativeElement.style;
    rightGroupStyle.top = `${top + 20}px`;
    rightGroupStyle.right = `${right + 20}px`;
    if (this.cornerGroupBottomRef) {
      this.cornerGroupBottomRef.nativeElement.style.bottom = `${bottom + 20}px`;
    }
  }

  // Shifts the ruler tick marks to track whatever part of the (possibly zoomed, scrolled)  design
  // canvasScrollRef currently shows - registered on canvasScrollRef's own scroll event (initCanvas2d())
  // and called once up front. ruler-x/ruler-y are static, fixed-size, overflow: hidden strips (see
  // their own SCSS comment) sitting outside canvasScrollRef entirely, each wrapping an inner *Ticks
  // element sized to the ticks' full (zoomed) span - translating that inner element by -scrollLeft/Top
  // is what makes the visible slice of ticks match the visible slice of design, the same way the
  // design's own content naturally does by scrolling. Local (canvasScrollRef's own, pre-fit-scale)
  // pixels, not screen ones - both scrollLeft/Top and the ticks' own left/top (tick.pos * canvas2dZoom,
  // see the template) already live in that space, so no fit-scale conversion is needed here.
  private updateRulerScrollOffset = (): void => {
    const scrollEl = this.canvasScrollRef?.nativeElement;
    if (!scrollEl) {
      return;
    }
    if (this.rulerXTicksRef) {
      this.rulerXTicksRef.nativeElement.style.transform = `translateX(${-scrollEl.scrollLeft}px)`;
    }
    if (this.rulerYTicksRef) {
      this.rulerYTicksRef.nativeElement.style.transform = `translateY(${-scrollEl.scrollTop}px)`;
    }
  };

  // Moves the crosshair overlay and its coordinate readout to the pointer - registered outside
  // Angular's zone (see initCanvas2d()) since mouse:move fires on every pixel of movement. point
  // is already in the same native pixel space as the ruler ticks (see buildTicks()), so it needs
  // the same canvas2dZoom multiplier the ticks get (design-2d-canvas-wrap's own transform - the
  // crosshair lines live in the static ruler-grid block, not the wrap), then the same
  // rulerThicknessPx-wide ruler-strip offset the canvas itself sits behind in the grid (unmultiplied,
  // since it belongs to the ruler frame, not the zoomed design) - minus canvasScrollRef's own current
  // scroll, since the crosshair lives outside that scroll viewport (see .design-2d-canvas-scroll's
  // own comment) and so, unlike before, no longer gets it applied for free just by sitting inside it.
  private updateCrosshair(point: { x: number; y: number }): void {
    const vertical = this.crosshairVerticalRef?.nativeElement;
    const horizontal = this.crosshairHorizontalRef?.nativeElement;
    const label = this.crosshairLabelRef?.nativeElement;
    if (!vertical || !horizontal || !label) {
      return;
    }
    const scrollEl = this.canvasScrollRef?.nativeElement;
    const rulerOffset = this.rulerThicknessPx;
    const x = point.x * this.canvas2dZoom + rulerOffset - (scrollEl?.scrollLeft ?? 0) + this.canvas2dCenterOffsetX;
    const y = point.y * this.canvas2dZoom + rulerOffset - (scrollEl?.scrollTop ?? 0) + this.canvas2dCenterOffsetY;
    vertical.style.transform = `translateX(${x}px)`;
    horizontal.style.transform = `translateY(${y}px)`;
    vertical.style.display = 'block';
    horizontal.style.display = 'block';

    // Readout is in design-area inches (0 at the die-line, not the canvasMargin bleed), matching
    // what the ruler ticks themselves measure from.
    const xInches = (point.x - this.canvasMargin) / this.ppi;
    const yInches = (point.y - this.canvasMargin) / this.ppi;
    label.textContent = `${xInches.toFixed(2)}", ${yInches.toFixed(2)}"`;
    // Offset from the pointer so the text doesn't sit under the crosshair lines it's labeling.
    label.style.transform = `translate(${x + 8}px, ${y + 8}px)`;
    label.style.display = 'block';
  }

  // Moves the rotate/resize readout to the pointer, same coordinate math as updateCrosshair() (see
  // its own comment) - pointer here is object:rotating/object:scaling's own BasicTransformEvent
  // pointer, which (like mouse:move's scenePoint) is already in scene/native pixel space. Angle and
  // size are read off the target itself rather than passed in per-gesture, so rotating and scaling
  // both land in the one "angle - size" string instead of each showing only its own half.
  private updateTransformHud(pointer: { x: number; y: number }, target: FabricObject): void {
    const hud = this.transformHudRef?.nativeElement;
    if (!hud) {
      return;
    }
    const angle = Math.round(((target.angle % 360) + 360) % 360);
    const widthIn = target.getScaledWidth() / this.ppi;
    const heightIn = target.getScaledHeight() / this.ppi;
    const scrollEl = this.canvasScrollRef?.nativeElement;
    const rulerOffset = this.rulerThicknessPx;
    const x = pointer.x * this.canvas2dZoom + rulerOffset - (scrollEl?.scrollLeft ?? 0) + this.canvas2dCenterOffsetX;
    const y = pointer.y * this.canvas2dZoom + rulerOffset - (scrollEl?.scrollTop ?? 0) + this.canvas2dCenterOffsetY;
    hud.textContent = `${widthIn.toFixed(2)}" × ${heightIn.toFixed(2)}" - ${angle}°`;
    hud.style.transform = `translate(${x + 8}px, ${y + 8}px)`;
    hud.style.display = 'block';
    this.transformHudActive = true;
    this.hideCrosshair();
  }

  private hideTransformHud(): void {
    const hud = this.transformHudRef?.nativeElement;
    if (hud) {
      hud.style.display = 'none';
    }
    this.transformHudActive = false;
  }

  private hideCrosshair(): void {
    const vertical = this.crosshairVerticalRef?.nativeElement;
    const horizontal = this.crosshairHorizontalRef?.nativeElement;
    const label = this.crosshairLabelRef?.nativeElement;
    if (vertical) {
      vertical.style.display = 'none';
    }
    if (horizontal) {
      horizontal.style.display = 'none';
    }
    if (label) {
      label.style.display = 'none';
    }
  }

  // Resolves the pointer to a FacePanel the same way handlePanelFocusClick()/fillPanelAt() do, then
  // updates the outline + tooltip only on an actual panel change - not on every mouse:move pixel,
  // since that fires far more often than the hovered panel itself changes. Always shows the outline
  // regardless of the active tool (fill/pattern/crop) - unlike the crosshair, there's no competing
  // readout at the panel's own corner for it to collide with.
  private updatePanelHover(scenePoint: { x: number; y: number }): void {
    const panel = findPanelAt(
      scenePoint,
      this.canvasWidthPx,
      this.canvasHeightPx,
      this.canvasMargin,
      this.facePanels,
      this.activeSide === 'inside',
    );
    if (panel === this.hoveredPanel) {
      return;
    }
    this.hoveredPanel = panel;
    if (!panel) {
      this.hidePanelHoverOutline();
      this.hidePanelNameTooltip();
      return;
    }
    this.showPanelHoverOutline(panel);
    this.showPanelNameTooltip(panel);
  }

  private hidePanelHover(): void {
    this.hoveredPanel = undefined;
    this.hidePanelHoverOutline();
    this.hidePanelNameTooltip();
  }

  // A DOM element (inline SVG), not a Fabric Rect on the canvas - same absolutely-positioned-plus-
  // translate technique as the crosshair/tooltip above. Fabric's overlayImage (the gridlines guide,
  // see loadGridlinesOverlayFor()) always renders after every canvas object regardless of z-order -
  // that's the whole point of it (see the guide's own comment: "shows through over whatever's been
  // placed") - so a Fabric object can never sit above it no matter how far bringObjectToFront()
  // pushes it. Living outside Fabric's render pipeline entirely, in the same DOM layer as the
  // tooltip, is what lets the outline show above the guide too.
  //
  // The path traces the panel's real boundary (getPanelBoundaryPixelPoints(), built from
  // computeFaceBoundaryUV() on buildFacePanels()) as one subpath per disconnected region of the
  // panel (a rectangle plus separate, unconnected corner tabs is a real panel shape, not just a
  // hypothetical - see computeFaceBoundaryUV()'s own comment), falling back to a plain 4-point
  // rectangle from getPanelPixelBounds() when that geometry didn't resolve at all. Points are
  // expressed in the SVG's own viewBox (0,0 to the panel's own unscaled width/height, panel-local
  // rather than canvas-absolute) so the browser's native viewBox-to-element scaling handles
  // canvas2dZoom for the shape for free - only the outer <svg>'s own position/size need that
  // multiplier applied by hand, same as every other element in this DOM layer.
  private showPanelHoverOutline(panel: FacePanel): void {
    const svg = this.panelHoverOutlineRef?.nativeElement;
    const path = this.panelHoverOutlinePathRef?.nativeElement;
    if (!svg || !path) {
      return;
    }
    const bounds = getPanelPixelBounds(
      panel,
      this.canvasWidthPx,
      this.canvasHeightPx,
      this.canvasMargin,
      this.activeSide === 'inside',
    );
    const width = bounds.right - bounds.left;
    const height = bounds.bottom - bounds.top;
    const loops = getPanelBoundaryPixelPoints(
      panel,
      this.canvasWidthPx,
      this.canvasHeightPx,
      this.canvasMargin,
      this.activeSide === 'inside',
    ) ?? [
      [
        { x: bounds.left, y: bounds.top },
        { x: bounds.right, y: bounds.top },
        { x: bounds.right, y: bounds.bottom },
        { x: bounds.left, y: bounds.bottom },
      ],
    ];
    const d = loops
      .map((loop) => {
        const local = loop.map((p) => `${p.x - bounds.left},${p.y - bounds.top}`);
        return `M ${local[0]} L ${local.slice(1).join(' ')} Z`;
      })
      .join(' ');

    const scrollEl = this.canvasScrollRef?.nativeElement;
    const rulerOffset = this.rulerThicknessPx;
    const x = bounds.left * this.canvas2dZoom + rulerOffset - (scrollEl?.scrollLeft ?? 0) + this.canvas2dCenterOffsetX;
    const y = bounds.top * this.canvas2dZoom + rulerOffset - (scrollEl?.scrollTop ?? 0) + this.canvas2dCenterOffsetY;

    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    svg.style.width = `${width * this.canvas2dZoom}px`;
    svg.style.height = `${height * this.canvas2dZoom}px`;
    svg.style.transform = `translate(${x}px, ${y}px)`;
    svg.style.display = 'block';
    path.setAttribute('d', d);
  }

  private hidePanelHoverOutline(): void {
    const svg = this.panelHoverOutlineRef?.nativeElement;
    if (svg) {
      svg.style.display = 'none';
    }
  }

  // Pinned just outside the panel's hover outline, not inside it: left edge even with the outline's
  // left edge, bottom edge flush against the outline's top edge. Same canvas2dZoom/scroll/
  // center-offset math as updateCrosshair(), fed getPanelPixelBounds()'s top-left corner - the
  // trailing translateY(-100%) then walks the tooltip up by exactly its own rendered height so its
  // bottom (not its top) lands on that corner, without needing to measure the element in JS.
  private showPanelNameTooltip(panel: FacePanel): void {
    const tooltip = this.panelNameTooltipRef?.nativeElement;
    if (!tooltip) {
      return;
    }
    // Master toggle for the whole tooltip feature - loadPanelNames() already skips fetching the
    // data at all when this is off, so panelNames would be undefined here anyway, but checking the
    // flag directly (rather than relying on that side effect) keeps this correct even if the flag
    // changes after panelNames was already loaded.
    if (!this.environment.features?.panelNames) {
      tooltip.style.display = 'none';
      return;
    }
    // Panel names track the guide overlay: when the active side's gridlines aren't showing (guides
    // toggled off, or this side's gridlines image failed to load), there's no name either - not
    // just whichever side's canvas happens to be active.
    if (!this.gridlinesShownFor(this.activeSide)) {
      tooltip.style.display = 'none';
      return;
    }
    // panel.name absent from the map entirely means panel-names hasn't loaded yet (or this box type
    // has no file at all) - nothing to show. A key present but mapped to null is different: the
    // backend has explicitly marked that panel as not printable on either side, which still gets a
    // tooltip.
    if (!this.panelNames || !(panel.name in this.panelNames)) {
      tooltip.style.display = 'none';
      return;
    }
    const faceEntry = this.panelNames[panel.name];
    if (!faceEntry) {
      this.renderPanelNameTooltip(panel, 'Not visible', true);
      return;
    }
    // Named per side, not just per panel - hovering the outside canvas shows that side's name, the
    // inside canvas shows the inside name (currently always a placeholder like "4 (stub)" for this
    // box type - see the hideStubPanelNames check below). Absent for this side entirely means this
    // panel isn't printable on the currently active side (e.g. a panel that only exists on the
    // outside atlas) - same "Not visible" tooltip as a face excluded on both sides above, not a
    // silently-hidden tooltip.
    const sideEntry = faceEntry[this.activeSide];
    if (!sideEntry) {
      this.renderPanelNameTooltip(panel, 'Not visible', true);
      return;
    }
    // Manufacturing glue stubs aren't meaningful design areas to a user hovering the canvas - skip
    // the tooltip for them (the outline still shows, same as any other panel) - gated separately
    // from the panelNames flag above since a box style could want names on but stubs still hidden.
    if (this.environment.features?.hideStubPanelNames && sideEntry.name.toLowerCase().includes('stub')) {
      tooltip.style.display = 'none';
      return;
    }
    this.renderPanelNameTooltip(panel, sideEntry.name, false);
  }

  private renderPanelNameTooltip(panel: FacePanel, text: string, notPrintable: boolean): void {
    const tooltip = this.panelNameTooltipRef?.nativeElement;
    if (!tooltip) {
      return;
    }
    const bounds = getPanelPixelBounds(
      panel,
      this.canvasWidthPx,
      this.canvasHeightPx,
      this.canvasMargin,
      this.activeSide === 'inside',
    );
    const scrollEl = this.canvasScrollRef?.nativeElement;
    const rulerOffset = this.rulerThicknessPx;
    const x = bounds.left * this.canvas2dZoom + rulerOffset - (scrollEl?.scrollLeft ?? 0) + this.canvas2dCenterOffsetX;
    const y = bounds.top * this.canvas2dZoom + rulerOffset - (scrollEl?.scrollTop ?? 0) + this.canvas2dCenterOffsetY;
    tooltip.textContent = text;
    tooltip.classList.toggle('panel-name-tooltip--not-printable', notPrintable);
    tooltip.style.transform = `translate(${x}px, ${y}px) translateY(-100%)`;
    tooltip.style.display = 'block';
  }

  private hidePanelNameTooltip(): void {
    const tooltip = this.panelNameTooltipRef?.nativeElement;
    if (tooltip) {
      tooltip.style.display = 'none';
    }
  }

  // 1 initial attempt + 3 retries, doubling the delay each time (500ms, 1s, 2s) - covers a
  // transient network blip or a generation job that's still finishing on the backend. A gridlines
  // PNG that's genuinely missing (never generated for this container) still fails after all 4.
  private static readonly GRIDLINES_MAX_ATTEMPTS = 4;
  private static readonly GRIDLINES_RETRY_DELAY_MS = 500;

  private loadGridlinesOverlayFor(side: Side, canvas: Canvas): void {
    this.fetchGridlinesImage(side, 1)
      .then((image) => {
        image.set({
          left: this.canvasMargin,
          top: this.canvasMargin,
          // Fabric's default origin is 'center' (not 'left'/'top'), so without this the image's
          // center - not its corner - lands at (canvasMargin, canvasMargin), shifting it up-left
          // by half its own size and clipping the top/left portion off-canvas.
          originX: 'left',
          originY: 'top',
          // gridlines.png is cropped 1px short on its right/bottom edge (source asset defect - the
          // die-line stroke itself is drawn right up to that missing edge), so scaling to exactly
          // canvasWidthPx/canvasHeightPx lands the blank cropped edge on the design area's true
          // right/bottom boundary and whatever's underneath (background color/image) shows through
          // instead of the line. Scaling 1px larger pushes that blank edge 1px past the boundary,
          // into the canvasMargin (see field) frame around it, so the real line lands on the edge.
          // gridlines_inside.png is assumed to share the same defect/convention.
          scaleX: (this.canvasWidthPx + 1) / (image.width || 1),
          scaleY: (this.canvasHeightPx + 1) / (image.height || 1),
          selectable: false,
          evented: false,
          // Covers toObject()/toJSON()/toSVG() (undo/redo history, and Download SVG/PDF export) -
          // but NOT raw canvas rasterization (the 3D texture), which ignores this flag entirely.
          // See renderWithoutOverlayFor().
          excludeFromExport: true,
        });
        this.sideState[side].gridlinesImage = image;
        this.applyGridlinesVisibilityFor(side, canvas);
      })
      .catch((error) => {
        this.sideState[side].gridlinesAvailable = false;
        console.error(
          `loadGridlinesOverlayFor: gave up on ${this.gridlinesUrlFor(side)} after ${Design3dComponent.GRIDLINES_MAX_ATTEMPTS} attempts`,
          error,
        );
      });
  }

  private fetchGridlinesImage(side: Side, attempt: number): Promise<FabricImage> {
    return FabricImage.fromURL(this.gridlinesUrlFor(side)).catch((error) => {
      if (attempt >= Design3dComponent.GRIDLINES_MAX_ATTEMPTS) {
        throw error;
      }
      const delayMs = Design3dComponent.GRIDLINES_RETRY_DELAY_MS * 2 ** (attempt - 1);
      return new Promise<void>((resolve) => setTimeout(resolve, delayMs)).then(() =>
        this.fetchGridlinesImage(side, attempt + 1),
      );
    });
  }

  private applyGridlinesVisibilityFor(side: Side, canvas: Canvas): void {
    const state = this.sideState[side];
    canvas.overlayImage = this.showGridlines && state.gridlinesAvailable ? state.gridlinesImage : undefined;
    canvas.requestRenderAll();
  }

  // Same condition applyGridlinesVisibilityFor() uses to decide whether that side's guide overlay
  // actually renders - showPanelNameTooltip() reuses it so panel names track the guide precisely
  // (off when guides are toggled off, and off for a side whose gridlines image failed to load, not
  // just tied to which canvas is active).
  private gridlinesShownFor(side: Side): boolean {
    return this.showGridlines && this.sideState[side].gridlinesAvailable;
  }

  private applyGridlinesVisibility(): void {
    this.applyGridlinesVisibilityFor(this.activeSide, this.fabricCanvas);
  }

  toggleGridlines(): void {
    this.showGridlines = !this.showGridlines;
    // Applies to both sides immediately, not just the active one, so the guide's on/off state
    // doesn't silently reset back to "on" the next time the user switches sides.
    (['outside', 'inside'] as Side[]).forEach((side) => {
      const canvas = this.sideState[side].fabricCanvas;
      if (canvas) {
        this.applyGridlinesVisibilityFor(side, canvas);
      }
    });
    // Panel names track this toggle live (see showPanelNameTooltip()) - re-render immediately
    // rather than waiting for the next hover change, so a currently-shown tooltip doesn't linger
    // after guides are turned off (or fail to reappear after they're turned back on while the
    // pointer never left the panel).
    if (this.hoveredPanel) {
      this.showPanelNameTooltip(this.hoveredPanel);
    }
  }

  // The rotate handle's default position is anchored to the object's own real (oversized, often
  // off-canvas for a pattern) top edge, offset a further ~40px beyond it - for an object this big
  // that lands the handle well outside the visible canvas, unreachable once rotated away from
  // wherever it happened to land. Fabric computes both the handle position and its connecting line
  // from the same `mtr.y * object.height*object.scaleY + mtr.offsetY`, with the line drawn from
  // `y*height*scaleY` (the object's own edge by default) to that final point - setting `y: 0` moves
  // the line's start to the object's actual center instead, so it runs the full way out to the
  // handle, and offsetY alone (a safe fixed reach, independent of how oversized this specific
  // pattern's diagonal-covering scale made it) places the handle a constant, always-on-canvas
  // distance out at any rotation angle. Not part of Fabric's serialized state (control definitions
  // aren't), so restoreHistory() re-runs this on a restored pattern the same as addPattern() does
  // on a new one.
  private applyPatternRotateControl(fabricImage: FabricImage): void {
    fabricImage.setControlsVisibility({ ml: false, mr: false, mt: false, mb: false, tl: false, tr: false, bl: false, br: false });
    const reach = Math.min(this.canvasWidthPx, this.canvasHeightPx) / 2 + 30;
    fabricImage.controls['mtr'].y = 0;
    fabricImage.controls['mtr'].offsetY = -reach;
    // canvas.add() already cached this object's oCoords using the control's original y/offsetY,
    // before this override ran - the connecting line reads y/offsetY live on every render so it
    // always shows the new position, but the square itself is drawn from that now-stale cache, so
    // without this they render in different places until something else forces oCoords to recompute.
    fabricImage.setCoords();
  }

  // Two extra Fabric controls (alongside the normal move/scale/rotate ones, not replacing them) for
  // dragging a radial gradient's center and radius directly on the canvas - see
  // docs/plans/design3d-gradient-fills-plan.md's "Radial drag handles" section. Both work entirely
  // in the shape's own native (unscaled, unrotated) coordinate space, the same convention Fabric's
  // own polygon-point controls use (see fabric's controls/polyControl.ts) - forwardMatrix composes
  // the viewport with the object's own transform for drawing the handle each frame, while
  // actionHandler inverts just the object's own transform (no viewport - Fabric's pointer is
  // already in scene space) to turn a drag position back into that same native space.
  //
  // Deliberately does NOT rebake the gradient image on every drag tick. buildGradientFabricImage()/
  // setShapeFill()/setCanvasBackgroundFill() swap the object instance via fabricCanvas.remove()/
  // insertAt()/setActiveObject() (or backgroundImage reassignment), which would sever Fabric's own
  // in-progress transform.target reference mid-gesture and break the drag. Instead each tick just
  // updates the object's own live fillValue and re-renders - the handle moves immediately, the
  // gradient image itself catches up once, in mouseUpHandler, which calls `commit` exactly as if the
  // change had been made through the picker.
  //
  // `commit` is setShapeFill for a selected shape, or setCanvasBackgroundFill for the background
  // rect (see updateBackgroundGradientInteractivity()) - both take the same FillValue shape, so one
  // control implementation covers both call sites.
  private attachRadialGradientControls(
    shape: FabricObject,
    initial: Extract<FillValue, { type: 'radial' }>,
    commit: (value: Extract<FillValue, { type: 'radial' }>) => void,
  ): void {
    // center/radius can go missing on an otherwise-valid radial fillValue that round-tripped
    // through undo/redo history from before this component's own history-serialization guarantees
    // covered them - same "tolerate old/incomplete data" stance as applyDesignJsonFor()'s own
    // fallbacks. Defaulted to dead center/half-width rather than crashing (nothing read center/
    // radius before this control existed, so this is the first code to ever notice a gap here).
    const readValue = (target: InteractiveFabricObject): Extract<FillValue, { type: 'radial' }> => {
      const fv = (target as unknown as GradientImageProps).fillValue;
      if (fv?.type !== 'radial') {
        return initial;
      }
      return { ...fv, center: fv.center ?? { x: 0.5, y: 0.5 }, radius: fv.radius ?? 0.5 };
    };
    const writeValue = (target: InteractiveFabricObject, next: Extract<FillValue, { type: 'radial' }>): void => {
      (target as unknown as GradientImageProps).fillValue = next;
    };
    const forwardMatrix = (target: InteractiveFabricObject) =>
      util.multiplyTransformMatrices(target.getViewportTransform(), target.calcTransformMatrix());
    const localPoint = (target: InteractiveFabricObject, x: number, y: number): Point =>
      util.sendPointToPlane(new Point(x, y), undefined, target.calcOwnMatrix());
    const commitOnRelease = (transform: { target: FabricObject }): boolean => {
      commit(readValue(transform.target as unknown as InteractiveFabricObject));
      return true;
    };

    shape.controls['gradientCenter'] = new Control({
      cursorStyle: 'move',
      actionName: 'modifyGradientCenter',
      positionHandler: (_dim, _finalMatrix, target) => {
        const { center } = readValue(target);
        const width = target.width || 1;
        const height = target.height || 1;
        return new Point((center.x - 0.5) * width, (center.y - 0.5) * height).transform(forwardMatrix(target));
      },
      actionHandler: (_eventData, transform, x, y) => {
        const target = transform.target as unknown as InteractiveFabricObject;
        const width = target.width || 1;
        const height = target.height || 1;
        const local = localPoint(target, x, y);
        writeValue(target, {
          ...readValue(target),
          center: { x: Math.min(1, Math.max(0, 0.5 + local.x / width)), y: Math.min(1, Math.max(0, 0.5 + local.y / height)) },
        });
        target.canvas?.requestRenderAll();
        return true;
      },
      mouseUpHandler: (_eventData, transform) => commitOnRelease(transform),
    });

    // Always sits directly to the right of center, at distance `radius` (a fraction of width, same
    // units as center.x) - only horizontal drag distance from center matters, vertical position is
    // ignored, so this control's own affordance stays a simple "drag right/left to resize" rather
    // than needing the user to trace a specific radius arc.
    shape.controls['gradientRadius'] = new Control({
      cursorStyle: 'ew-resize',
      actionName: 'modifyGradientRadius',
      positionHandler: (_dim, _finalMatrix, target) => {
        const { center, radius } = readValue(target);
        const width = target.width || 1;
        const height = target.height || 1;
        return new Point((center.x - 0.5 + radius) * width, (center.y - 0.5) * height).transform(forwardMatrix(target));
      },
      actionHandler: (_eventData, transform, x, y) => {
        const target = transform.target as unknown as InteractiveFabricObject;
        const width = target.width || 1;
        const current = readValue(target);
        const centerLocalX = (current.center.x - 0.5) * width;
        const local = localPoint(target, x, y);
        writeValue(target, { ...current, radius: Math.min(1.5, Math.max(0.02, (local.x - centerLocalX) / width)) });
        target.canvas?.requestRenderAll();
        return true;
      },
      mouseUpHandler: (_eventData, transform) => commitOnRelease(transform),
    });

    shape.setCoords();
  }

  // One extra Fabric control for dragging a linear gradient's angle directly on the canvas - same
  // conventions as attachRadialGradientControls() above (native-space math, commit-on-release-only,
  // shared between a selected shape and the background rect), but there's no center/radius to carry
  // for a linear fill - just a rotation around the object's own geometric center. The handle sits a
  // fixed distance out (LINEAR_HANDLE_DISTANCE) in the gradient's current direction; dragging it
  // recomputes that direction's angle via atan2, which already matches Fabric's clockwise-positive
  // convention (0deg = left-to-right) in this y-down canvas coordinate space with no sign flip
  // needed - see svgGradient()'s own angleDegrees param doc.
  private static readonly LINEAR_HANDLE_DISTANCE = 0.4;

  // Fabric's own rotate handle (patterns/images) snaps via InteractiveFabricObject.ownDefaults.
  // snapAngle/snapThreshold near the top of this file, but that mechanism only wires into Fabric's
  // built-in rotationWithSnapping actionHandler - a fully custom control like this one has to
  // reimplement the same "snap once within snapThreshold degrees of a snap point" behavior itself.
  // Reuses that same 5deg threshold for a consistent feel, but every 15deg here rather than the
  // pattern/image default's every 45deg, per request.
  private static readonly ANGLE_SNAP_INCREMENT = 15;

  private attachLinearGradientControl(
    shape: FabricObject,
    initial: Extract<FillValue, { type: 'linear' }>,
    commit: (value: Extract<FillValue, { type: 'linear' }>) => void,
  ): void {
    const readValue = (target: InteractiveFabricObject): Extract<FillValue, { type: 'linear' }> => {
      const fv = (target as unknown as GradientImageProps).fillValue;
      return fv?.type === 'linear' ? fv : initial;
    };
    const writeValue = (target: InteractiveFabricObject, next: Extract<FillValue, { type: 'linear' }>): void => {
      (target as unknown as GradientImageProps).fillValue = next;
    };
    const forwardMatrix = (target: InteractiveFabricObject) =>
      util.multiplyTransformMatrices(target.getViewportTransform(), target.calcTransformMatrix());
    const localPoint = (target: InteractiveFabricObject, x: number, y: number): Point =>
      util.sendPointToPlane(new Point(x, y), undefined, target.calcOwnMatrix());
    const snapAngle = (degrees: number): number => {
      const normalized = ((degrees % 360) + 360) % 360;
      const increment = Design3dComponent.ANGLE_SNAP_INCREMENT;
      const nearest = Math.round(normalized / increment) * increment;
      const diff = Math.min(Math.abs(normalized - nearest), 360 - Math.abs(normalized - nearest));
      return diff <= (InteractiveFabricObject.ownDefaults.snapThreshold ?? 5) ? nearest % 360 : normalized;
    };

    shape.controls['gradientAngle'] = new Control({
      cursorStyle: 'grab',
      actionName: 'modifyGradientAngle',
      positionHandler: (_dim, _finalMatrix, target) => {
        const { angle } = readValue(target);
        const width = target.width || 1;
        const rad = (angle * Math.PI) / 180;
        const distance = Design3dComponent.LINEAR_HANDLE_DISTANCE * width;
        return new Point(distance * Math.cos(rad), distance * Math.sin(rad)).transform(forwardMatrix(target));
      },
      actionHandler: (_eventData, transform, x, y) => {
        const target = transform.target as unknown as InteractiveFabricObject;
        const local = localPoint(target, x, y);
        const degrees = (Math.atan2(local.y, local.x) * 180) / Math.PI;
        writeValue(target, { ...readValue(target), angle: snapAngle(degrees) });
        target.canvas?.requestRenderAll();
        return true;
      },
      mouseUpHandler: (_eventData, transform) => {
        commit(readValue(transform.target as unknown as InteractiveFabricObject));
        return true;
      },
    });

    shape.setCoords();
  }

  // A pattern's diagonal-based sizing intentionally overshoots the die-line (see addPattern()) -
  // without this it paints straight into the workspace margin around the net, not just up to its
  // edges. gridlines.png was tried as an alpha-based clip first (it draws the exact die-cut
  // polygon, notches and rounded corners included), but checking the raw file directly showed its
  // alpha channel only encodes the guide lines themselves - it isn't a filled inside/outside mask,
  // so there's nothing usable there to clip against. Clipping to the design area's own rectangle
  // instead is coarser (a pattern can still show through the notch cutouts within that rectangle)
  // but reliable, and matches what "outside the gridlines" means in practice - the workspace margin
  // added around the net, not individual notch shapes.
  private applyDesignAreaClipPath(target: FabricImage): void {
    target.set({
      clipPath: new Rect({
        left: this.canvasMargin,
        top: this.canvasMargin,
        // Fabric's default origin is 'center' (not 'left'/'top' - see loadGridlinesOverlay()), so
        // without this the clip rect's center - not its corner - lands at (canvasMargin, canvasMargin),
        // clipping away most of the canvas instead of covering it.
        originX: 'left',
        originY: 'top',
        width: this.canvasWidthPx,
        height: this.canvasHeightPx,
        absolutePositioned: true,
      }),
    });
  }

  // Fabric's overlayImage participates in raw canvas rendering (unlike toObject()/toSVG(), which
  // respect excludeFromExport) - anything that rasterizes the canvas must go through this to avoid
  // baking the die-line guide into the actual design: the 3D box texture. Takes an explicit canvas
  // rather than the active-side getter since the 3D texture refresh runs for both sides regardless
  // of which is currently on screen.
  private renderWithoutOverlayFor<T>(canvas: Canvas, render: () => T): T {
    const overlay = canvas.overlayImage;
    canvas.overlayImage = undefined;
    const result = render();
    canvas.overlayImage = overlay;
    return result;
  }

  // Crops toCanvasElement()'s snapshot down to just the design area, excluding the canvasMargin
  // border on every side - the box texture (and its UV mapping, sized off info.width/height) only
  // ever expects the printable net itself, never the blank margin around it.
  private toDesignAreaCanvasElementFor(canvas: Canvas): HTMLCanvasElement {
    return canvas.toCanvasElement(1, {
      left: this.canvasMargin,
      top: this.canvasMargin,
      width: this.canvasWidthPx,
      height: this.canvasHeightPx,
    });
  }

  // Same crop as toDesignAreaCanvasElementFor(), for toSVG() instead of the raster path - keeps the
  // SVG/PDF download the same size as the gridlines guide (canvasWidthPx x canvasHeightPx), not the
  // full canvas with its canvasMargin bleed border included.
  private designAreaSVGOptions(): TSVGExportOptions {
    return {
      viewBox: {
        x: this.canvasMargin,
        y: this.canvasMargin,
        width: this.canvasWidthPx,
        height: this.canvasHeightPx,
      },
      width: `${this.canvasWidthPx}`,
      height: `${this.canvasHeightPx}`,
    };
  }

  toggleRulers(): void {
    this.showRulers = !this.showRulers;
    // Rulers change the block's own native size, not the column's - ResizeObserver only fires on
    // the observed element's size changing, so this needs an explicit recompute.
    this.updateCanvas2dScale();
    // showRulers also flips the ruler-x/y @if - force the view to actually create those elements now
    // (updating rulerXTicksRef/rulerYTicksRef) so updateRulerScrollOffset() below has current refs to
    // write to. Without this, a newly-shown ruler strip's ticks get created with no scroll-offset
    // transform at all (its own listener hasn't fired yet) and stay misaligned with the currently
    // scrolled-to design until some later scroll event happens to fix it.
    this.cdr.detectChanges();
    this.updateRulerScrollOffset();
    // updateCanvas2dScale()'s own pinViewportChrome() call ran before detectChanges() above, against
    // the DOM as it stood before this toggle - rulerThicknessPx had already changed by then, but the
    // template's [style.gridTemplateColumns]/[style.gridTemplateRows] binding it feeds hadn't been
    // applied to the DOM yet, so canvasScrollRef's own rect (what the corner-button groups position
    // themselves against) still reflected the old layout. Re-running it now, against the DOM
    // detectChanges() just brought current, is what positions the corner-button groups against the
    // ruler strip's actual new (on or off) footprint instead of its footprint from before this toggle.
    this.pinViewportChrome();
  }

  // 2D-canvas-only zoom (see CANVAS_2D_ZOOM_STEP above) - changes canvas2dZoom, then re-derives
  // canvas2dScale from the current fit scale the same way a column resize would. Deliberately
  // doesn't touch the 3D viewer's own camera/OrbitControls zoom (initScene()) - the two canvases
  // are independent views of the design and this only affects how the flat net is displayed.
  zoomIn(): void {
    this.zoomAroundViewportCenter(() => {
      this.canvas2dZoom = Math.min(CANVAS_2D_ZOOM_MAX, this.canvas2dZoom * CANVAS_2D_ZOOM_STEP);
    });
  }

  zoomOut(): void {
    this.zoomAroundViewportCenter(() => {
      this.canvas2dZoom = Math.max(CANVAS_2D_ZOOM_MIN, this.canvas2dZoom / CANVAS_2D_ZOOM_STEP);
    });
  }

  // Only shown (see template's @if) once canvas2dZoom has actually moved off 1 - zoomIn()/
  // zoomOut() only ever reach exactly 1 via this method or the initial field value, never through
  // the *=/ /= step itself, so the template's strict !== 1 check doesn't need an epsilon.
  resetZoom(): void {
    this.zoomAroundViewportCenter(() => {
      this.canvas2dZoom = 1;
    });
  }

  // Keeps whatever design point is centered in canvasScrollRef's own viewport centered after a zoom
  // change, instead of the view staying pinned to the top-left (design-2d-canvas-wrap's own
  // transform-origin - see its comment) and the zoomed-in content growing away toward the
  // bottom-right corner, out of view. Measures the tracked point in screen space both before and
  // after the change rather than deriving the scroll viewport's post-zoom scroll bounds
  // analytically, because canvas2dRef's cumulative on-screen position already reflects every
  // transform in the chain (fit scale on the outer block, zoom scale on the wrap) via
  // getBoundingClientRect() - exactly what's needed, without having to re-derive that chain by hand.
  //
  // The tracked point is the center of the design's *visible* rect - canvasRectBefore intersected
  // with the viewport, not canvasScrollRef's own raw center: canvasScrollRef is sized to the
  // available column space now (see its own comment), which can be considerably bigger than the
  // design itself (e.g. a small box, or before zooming in), so canvasScrollRef's own geometric
  // center often falls in the empty margin past the design's own edge rather than on the design at
  // all. Zooming around a point out there isn't grounded in anything the user can see change size,
  // and since it can even sit past the design's actual scrollable extent, the resulting scroll
  // adjustment could be silently clamped by the browser to less than intended - which looked exactly
  // like zoom not recentering at all, just growing the design toward the bottom-right unchecked. When
  // the whole design is already visible (the common small-design case) this converges to the
  // design's own center; once zoom has grown the design past the viewport, it converges to
  // canvasScrollRef's own center - i.e. exactly the original behavior in that regime.
  //
  // The "after" measurement needs the DOM's [style.transform] already updated to the new zoom -
  // cdr.detectChanges() forces that synchronously instead of waiting on Angular's normal
  // zone-triggered change detection (which wouldn't run until this whole call stack, including the
  // (click) handler, unwinds) or requestAnimationFrame (which, tried first, turned out to never
  // fire at all in some hosting contexts - e.g. a backgrounded/occluded tab - silently breaking
  // this for exactly the click that triggered it). getBoundingClientRect() right after
  // detectChanges() forces a synchronous layout pass, so it reflects the new transform immediately
  // - no frame needs to actually paint.
  private zoomAroundViewportCenter(applyZoom: () => void): void {
    const scrollEl = this.canvasScrollRef.nativeElement;
    // Both side canvases are the same size, absolutely stacked in the same spot (see
    // .design-2d-canvas-wrap) - getElement() always reaches whichever one is actually visible.
    const canvasEl = this.fabricCanvas.getElement();
    const scrollRect = scrollEl.getBoundingClientRect();
    const canvasRectBefore = canvasEl.getBoundingClientRect();
    const visibleLeft = Math.max(scrollRect.left, canvasRectBefore.left);
    const visibleTop = Math.max(scrollRect.top, canvasRectBefore.top);
    const visibleRight = Math.min(scrollRect.right, canvasRectBefore.right);
    const visibleBottom = Math.min(scrollRect.bottom, canvasRectBefore.bottom);
    const viewportCenterX = (visibleLeft + visibleRight) / 2;
    const viewportCenterY = (visibleTop + visibleBottom) / 2;
    const totalScaleBefore = this.canvas2dFitScale * this.canvas2dZoom;
    // The design-space point (native canvas pixels) currently sitting at the viewport's center.
    const nativeX = (viewportCenterX - canvasRectBefore.left) / totalScaleBefore;
    const nativeY = (viewportCenterY - canvasRectBefore.top) / totalScaleBefore;

    applyZoom();
    this.updateCanvas2dScale();
    this.cdr.detectChanges();
    // updateCanvas2dScale()'s own pinViewportChrome() call ran before detectChanges() above, so on
    // the zoom step that first crosses the > 1 threshold, cornerGroupBottomRef was still undefined
    // (the @if hadn't created #cornerGroupBottom yet) and its bottom style never got set - leaving it
    // to whatever position it happens to render at instead of pinned to the viewport's bottom edge.
    // Re-run now that detectChanges() has created it.
    this.pinViewportChrome();
    // Chrome (confirmed via direct testing - not just theory) can leave canvasScrollRef's own
    // scrollHeight/Width stuck at its un-overflowing value the very first time a zoom grows
    // .design-2d-canvas-wrap enough to overflow it, even though the wrap's own getBoundingClientRect()
    // already reflects the new, overflowing size correctly - every zoom after that first one recomputes
    // it fine. Whatever's cached, an overflow: hidden/auto round-trip forces it to let go - without
    // this, the scrollLeft/Top write below silently clamps to 0 against the stale (too-small) max,
    // which looked exactly like the zoomed design growing toward the bottom-right uncontained instead
    // of the view recentering on it.
    const previousOverflow = scrollEl.style.overflow;
    scrollEl.style.overflow = 'hidden';
    void scrollEl.offsetHeight;
    scrollEl.style.overflow = previousOverflow;

    const totalScaleAfter = this.canvas2dFitScale * this.canvas2dZoom;
    // Where that same design-space point now renders on screen, at the new scale but still at the
    // old scroll position - the gap between this and the viewport center is exactly how far the
    // view has drifted off-center, in either direction.
    const canvasRectAfter = canvasEl.getBoundingClientRect();
    const screenXNow = canvasRectAfter.left + nativeX * totalScaleAfter;
    const screenYNow = canvasRectAfter.top + nativeY * totalScaleAfter;
    scrollEl.scrollLeft += screenXNow - viewportCenterX;
    scrollEl.scrollTop += screenYNow - viewportCenterY;
    // Setting scrollLeft/Top does dispatch a native 'scroll' event that updateRulerScrollOffset()
    // also listens for, but browsers coalesce/defer it a frame or two - calling it directly here
    // avoids a visible flash of the ruler ticks lagging a beat behind this one-shot jump (as opposed
    // to a continuous pan-drag, where that same one-frame lag isn't noticeable).
    this.updateRulerScrollOffset();
  }

  // toCanvasElement() briefly mutates the canvas's own width/height/viewportTransform/
  // skipControlsDrawing to render a clean (no-controls) snapshot, then restores them - calling it
  // synchronously from inside the after:render handler that fires it re-enters that state while
  // Fabric's own render/interaction cycle is still using it, which was corrupting both the live
  // on-screen controls and later adds. Deferring to the next frame runs it outside that call stack.
  private scheduleDesignTextureRefreshFor(side: Side): void {
    const state = this.sideState[side];
    if (state.textureRefreshHandle !== undefined) {
      return;
    }
    state.textureRefreshHandle = requestAnimationFrame(() => {
      state.textureRefreshHandle = undefined;
      this.refreshDesignTextureFor(side);
    });
  }

  // Fabric's interactive Canvas draws the active object's selection borders/handles onto the same
  // "lower" canvas that holds the actual artwork (getElement() doesn't isolate them onto a separate
  // overlay the way the two-canvas architecture suggests it might) - toCanvasElement() is Fabric's
  // own clean-export path, which temporarily suppresses control drawing for one render and restores
  // it after, so the box texture never picks up selection UI while the 2D editor still shows it.
  private refreshDesignTextureFor(side: Side): void {
    const state = this.sideState[side];
    if (!state.designTexture || !state.fabricCanvas) {
      return;
    }
    // The crop rect/dim overlay (see startImageCrop()) are real canvas objects, not the
    // overlayImage renderWithoutOverlayFor() below already knows to skip - without this they'd
    // bake straight into the 3D texture while the user is still mid-drag.
    if (this.cropModeActive && this.activeSide === side) {
      return;
    }
    const canvas = state.fabricCanvas;
    state.designTexture.image = this.renderWithoutOverlayFor(canvas, () => this.toDesignAreaCanvasElementFor(canvas));
    state.designTexture.needsUpdate = true;
  }

  addText(): void {
    // Same early-click race as toggleCategory() - its own sidebar button isn't gated on
    // loading/valid either.
    if (this.loading || !this.valid) {
      return;
    }
    const text = new Textbox('Double-click to edit', {
      left: this.canvasMargin + this.canvasWidthPx / 2,
      top: this.canvasMargin + this.canvasHeightPx / 2,
      // Center origin means (left, top) above is the textbox's own center, so it lands centered in
      // the design area regardless of its size - see loadGridlinesOverlay() for why Fabric's
      // 'center' default can't just be left alone everywhere else in this file.
      originX: 'center',
      originY: 'center',
      width: 160,
      fontSize: 20,
      // Without this, Fabric falls back to its own built-in default font family, which isn't in
      // fontGroups - the Font dropdown would show blank instead of a valid selection.
      fontFamily: this.defaultFontFamily,
    });
    this.fabricCanvas.add(text);
    this.fabricCanvas.setActiveObject(text);
    // Starts fitted to the placeholder text rather than sitting at the arbitrary 160px default.
    this.autoFitTextbox(text);
  }

  // Grows/shrinks a Textbox's width to whatever the current text actually needs, capped at the
  // design area's width - past that cap, text wraps and the box grows in height instead (Fabric's
  // own built-in behavior once width stops changing). Skipped once the user has manually dragged
  // the box's own resize handle (see the 'object:resizing' listener in initCanvas2dFor()) - that's
  // a deliberate width choice, and auto-fit overwriting it on the next keystroke would be
  // surprising.
  private autoFitTextbox(text: Textbox): void {
    if ((text as unknown as { autoFit?: boolean }).autoFit === false) {
      return;
    }
    const maxWidth = this.canvasWidthPx;
    // calcTextWidth() reports the widest *already-wrapped* line, so wrapping first against the cap
    // is what makes its result reflect "how wide would this content like to be" rather than
    // whatever width the box happened to be left at before this edit.
    text.set({ width: maxWidth });
    text.initDimensions();
    const desiredWidth = Math.max(text.calcTextWidth(), text.minWidth);
    text.set({ width: Math.min(desiredWidth, maxWidth) });
    text.initDimensions();
    text.setCoords();
    this.fabricCanvas.requestRenderAll();
  }

  private get activeTextbox(): Textbox | null {
    const object = this.fabricCanvas?.getActiveObject();
    return object instanceof Textbox ? object : null;
  }

  private updateSelectionState(): void {
    const object = this.fabricCanvas.getActiveObject();
    if (object && object === this.canvasBackgroundRect) {
      // Our own synthetic selection for the background's radial/linear gradient drag handles (see
      // updateBackgroundGradientInteractivity()), not a real user selection - leave
      // selectedElementType alone (Background Settings stays the shown panel) and skip re-running
      // updateBackgroundGradientInteractivity() below, which is what put this object here in the
      // first place; doing so again would just recurse through setActiveObject() pointlessly.
      return;
    }
    if (!object) {
      this.selectedElementType = null;
      this.updateBackgroundGradientInteractivity();
      return;
    }
    this.elementOpacity = object.opacity ?? 1;
    if (object instanceof Textbox) {
      this.selectedElementType = 'text';
      this.syncTextControlsFromObject(object);
    } else if (object instanceof FabricImage) {
      const patternProps = object as unknown as PatternImageProps;
      const geometricProps = object as unknown as GeometricPatternImageProps;
      const gradientProps = object as unknown as GradientImageProps;
      if (patternProps.isPattern) {
        this.selectedElementType = 'pattern';
        this.syncPatternControlsFromObject(object, patternProps);
      } else if (geometricProps.isGeometricPattern) {
        this.selectedElementType = 'geometricPattern';
        this.syncGeometricPatternControlsFromObject(geometricProps);
      } else if (gradientProps.isGradient) {
        // A gradient-filled shape or fill-tool stamp - structurally a FabricImage (see
        // setShapeFill()/fillPanelAt()) but conceptually still "a shape" for Shape Settings
        // purposes, the same distinction activeShape's own getter makes.
        this.selectedElementType = 'shape';
        this.syncShapeControlsFromObject(object);
      } else {
        this.selectedElementType = 'image';
      }
    } else {
      this.selectedElementType = 'shape';
      this.syncShapeControlsFromObject(object);
    }
    this.updateBackgroundGradientInteractivity();
  }

  private syncTextControlsFromObject(text: Textbox): void {
    this.textFontFamily = text.fontFamily || this.defaultFontFamily;
    this.textFontSize = text.fontSize || 20;
    this.textColor = (text.fill as string) || '#000000';
    // fillCmyk is a custom property (see setTextColor()), absent on objects created before this
    // feature existed or picked via the Hex tab before any CMYK edit - back-compute it from the hex
    // that's always present so the toolbar's CMYK sliders are never left showing a stale value.
    this.textColorCmyk = (text as unknown as { fillCmyk?: CMYKColor }).fillCmyk ?? hexToCmyk(this.textColor);
    // A design saved before a font's weight got gated (or a legacy font that never had one) can
    // carry fontWeight/fontStyle the toolbar now refuses to offer. Leave the object's own data
    // untouched - just don't show the button as active for a state it can no longer toggle back
    // off, which would otherwise strand it in an active-but-disabled state.
    this.textBold = (text.fontWeight === 'bold' || text.fontWeight === 700) && this.fontSupportsBold(this.textFontFamily);
    this.textItalic = text.fontStyle === 'italic' && this.fontSupportsItalic(this.textFontFamily);
    this.textUnderline = text.underline;
    this.textStrikethrough = text.linethrough;
    this.textOverline = text.overline;
    this.textJustify = (text.textAlign as 'left' | 'center' | 'right') || 'left';
    this.textLineHeight = text.lineHeight ?? 1.16;
    this.textCharSpacing = text.charSpacing ?? 0;
    this.textHighlightEnabled = !!text.backgroundColor;
    this.textBackgroundColor = text.backgroundColor || this.textBackgroundColor;
    // backgroundColorCmyk is a custom property (see setTextBackgroundColor()), same reasoning as
    // fillCmyk above.
    this.textBackgroundColorCmyk =
      (text as unknown as { backgroundColorCmyk?: CMYKColor }).backgroundColorCmyk ??
      hexToCmyk(this.textBackgroundColor);
    this.textStrokeColor = (text.stroke as string) || '#000000';
    // strokeCmyk is a custom property (see setTextStrokeColor()), same reasoning as fillCmyk above.
    this.textStrokeColorCmyk = (text as unknown as { strokeCmyk?: CMYKColor }).strokeCmyk ?? hexToCmyk(this.textStrokeColor);
    this.textStrokeWidth = text.strokeWidth ?? 0;
  }

  private syncPatternControlsFromObject(pattern: FabricImage, props: PatternImageProps): void {
    this.patternBackgroundColor = props.patternBackgroundColor || props.patternOriginalBackgroundColor || '#ffffff';
    this.patternBackgroundColorCmyk = props.patternBackgroundColorCmyk ?? hexToCmyk(this.patternBackgroundColor);
    this.patternAccentColors = props.patternAccentColors ? [...props.patternAccentColors] : [];
    this.patternAccentColorsCmyk = props.patternAccentColorsCmyk
      ? [...props.patternAccentColorsCmyk]
      : this.patternAccentColors.map((hex) => hexToCmyk(hex));
    this.patternFlipX = pattern.flipX;
    this.patternFlipY = pattern.flipY;
  }

  private syncShapeControlsFromObject(shape: FabricObject): void {
    const props = shape as unknown as GradientImageProps & { fillCmyk?: CMYKColor };
    if (props.fillValue) {
      // A gradient-baked shape/fill-tool stamp - its own recipe is the source of truth (a plain
      // `fill` read wouldn't mean anything on a FabricImage anyway).
      this.shapeFill = props.fillValue;
      if (props.fillValue.type === 'radial') {
        this.attachRadialGradientControls(shape, props.fillValue, (value) => this.setShapeFill(value));
      } else if (props.fillValue.type === 'linear') {
        this.attachLinearGradientControl(shape, props.fillValue, (value) => this.setShapeFill(value));
      }
    } else {
      const hex = (shape.fill as string) || '#000000';
      this.shapeFill = { type: 'solid', hex, cmyk: props.fillCmyk ?? hexToCmyk(hex) };
    }
    this.shapeStrokeColor = (shape.stroke as string) || '#000000';
    this.shapeStrokeColorCmyk =
      (shape as unknown as { strokeCmyk?: CMYKColor }).strokeCmyk ?? hexToCmyk(this.shapeStrokeColor);
    this.shapeStrokeWidth = shape.strokeWidth ?? 0;
    this.shapeFlipX = shape.flipX;
    this.shapeFlipY = shape.flipY;
  }

  // Any selected object that's neither a Textbox nor a plain FabricImage (pattern/geometric
  // pattern/plain image) is either a shape primitive (Rect/Circle/Triangle/Polygon/Path - see
  // addShape()) or a shape/fill-tool stamp that's been baked to a FabricImage for its gradient fill
  // (see setShapeFill()) - both share the same fill/stroke/strokeWidth/flipX/flipY surface for
  // Shape Settings purposes, the isGradient check is what tells the two FabricImage cases apart.
  private get activeShape(): FabricObject | null {
    const object = this.fabricCanvas?.getActiveObject();
    if (!object || object instanceof Textbox) {
      return null;
    }
    if (object instanceof FabricImage) {
      return (object as unknown as GradientImageProps).isGradient ? object : null;
    }
    return object;
  }

  // Guards overlapping rebuilds the same way patternRebuildToken does for rebuildPatternImage().
  private shapeFillBakeToken = 0;

  setShapeFill(rawValue: FillValue): void {
    const shape = this.activeShape;
    if (!shape) {
      return;
    }
    // A radial value can reach here with center/radius missing (an object round-tripped through
    // undo/redo history from before this app read those fields - see attachRadialGradientControls()'s
    // own comment). Default rather than let buildGradientFabricImage()/svgGradient() bake against
    // undefined.
    const value: FillValue =
      rawValue.type === 'radial' ? { ...rawValue, center: rawValue.center ?? { x: 0.5, y: 0.5 }, radius: rawValue.radius ?? 0.5 } : rawValue;
    this.shapeFill = value;
    if (value.type === 'solid' && !(shape instanceof FabricImage)) {
      // Already a genuine vector shape - recolor in place, no swap needed.
      shape.set({ fill: value.hex });
      Object.assign(shape as unknown as { fillCmyk?: CMYKColor; fillValue?: FillValue }, { fillCmyk: value.cmyk, fillValue: undefined });
      this.fabricCanvas.requestRenderAll();
      this.pushHistory();
      return;
    }

    const { left, top, originX, originY, scaleX, scaleY, angle, flipX, flipY, stroke, strokeWidth, opacity } = shape;
    const strokeCmyk = (shape as unknown as { strokeCmyk?: CMYKColor }).strokeCmyk;
    const index = this.fabricCanvas.getObjects().indexOf(shape);
    const token = ++this.shapeFillBakeToken;

    if (value.type === 'solid') {
      // Was gradient-filled (a baked FabricImage) - convert back to a genuine shape, since a flat
      // fill should render as real vector fill, not a leftover raster image with an inert `fill`
      // property. Rebuilt as a Rect sized to the image's own current bounding box - the original
      // shape's specific geometry (a Circle's radius, a Polygon's points, ...) isn't recoverable
      // once baked, the same "recipe only, not the original object" tradeoff PATTERN's own
      // recolor already accepts for a catalog SVG.
      const rect = new Rect({
        left,
        top,
        originX,
        originY,
        scaleX,
        scaleY,
        angle,
        flipX,
        flipY,
        width: shape.width || 1,
        height: shape.height || 1,
        fill: value.hex,
        stroke,
        strokeWidth,
        opacity,
      });
      Object.assign(rect as unknown as { fillCmyk?: CMYKColor; strokeCmyk?: CMYKColor }, { fillCmyk: value.cmyk, strokeCmyk });
      this.suppressHistory = true;
      this.fabricCanvas.remove(shape);
      this.fabricCanvas.insertAt(index, rect);
      this.fabricCanvas.setActiveObject(rect);
      this.suppressHistory = false;
      this.fabricCanvas.requestRenderAll();
      this.pushHistory();
      return;
    }

    // linear/radial - bake to an image sized to the shape's own native (unscaled) bounding box,
    // clipped to the shape's own outline so a non-rectangular shape (Circle/Triangle/Polygon/Path)
    // still reads as that shape, not a plain rectangular gradient swatch - the same
    // "generate at exactly the target size" approach geometric patterns use, plus a clip since a
    // shape (unlike a full panel fill) isn't already rectangular. If `shape` is ALREADY a baked
    // gradient image (re-baking after a center/radius drag, or a second color edit), there's no
    // vector source left to clone - reuse its own existing clipPath instead, so a shield/circle/etc.
    // doesn't revert to a plain rectangle on every re-bake (attachRadialGradientControls()'s
    // mouseUpHandler hits exactly this path on every drag release).
    const nativeWidth = shape.width || 1;
    const nativeHeight = shape.height || 1;
    this.suppressHistory = true;
    const clipSourcePromise =
      shape instanceof FabricImage ? (shape.clipPath ? (shape.clipPath.clone() as Promise<FabricObject>) : Promise.resolve(null)) : shape.clone();
    Promise.all([this.buildGradientFabricImage(value, nativeWidth, nativeHeight), clipSourcePromise])
      .then(([fabricImage, clipSource]) => {
        if (token !== this.shapeFillBakeToken) {
          this.suppressHistory = false;
          return;
        }
        fabricImage.set({ left, top, originX, originY, scaleX, scaleY, angle, flipX, flipY, stroke, strokeWidth, opacity });
        if (strokeCmyk) {
          (fabricImage as unknown as { strokeCmyk?: CMYKColor }).strokeCmyk = strokeCmyk;
        }
        if (clipSource) {
          // clipPath coordinates are local to the target object's own (unscaled, unrotated) space,
          // centered regardless of the target's own origin - not the shape's original canvas
          // position/transform, which fabricImage's own left/top/scale/angle above already carry.
          clipSource.set({ left: 0, top: 0, originX: 'center', originY: 'center', angle: 0, scaleX: 1, scaleY: 1 });
          fabricImage.set({ clipPath: clipSource });
        }
        this.fabricCanvas.remove(shape);
        this.fabricCanvas.insertAt(index, fabricImage);
        this.fabricCanvas.setActiveObject(fabricImage);
        this.suppressHistory = false;
        this.fabricCanvas.requestRenderAll();
        this.pushHistory();
      })
      .catch(() => {
        this.suppressHistory = false;
      });
  }

  // Shared by setShapeFill(), fillPanelAt(), and applyCanvasBackground() - builds a loaded,
  // fully-stamped (GradientImageProps set) FabricImage from a linear/radial FillValue, sized to
  // exactly width x height. Never called with a 'solid' value - callers branch on that themselves,
  // since a solid fill never needs baking. renderFrom/renderTo default to the recipe's own colors,
  // but the canvas background passes its own opacity-blended versions instead (see
  // applyCanvasBackground()) - the stamped recipe (gradientFromColor/gradientToColor/fillValue)
  // always stays the true unblended value regardless, so re-opening a fill later shows what was
  // actually picked, not the blended render.
  private buildGradientFabricImage(
    value: Extract<FillValue, { type: 'linear' } | { type: 'radial' }>,
    width: number,
    height: number,
    renderFrom: string = value.from.hex,
    renderTo: string = value.to.hex,
  ): Promise<FabricImage> {
    const angle = value.type === 'linear' ? value.angle : 0;
    const svg = svgGradient(
      value.type === 'radial' ? 'RADIAL' : 'LINEAR',
      renderFrom,
      renderTo,
      angle,
      Math.max(1, Math.round(width)),
      Math.max(1, Math.round(height)),
      value.type === 'radial' ? value.center.x : undefined,
      value.type === 'radial' ? value.center.y : undefined,
      value.type === 'radial' ? value.radius : undefined,
    );
    return FabricImage.fromURL(svgToDataUri(svg), { crossOrigin: 'anonymous' }).then((fabricImage) => {
      Object.assign(fabricImage as unknown as GradientImageProps, {
        isGradient: true,
        gradientType: value.type === 'radial' ? 'RADIAL' : 'LINEAR',
        gradientFromColor: value.from.hex,
        gradientFromColorCmyk: value.from.cmyk,
        gradientToColor: value.to.hex,
        gradientToColorCmyk: value.to.cmyk,
        fillAngle: angle,
        fillValue: value,
      });
      return fabricImage;
    });
  }

  setShapeStrokeColor(color: ColorPickerChange): void {
    const shape = this.activeShape;
    if (!shape) {
      return;
    }
    this.shapeStrokeColor = color.hex;
    this.shapeStrokeColorCmyk = color.cmyk;
    shape.set({ stroke: color.hex });
    (shape as unknown as { strokeCmyk?: CMYKColor }).strokeCmyk = color.cmyk;
    this.fabricCanvas.requestRenderAll();
    this.pushHistory();
  }

  setShapeStrokeWidth(width: number): void {
    const shape = this.activeShape;
    if (!shape || width < 0) {
      return;
    }
    this.shapeStrokeWidth = width;
    // A shape's stroke color isn't set at all until the user touches either control (addShape()
    // leaves it unset, matching Fabric's own default) - raising width alone would otherwise stay
    // invisible, reading as "the stroke control is broken" rather than "also pick a color". Syncing
    // it to whatever's already showing in the color swatch makes the first width increase visible
    // immediately, matching the color the user already sees selected.
    shape.set({ strokeWidth: width, stroke: this.shapeStrokeColor });
    (shape as unknown as { strokeCmyk?: CMYKColor }).strokeCmyk = this.shapeStrokeColorCmyk;
    this.fabricCanvas.requestRenderAll();
    this.pushHistory();
  }

  toggleShapeFlipX(): void {
    const shape = this.activeShape;
    if (!shape) {
      return;
    }
    this.shapeFlipX = !this.shapeFlipX;
    shape.set({ flipX: this.shapeFlipX });
    this.fabricCanvas.requestRenderAll();
    this.pushHistory();
  }

  toggleShapeFlipY(): void {
    const shape = this.activeShape;
    if (!shape) {
      return;
    }
    this.shapeFlipY = !this.shapeFlipY;
    shape.set({ flipY: this.shapeFlipY });
    this.fabricCanvas.requestRenderAll();
    this.pushHistory();
  }

  setTextFontFamily(value: string): void {
    const text = this.activeTextbox;
    if (!text) {
      return;
    }
    this.textFontFamily = value;
    // The new font may not have a Bold/Italic weight converter-service can render (see
    // fontSupportsBold()/fontSupportsItalic()). Drop whichever the new font can't do rather than
    // leaving the toolbar showing an active state its own button would now refuse to toggle off.
    if (this.textBold && !this.fontSupportsBold(value)) {
      this.textBold = false;
      text.set({ fontWeight: 'normal' });
    }
    if (this.textItalic && !this.fontSupportsItalic(value)) {
      this.textItalic = false;
      text.set({ fontStyle: 'normal' });
    }
    text.set({ fontFamily: value });
    // .set() only marks the object's paint cache dirty - it doesn't recompute text layout, so
    // the old font's glyph metrics stick around until initDimensions() rebuilds them (Fabric's own
    // IText editing-exit path does the same pairing). setCoords() follows since the new font can
    // change the box's measured width/height, which moves the selection controls out of sync.
    text.initDimensions();
    text.setCoords();
    // renderAll() (not requestRenderAll()) - requestRenderAll() no-ops if a render is already
    // queued via its own internal handle, and that handle only clears when the queued
    // requestAnimationFrame actually fires. If the tab is backgrounded/occluded at that moment,
    // the frame can stall indefinitely, silently swallowing every requestRenderAll() call after it
    // (this is what made picking a font look like it did nothing until some other action - e.g.
    // toggling bold - happened to trigger a real render). renderAll() paints synchronously and
    // resets that handle itself, so it can't get stuck this way.
    this.fabricCanvas.renderAll();
    this.forceTextRepaint(text);
    // The webfont file itself loads lazily on first use, so the calls above can run before the
    // font is ready and render with a fallback. Redo them once the browser confirms it's loaded.
    document.fonts.load(`1em "${value}"`).then(() => {
      text.initDimensions();
      text.setCoords();
      this.fabricCanvas.renderAll();
      this.forceTextRepaint(text);
    });
    this.pushHistory();
  }

  // Even with renderAll() above, some font changes still don't show up on the first paint - the
  // canvas ends up in a state where nothing distinguishes "correctly rendered" from "needs one
  // more repaint" from Fabric's own bookkeeping. Nudging an unrelated cached property (fontSize) by
  // a tiny amount and immediately back is a blunt but reliable way to force a full cache
  // invalidation and repaint without changing anything the user asked for.
  private forceTextRepaint(text: Textbox): void {
    const originalFontSize = text.fontSize;
    text.set({ fontSize: originalFontSize + 0.1 });
    this.fabricCanvas.renderAll();
    this.fabricCanvas.renderAll();
    text.set({ fontSize: originalFontSize });
    this.fabricCanvas.renderAll();
  }

  setTextFontSize(value: number): void {
    const text = this.activeTextbox;
    if (!text || !value) {
      return;
    }
    this.textFontSize = value;
    text.set({ fontSize: value });
    this.fabricCanvas.requestRenderAll();
    this.pushHistory();
  }

  setTextLineHeight(value: number): void {
    const text = this.activeTextbox;
    if (!text || value == null) {
      return;
    }
    this.textLineHeight = value;
    text.set({ lineHeight: value });
    this.fabricCanvas.requestRenderAll();
    this.pushHistory();
  }

  setTextCharSpacing(value: number): void {
    const text = this.activeTextbox;
    if (!text || value == null) {
      return;
    }
    this.textCharSpacing = value;
    text.set({ charSpacing: value });
    this.fabricCanvas.requestRenderAll();
    this.pushHistory();
  }

  setTextColor(value: ColorPickerChange): void {
    const text = this.activeTextbox;
    if (!text) {
      return;
    }
    this.textColor = value.hex;
    this.textColorCmyk = value.cmyk;
    text.set({ fill: value.hex });
    (text as unknown as { fillCmyk?: CMYKColor }).fillCmyk = value.cmyk;
    this.fabricCanvas.requestRenderAll();
    this.pushHistory();
  }

  setElementOpacity(value: number): void {
    const object = this.fabricCanvas.getActiveObject();
    if (!object) {
      return;
    }
    this.elementOpacity = value;
    object.set({ opacity: value });
    this.fabricCanvas.requestRenderAll();
    this.pushHistory();
  }

  // Whether converter-service has a Bold/Italic file bundled for this exact family - drives the
  // Style toolbar's [disabled] state so a user can't pick a weight that renders fine in the
  // browser preview but silently falls back to a base-14 substitute font in the exported PDF.
  fontSupportsBold(fontFamily: string): boolean {
    return this.fontLookup.get(fontFamily)?.bold ?? false;
  }

  fontSupportsItalic(fontFamily: string): boolean {
    return this.fontLookup.get(fontFamily)?.italic ?? false;
  }

  toggleTextBold(): void {
    const text = this.activeTextbox;
    if (!text || !this.fontSupportsBold(this.textFontFamily)) {
      return;
    }
    this.textBold = !this.textBold;
    text.set({ fontWeight: this.textBold ? 'bold' : 'normal' });
    // renderAll(), not requestRenderAll() - see the comment in setTextFontFamily() for why.
    this.fabricCanvas.renderAll();
    this.pushHistory();
  }

  toggleTextItalic(): void {
    const text = this.activeTextbox;
    if (!text || !this.fontSupportsItalic(this.textFontFamily)) {
      return;
    }
    this.textItalic = !this.textItalic;
    text.set({ fontStyle: this.textItalic ? 'italic' : 'normal' });
    // renderAll(), not requestRenderAll() - see the comment in setTextFontFamily() for why.
    this.fabricCanvas.renderAll();
    this.pushHistory();
  }

  toggleTextUnderline(): void {
    const text = this.activeTextbox;
    if (!text) {
      return;
    }
    this.textUnderline = !this.textUnderline;
    text.set({ underline: this.textUnderline });
    // renderAll(), not requestRenderAll() - see the comment in setTextFontFamily() for why.
    this.fabricCanvas.renderAll();
    this.pushHistory();
  }

  toggleTextStrikethrough(): void {
    const text = this.activeTextbox;
    if (!text) {
      return;
    }
    this.textStrikethrough = !this.textStrikethrough;
    text.set({ linethrough: this.textStrikethrough });
    // renderAll(), not requestRenderAll() - see the comment in setTextFontFamily() for why.
    this.fabricCanvas.renderAll();
    this.pushHistory();
  }

  toggleTextOverline(): void {
    const text = this.activeTextbox;
    if (!text) {
      return;
    }
    this.textOverline = !this.textOverline;
    text.set({ overline: this.textOverline });
    // renderAll(), not requestRenderAll() - see the comment in setTextFontFamily() for why.
    this.fabricCanvas.renderAll();
    this.pushHistory();
  }

  // Highlight is a plain on/off toggle over a remembered color, not a continuous control, so
  // toggling it back on re-applies whatever color was last chosen rather than resetting to a
  // default - matches how setShapeStrokeWidth() remembers the last stroke color across width 0.
  toggleTextHighlight(): void {
    const text = this.activeTextbox;
    if (!text) {
      return;
    }
    this.textHighlightEnabled = !this.textHighlightEnabled;
    text.set({ backgroundColor: this.textHighlightEnabled ? this.textBackgroundColor : '' });
    // renderAll(), not requestRenderAll() - see the comment in setTextFontFamily() for why.
    this.fabricCanvas.renderAll();
    this.pushHistory();
  }

  setTextBackgroundColor(value: ColorPickerChange): void {
    const text = this.activeTextbox;
    if (!text) {
      return;
    }
    this.textBackgroundColor = value.hex;
    this.textBackgroundColorCmyk = value.cmyk;
    this.textHighlightEnabled = true;
    text.set({ backgroundColor: value.hex });
    (text as unknown as { backgroundColorCmyk?: CMYKColor }).backgroundColorCmyk = value.cmyk;
    this.fabricCanvas.requestRenderAll();
    this.pushHistory();
  }

  setTextStrokeColor(value: ColorPickerChange): void {
    const text = this.activeTextbox;
    if (!text) {
      return;
    }
    this.textStrokeColor = value.hex;
    this.textStrokeColorCmyk = value.cmyk;
    text.set({ stroke: value.hex });
    (text as unknown as { strokeCmyk?: CMYKColor }).strokeCmyk = value.cmyk;
    this.fabricCanvas.requestRenderAll();
    this.pushHistory();
  }

  setTextStrokeWidth(width: number): void {
    const text = this.activeTextbox;
    if (!text || width < 0) {
      return;
    }
    this.textStrokeWidth = width;
    // paintFirst: 'stroke' paints the outline behind the fill, so only the outer half of the
    // stroke shows past the fill's edge - the standard "outlined text" look. Fabric's default
    // ('fill') paints the fill on top of a centered stroke instead, which reads as a soft halo.
    // Same "seed the color on first use" reasoning as setShapeStrokeWidth() for the stroke color.
    text.set({ strokeWidth: width, stroke: this.textStrokeColor, paintFirst: width > 0 ? 'stroke' : 'fill' });
    (text as unknown as { strokeCmyk?: CMYKColor }).strokeCmyk = this.textStrokeColorCmyk;
    this.fabricCanvas.requestRenderAll();
    this.pushHistory();
  }

  setTextJustify(align: 'left' | 'center' | 'right'): void {
    const text = this.activeTextbox;
    if (!text) {
      return;
    }
    this.textJustify = align;
    text.set({ textAlign: align });
    // renderAll(), not requestRenderAll() - see the comment in setTextFontFamily() for why.
    this.fabricCanvas.renderAll();
    this.pushHistory();
  }

  // Aligns the active object relative to the physical face/panel it currently sits on (via the
  // same UV-bounds lookup handlePanelFocusClick() uses to resolve a canvas click to a panel), not
  // the canvas/design-area as a whole - a "left" alignment on a panel positioned mid-net should
  // hug that panel's own edge, not the die-line's overall bounding box.
  alignElementOnFace(direction: 'left' | 'right' | 'centerX' | 'top' | 'bottom' | 'centerY'): void {
    const object = this.fabricCanvas.getActiveObject();
    if (!object) {
      return;
    }
    const bounds = this.getFaceBoundsForObject(object);
    if (!bounds) {
      return;
    }
    // getBoundingRect() is the object's axis-aligned on-screen box and accounts for rotation;
    // object.left/top/width/height do not, so they misalign a rotated object -
    // see [[design3d_fabric_v7_center_origin]]. A pure translation shifts left/top by the same
    // delta regardless of origin or angle, so applying the delta below needs no angle lookup.
    const box = object.getBoundingRect();
    let dx = 0;
    let dy = 0;
    switch (direction) {
      case 'left':
        dx = bounds.left - box.left;
        break;
      case 'right':
        dx = bounds.right - (box.left + box.width);
        break;
      case 'centerX':
        dx = bounds.left + (bounds.right - bounds.left - box.width) / 2 - box.left;
        break;
      case 'top':
        dy = bounds.top - box.top;
        break;
      case 'bottom':
        dy = bounds.bottom - (box.top + box.height);
        break;
      case 'centerY':
        dy = bounds.top + (bounds.bottom - bounds.top - box.height) / 2 - box.top;
        break;
    }
    object.set({ left: (object.left ?? 0) + dx, top: (object.top ?? 0) + dy });
    object.setCoords();
    this.fabricCanvas.requestRenderAll();
    this.pushHistory();
  }

  // ---- Image crop ----
  //
  // Crops via FabricImage's own native cropX/cropY/width/height (source-pixel window into the
  // still-fully-decoded image) rather than a clipPath - clipPath is already spoken for by
  // applyDesignAreaClipPath() on every image, and cropX/Y leaves the crop reversible: nothing
  // outside the current crop is discarded, so re-opening Crop widens back out from the current
  // window, never the original full image.
  //
  // The overlay is two temporary Fabric objects sharing the image's own angle (never the user's to
  // rotate independently - see startImageCrop()'s lockRotation): a dimming mask covering the image
  // except for a punched-out hole over the crop window (so only the cropped-away area reads as
  // dimmed), and the crop rectangle itself, whose resize handles the user drags. The mask is a
  // single evenodd-filled Path (outer ring = image bounds, inner ring = crop rect - see
  // dimMaskPathData()), not a Rect + inverted clipPath: that combination (inverted, rotated,
  // absolutePositioned) renders as fully invisible in this Fabric version - confirmed live via
  // ng.getComponent() debug access, a plain (non-inverted) clip on the same objects DOES render,
  // only flipping `inverted` on is what breaks - so the evenodd path sidesteps clipPath entirely.
  // Both overlay objects are excludeFromExport (kept out of undo history/SVG) and suppressHistory
  // is held for the whole session so dragging them doesn't spam undo - applyImageCrop()/
  // cancelImageCrop() are each the one real history-worthy action (or none, for Cancel).
  cropModeActive = false;
  private cropTargetImage?: FabricImage;
  private cropRect?: Rect;
  private cropDimOverlay?: Path;

  private toCropTransform(image: FabricImage): ImageCropTransform {
    return {
      left: image.left ?? 0,
      top: image.top ?? 0,
      angle: image.angle ?? 0,
      scaleX: image.scaleX ?? 1,
      scaleY: image.scaleY ?? 1,
      width: image.width ?? 1,
      height: image.height ?? 1,
      cropX: image.cropX ?? 0,
      cropY: image.cropY ?? 0,
    };
  }

  startImageCrop(): void {
    const image = this.activeImage;
    if (!image || this.cropModeActive) {
      return;
    }
    this.cropTargetImage = image;
    this.suppressHistory = true;

    const geometry = {
      left: image.left,
      top: image.top,
      angle: image.angle,
      originX: 'center' as const,
      originY: 'center' as const,
      width: image.width,
      height: image.height,
      scaleX: image.scaleX,
      scaleY: image.scaleY,
    };

    this.cropDimOverlay = new Path(dimMaskPathData(geometry, geometry), {
      fill: 'rgba(0, 0, 0, 0.55)',
      fillRule: 'evenodd',
      selectable: false,
      evented: false,
      excludeFromExport: true,
    });

    this.cropRect = new Rect({
      ...geometry,
      fill: 'rgba(0, 0, 0, 0)',
      stroke: '#fff',
      strokeWidth: 1,
      strokeDashArray: [6, 4],
      lockRotation: true,
      excludeFromExport: true,
    });
    this.cropRect.setControlVisible('mtr', false);

    image.selectable = false;
    image.evented = false;
    // Set before discardActiveObject()/setActiveObject() below - both fire selection events
    // synchronously, and the selection:cleared/created guards (see initCanvas2dFor()) key off this
    // flag to keep the toolbar showing Crop instead of briefly reading the crop rect as a plain
    // Shape selection.
    this.cropModeActive = true;
    this.fabricCanvas.discardActiveObject();
    this.fabricCanvas.add(this.cropDimOverlay);
    this.fabricCanvas.add(this.cropRect);
    this.syncCropOverlay();

    this.cropRect.on('scaling', () => this.syncCropOverlay());
    this.cropRect.on('moving', () => this.syncCropOverlay());

    this.fabricCanvas.setActiveObject(this.cropRect);
    this.fabricCanvas.requestRenderAll();
  }

  // Re-clamps the crop rect to the image's bounds (see resolveCropRect()) and rebuilds the dim
  // mask's path data from that clamped result - run on every drag frame so the handles snap back
  // the instant they'd otherwise cross the image's own edge, and the dim/clear split always
  // matches what Apply would actually produce. The mask is removed and re-added (Path geometry
  // isn't cheaply mutable in place) at the same stack position, directly below the crop rect.
  private syncCropOverlay(): void {
    if (!this.cropTargetImage || !this.cropRect || !this.cropDimOverlay) {
      return;
    }
    const imageTransform = this.toCropTransform(this.cropTargetImage);
    const resolved = resolveCropRect(imageTransform, {
      left: this.cropRect.left ?? 0,
      top: this.cropRect.top ?? 0,
      width: this.cropRect.width ?? 1,
      height: this.cropRect.height ?? 1,
      scaleX: this.cropRect.scaleX ?? 1,
      scaleY: this.cropRect.scaleY ?? 1,
    });
    const snapped = cropRectTransformFor(imageTransform, resolved);
    this.cropRect.set({
      left: snapped.left,
      top: snapped.top,
      width: snapped.width,
      height: snapped.height,
      scaleX: snapped.scaleX,
      scaleY: snapped.scaleY,
    });
    this.cropRect.setCoords();

    this.fabricCanvas.remove(this.cropDimOverlay);
    // Captured after removing the old mask, so it lands directly below the crop rect rather than
    // one slot too high (removing an earlier stack member shifts every later index down by one).
    const cropRectIndex = this.fabricCanvas.getObjects().indexOf(this.cropRect);
    this.cropDimOverlay = new Path(
      dimMaskPathData(imageTransform, { ...snapped, angle: imageTransform.angle }),
      {
        fill: 'rgba(0, 0, 0, 0.55)',
        fillRule: 'evenodd',
        selectable: false,
        evented: false,
        excludeFromExport: true,
      },
    );
    this.fabricCanvas.insertAt(cropRectIndex, this.cropDimOverlay);
    this.fabricCanvas.requestRenderAll();
  }

  applyImageCrop(): void {
    if (!this.cropModeActive || !this.cropTargetImage || !this.cropRect) {
      return;
    }
    const image = this.cropTargetImage;
    const imageTransform = this.toCropTransform(image);
    const resolved = resolveCropRect(imageTransform, {
      left: this.cropRect.left ?? 0,
      top: this.cropRect.top ?? 0,
      width: this.cropRect.width ?? 1,
      height: this.cropRect.height ?? 1,
      scaleX: this.cropRect.scaleX ?? 1,
      scaleY: this.cropRect.scaleY ?? 1,
    });
    const result = cropResultFor(imageTransform, resolved);
    image.set({
      cropX: result.cropX,
      cropY: result.cropY,
      width: result.width,
      height: result.height,
      left: result.left,
      top: result.top,
    });
    image.setCoords();

    this.cleanupCropMode();
    this.suppressHistory = false;
    this.fabricCanvas.setActiveObject(image);
    this.updateSelectionState();
    this.fabricCanvas.requestRenderAll();
    this.pushHistory();
  }

  cancelImageCrop(): void {
    if (!this.cropModeActive) {
      return;
    }
    const image = this.cropTargetImage;
    this.cleanupCropMode();
    this.suppressHistory = false;
    if (image) {
      this.fabricCanvas.setActiveObject(image);
      this.updateSelectionState();
    }
    this.fabricCanvas.requestRenderAll();
  }

  private cleanupCropMode(): void {
    if (this.cropRect) {
      this.fabricCanvas.remove(this.cropRect);
    }
    if (this.cropDimOverlay) {
      this.fabricCanvas.remove(this.cropDimOverlay);
    }
    if (this.cropTargetImage) {
      this.cropTargetImage.selectable = true;
      this.cropTargetImage.evented = true;
    }
    this.cropRect = undefined;
    this.cropDimOverlay = undefined;
    this.cropTargetImage = undefined;
    this.cropModeActive = false;
  }

  // Any selected, non-pattern FabricImage - the only kind Crop (and eventually other image-only
  // tools) applies to. Mirrors activeShape/activeTextbox's role for their own element types.
  private get activeImage(): FabricImage | null {
    const object = this.fabricCanvas?.getActiveObject();
    if (!(object instanceof FabricImage)) {
      return null;
    }
    return (object as unknown as PatternImageProps).isPattern ? null : object;
  }

  private getFaceBoundsForObject(object: { getCenterPoint(): { x: number; y: number } }): {
    left: number;
    right: number;
    top: number;
    bottom: number;
  } | null {
    if (!this.canvasWidthPx || !this.canvasHeightPx) {
      return null;
    }
    // findPanelAt() takes the same canvas-absolute point getCenterPoint() already returns - reused
    // here instead of duplicating its u/v math, so this stays correct on the inside canvas too
    // (see findPanelAt()'s own comment on the mirror argument) without a second place to fix.
    const panel = findPanelAt(
      object.getCenterPoint(),
      this.canvasWidthPx,
      this.canvasHeightPx,
      this.canvasMargin,
      this.facePanels,
      this.activeSide === 'inside',
    );
    if (!panel) {
      return null;
    }
    return getPanelPixelBounds(panel, this.canvasWidthPx, this.canvasHeightPx, this.canvasMargin, this.activeSide === 'inside');
  }

  deleteSelected(): void {
    const active = this.fabricCanvas.getActiveObjects();
    if (!active.length) {
      return;
    }
    active.forEach((obj) => this.fabricCanvas.remove(obj));
    this.fabricCanvas.discardActiveObject();
    this.fabricCanvas.renderAll();
  }

  async duplicateSelected(): Promise<void> {
    const active = this.fabricCanvas.getActiveObject();
    if (!active) {
      return;
    }
    const clone = await active.clone();
    clone.set({ left: (active.left ?? 0) + 20, top: (active.top ?? 0) + 20 });
    this.fabricCanvas.add(clone);
    this.fabricCanvas.setActiveObject(clone);
  }

  moveBackward(): void {
    const active = this.fabricCanvas.getActiveObject();
    if (!active) {
      return;
    }
    this.fabricCanvas.sendObjectBackwards(active);
    this.fabricCanvas.renderAll();
  }

  moveForward(): void {
    const active = this.fabricCanvas.getActiveObject();
    if (!active) {
      return;
    }
    this.fabricCanvas.bringObjectForward(active);
    this.fabricCanvas.renderAll();
  }

  // Ported from design.component.ts's confirmClearAll()/clearAll() split - same confirmation
  // modal (#clearAllWarning) instead of a native confirm() alert.
  confirmClearAll(): void {
    this.modalRef = this.bsModalService.show(this.clearAllWarningTemplate);
  }

  // Mac names the Alt key "Option" - the browser's altKey flag is the same either way, so this
  // only changes what the shortcut help text below calls it.
  readonly modifierKeyLabel: 'Option' | 'Alt' = isMacPlatform() ? 'Option' : 'Alt';

  // The (?) next to the Preview header (template) - explains the OrbitControls gestures wired up
  // in initScene()/applyViewerTool() (rotate/pan/zoom, the Alt-drag pan override) plus the
  // Rotate/Move toggle, Reset View, and Expand Preview buttons floating over the canvas itself.
  // "Operations" covers the on-screen tool buttons; "Keyboard Shortcuts" covers the mouse/trackpad
  // gestures that have no visible button.
  previewHelpTab: 'operations' | 'shortcuts' = 'operations';

  showPreviewHelp(): void {
    this.previewHelpTab = 'operations';
    this.modalRef = this.bsModalService.show(this.previewHelpTemplate, { class: 'modal-lg' });
  }

  // The rest of the (?) Help links (template) - one per element-toolbar section, each opening its
  // own #xHelp template the same way showPreviewHelp() does. showPatternHelp() is shared by both
  // the "pattern" (library image) and "geometricPattern" (custom) toolbars, since #patternHelp
  // already covers both element types' controls in its own two columns.
  showBackgroundHelp(): void {
    this.modalRef = this.bsModalService.show(this.backgroundHelpTemplate, { class: 'modal-lg' });
  }

  showTextHelp(): void {
    this.modalRef = this.bsModalService.show(this.textHelpTemplate, { class: 'modal-lg' });
  }

  showPatternHelp(): void {
    this.modalRef = this.bsModalService.show(this.patternHelpTemplate, { class: 'modal-lg' });
  }

  showShapeHelp(): void {
    this.modalRef = this.bsModalService.show(this.shapeHelpTemplate, { class: 'modal-lg' });
  }

  showImageHelp(): void {
    this.modalRef = this.bsModalService.show(this.imageHelpTemplate, { class: 'modal-lg' });
  }

  // The (?) Help link in the flyout header (template) - one per sidebar category, describing what
  // each tool in that category's flyout does. "text" has no flyout (addText() adds text directly),
  // so it never reaches this - toggleCategory() never sets activeCategory to it.
  private readonly categoryHelpTemplates: Partial<Record<ToolCategory, () => TemplateRef<any>>> = {
    ai: () => this.aiCategoryHelpTemplate,
    draw: () => this.drawCategoryHelpTemplate,
    patterns: () => this.patternsCategoryHelpTemplate,
    shapes: () => this.shapesCategoryHelpTemplate,
    images: () => this.imagesCategoryHelpTemplate,
    material: () => this.materialCategoryHelpTemplate,
  };

  showCategoryHelp(): void {
    const template = this.activeCategory ? this.categoryHelpTemplates[this.activeCategory]?.() : undefined;
    if (template) {
      this.modalRef = this.bsModalService.show(template, { class: 'modal-lg' });
    }
  }

  // The (?) Help link next to Legend, above the 2D canvas - covers the canvas-level controls
  // (rulers/guides/zoom/select-move/two-sided/download/divider) on its "Design" tab, plus the
  // keyboard shortcuts (formerly the DESIGN toolbar's own Shortcuts button/#shortcutsHelp modal)
  // on its "Keyboard Shortcuts" tab. Separate from the per-element toolbar help above since those
  // describe the sidebar categories' own flyouts instead.
  designAreaHelpTab: 'design' | 'shortcuts' = 'design';

  showDesignAreaHelp(): void {
    this.designAreaHelpTab = 'design';
    this.modalRef = this.bsModalService.show(this.designAreaHelpTemplate, { class: 'modal-lg' });
  }

  clearAll(): void {
    this.clearActiveCanvas();
  }

  // Split out so confirmCancelTwoSidedPrinting() can discard the inside design (via activeSide,
  // see cancelTwoSidedPrinting()) without a second, redundant confirm modal - its
  // own modal already got explicit confirmation for exactly that.
  private clearActiveCanvas(): void {
    this.fabricCanvas.clear();
    this.canvasBackgroundFill = DEFAULT_CANVAS_BACKGROUND_FILL;
    this.canvasBackgroundOpacity = 1;
    // clear() also wipes backgroundImage/overlayImage (the design-area background rect and the
    // die-line guide) along with every design object - unlike the guide, the background rect is
    // meant to reset (not survive) on clear, so this drops the stale reference and lets
    // applyCanvasBackground() recreate it fresh at the default color/opaque default above.
    this.canvasBackgroundRect = undefined;
    this.applyCanvasBackground();
    // The guide itself isn't a design object, so it should survive a "clear the design" action.
    // Re-applies from the already-loaded gridlinesImage rather than re-fetching it.
    this.applyGridlinesVisibility();
    this.fabricCanvas.renderAll();
    // clear() removes objects without firing object:removed per item, so the cleared state needs
    // an explicit history entry - otherwise undo would have nothing to step back through.
    this.pushHistory();
  }

  // Used by cancelTwoSidedPrinting() - the confirmation modal promises removing two-sided
  // printing discards the inside design, so it needs to actually happen rather than leaving
  // stale content in sideState.inside to silently reappear if two-sided printing is turned back
  // on later. Reuses clearActiveCanvas() (all its ambient this.fabricCanvas/etc. getters resolve
  // through activeSide) by pointing activeSide at 'inside' for the duration of the call -
  // toggleShowingSide() right after in cancelTwoSidedPrinting() moves it to its real final value.
  private discardInsideDesign(): void {
    const outgoingSide = this.activeSide;
    this.activeSide = 'inside';
    this.clearActiveCanvas();
    this.activeSide = outgoingSide;
  }

  undo(): void {
    if (!this.canUndo) {
      return;
    }
    this.historyIndex--;
    void this.restoreHistory();
  }

  redo(): void {
    if (!this.canRedo) {
      return;
    }
    this.historyIndex++;
    void this.restoreHistory();
  }

  // Fabric's default serialization only covers a fixed core property set - lockMovementX/Y,
  // lockScalingX/Y and transparentCorners (the pattern's "rotate-only" enforcement) aren't part of
  // it, so without explicitly listing them here a restored pattern would silently come back fully
  // draggable/resizable. isPattern is this app's own marker (see addPattern()), included the same
  // way so restoreHistory() can identify which restored object, if any, is the pattern.
  private readonly extraHistoryProperties = [
    'lockMovementX',
    'lockMovementY',
    'lockScalingX',
    'lockScalingY',
    // Nothing currently ships a persisted object with this set - both the catalog and whole-box
    // custom patterns are rotate-only (applyPatternRotateControl()) - but it's cheap insurance
    // against a future lock, the same reasoning as the other lock* entries above.
    'lockRotation',
    'transparentCorners',
    'isPattern',
    'patternSvgTemplate',
    'patternOriginalBackgroundColor',
    'patternOriginalAccentColors',
    'patternBackgroundColor',
    'patternAccentColors',
    // Custom (geometric) pattern recipe fields - see GeometricPatternImageProps. Field names
    // (other than the Cmyk companions) match project-service's AI-placed GEOMETRIC_PATTERN element
    // exactly - see docs/plans/design3d-geometric-pattern-plan.md's "Backend integration" section.
    'isGeometricPattern',
    'geometricPatternStyle',
    'geometricPatternScale',
    'geometricPatternBackgroundColor',
    'geometricPatternAccentColors',
    // Gradient fill recipe fields - see GradientImageProps. Field names (other than the Cmyk
    // companions and fillValue, this app's own richer recipe) match project-service's AI-placed
    // GRADIENT element exactly - see docs/plans/design3d-gradient-fills-plan.md's "Backend
    // symmetry" section. fillAngle is shared between the two fill kinds above.
    'isGradient',
    'gradientType',
    'gradientFromColor',
    'gradientToColor',
    'fillAngle',
    'fillValue',
    // project-service's AI-placed SHAPE element's own recipe field (one of BANNER/TRIANGLE/STAR/
    // CIRCLE/POLYGON) - see ContainerDecalDesignService and ShapeSvgUtil. No isShape flag or Cmyk
    // companion needed the way the recipes above have: there's no manual UI tool that rebuilds a
    // placed shape's SVG in place yet, and its color lives on the audit record's own colorHex
    // (same as SOLID_COLOR/TEXT), not a Fabric-node field, so shapeType alone is what a refine
    // round needs preserved - without it here, an AI-placed shape's type would silently drop out
    // of the design the first time this app saves it (autosave or explicit), same failure mode
    // this whole list exists to prevent for the other recipe fields above.
    'shapeType',
    // Cosmetic CMYK companions (see [[design3d_cmyk_color_picker]]) - not part of Fabric's default
    // serialized property set either, same reasoning as the hex properties above.
    'fillCmyk',
    'strokeCmyk',
    'patternBackgroundColorCmyk',
    'patternAccentColorsCmyk',
    'geometricPatternBackgroundColorCmyk',
    'geometricPatternAccentColorsCmyk',
    'gradientFromColorCmyk',
    'gradientToColorCmyk',
    // Whether a Textbox has been manually resized and so opted out of auto-fit (see
    // autoFitTextbox()) - also not part of Fabric's default serialized property set.
    'autoFit',
    // project-service's AI decal service stamps every element it places with a guid (see
    // ContainerDecalDesignService) and uses it to refine that exact element on a later round -
    // without it here, silentSaveCanvasState()'s pre-decal sync (this app's own re-serialize of
    // the live canvas) silently strips it back out of design/outside.txt|inside.txt right before
    // the next AI call, so the backend can no longer tell the element still exists and treats it
    // as brand new, discarding whatever was already there.
    'guid',
  ];

  // Ambient wrapper for the ~15 existing call sites that mean "push for whatever's active" -
  // initCanvas2dFor() calls pushHistoryFor() directly instead, since it sets up the inactive side
  // too (activeSide wouldn't resolve to the right one during that call).
  private pushHistory(): void {
    this.pushHistoryFor(this.activeSide);
  }

  private pushHistoryFor(side: Side): void {
    const state = this.sideState[side];
    if (state.suppressHistory) {
      return;
    }
    state.history = state.history.slice(0, state.historyIndex + 1);
    state.history.push(this.serializeSide(side));
    state.historyIndex = state.history.length - 1;
    if (side === this.activeSide) {
      this.updateHistoryFlags();
    }
  }

  // The persisted design file (design/outside.txt / design/inside.txt) uses a different coordinate
  // space than this canvas's own live Fabric objects: ContainerDecalDesignService and the classic
  // builder both normalize left/top/scaleX/scaleY into a fixed 650-unit-wide "proportional" space
  // with no margin (see PROPORTIONAL_REFERENCE_WIDTH in ContainerDecalDesignService), while this
  // canvas works natively in canvasWidthPx pixels with canvasMargin baked into the origin (see
  // canvasMargin's own comment). Without this conversion, anything the AI decal service (or the
  // classic builder) writes lands at the wrong scale and origin when loaded here, and vice versa.
  // width/height are left untouched on both sides - they're intrinsic object dimensions, not
  // position/scale, and that convention already matches on both ends.
  private static readonly PROPORTIONAL_REFERENCE_WIDTH = 650;

  private get proportionalScaler(): number {
    return this.canvasWidthPx / Design3dComponent.PROPORTIONAL_REFERENCE_WIDTH;
  }

  private convertSnapshotToProportional(snapshot: any): void {
    const convert = (object: any) => {
      if (!object) {
        return;
      }
      if (typeof object.left === 'number') {
        object.left = (object.left - this.canvasMargin) / this.proportionalScaler;
      }
      if (typeof object.top === 'number') {
        object.top = (object.top - this.canvasMargin) / this.proportionalScaler;
      }
      if (typeof object.scaleX === 'number') {
        object.scaleX = object.scaleX / this.proportionalScaler;
      }
      if (typeof object.scaleY === 'number') {
        object.scaleY = object.scaleY / this.proportionalScaler;
      }
    };
    (snapshot.objects ?? []).forEach(convert);
    convert(snapshot.backgroundImage);
  }

  private convertSnapshotFromProportional(snapshot: any): void {
    const convert = (object: any) => {
      if (!object) {
        return;
      }
      if (typeof object.left === 'number') {
        object.left = object.left * this.proportionalScaler + this.canvasMargin;
      }
      if (typeof object.top === 'number') {
        object.top = object.top * this.proportionalScaler + this.canvasMargin;
      }
      if (typeof object.scaleX === 'number') {
        object.scaleX = object.scaleX * this.proportionalScaler;
      }
      if (typeof object.scaleY === 'number') {
        object.scaleY = object.scaleY * this.proportionalScaler;
      }
    };
    (snapshot.objects ?? []).forEach(convert);
    convert(snapshot.backgroundImage);
  }

  // Live snapshot of a side's canvas, independent of the undo history cache - used both to build
  // history entries and to read the design for Save/Add to Cart. Reading history[historyIndex]
  // instead would miss whatever hasn't triggered object:added/object:modified yet (e.g. a Textbox
  // still mid-edit, whose text content Fabric already mutated but hasn't fired an event for), so
  // callers that need "what's on the canvas right now" must call this, not the history array.
  private serializeSide(side: Side): string {
    const state = this.sideState[side];
    // canvasBackgroundFill/canvasBackgroundOpacity ride along as extra top-level keys (Fabric
    // ignores unknown keys on load) - Fabric's own snapshot only has the blended rgb() (solid) or
    // baked image (linear/radial) result, which can't be un-blended/un-baked back into the
    // original recipe for the controls to show.
    const snapshot = {
      ...state.fabricCanvas!.toObject(this.extraHistoryProperties),
      canvasBackgroundFill: state.canvasBackgroundFill,
      canvasBackgroundOpacity: state.canvasBackgroundOpacity,
    };
    this.convertSnapshotToProportional(snapshot);
    return JSON.stringify(snapshot);
  }

  private async restoreHistory(): Promise<void> {
    await this.applyDesignJsonFor(this.activeSide, this.history[this.historyIndex]);
    this.updateHistoryFlags();
  }

  // Backward compatibility: a design saved before canvasBackgroundFill existed only has the old
  // flat canvasBackgroundColor/canvasBackgroundColorCmyk keys (always solid, since gradients never
  // shipped before this) - synthesize the equivalent FillValue from those instead of losing the
  // saved color. A brand new snapshot always has canvasBackgroundFill itself.
  private resolveBackgroundFillFromSnapshot(snapshot: any): FillValue {
    if (snapshot.canvasBackgroundFill) {
      return snapshot.canvasBackgroundFill;
    }
    if (snapshot.canvasBackgroundColor) {
      return {
        type: 'solid',
        hex: snapshot.canvasBackgroundColor,
        cmyk: snapshot.canvasBackgroundColorCmyk ?? hexToCmyk(snapshot.canvasBackgroundColor),
      };
    }
    return DEFAULT_CANVAS_BACKGROUND_FILL;
  }

  // Applies a serialized canvas JSON - from undo/redo history, or from a previously-saved Design
  // record - onto a specific side's canvas. Everything Fabric's own loadFromJSON() can't carry is
  // restored alongside it: the un-blended background color/opacity pair (Fabric's snapshot only
  // has the blended rgb() result), the pattern's control overrides (deserializing always creates
  // fresh object instances), and the die-line guide (loadFromJSON() reconstructs overlayImage from
  // the snapshot too, wiping the guide out regardless of the "Hide Guides" toggle).
  private async applyDesignJsonFor(side: Side, jsonText: string): Promise<void> {
    const state = this.sideState[side];
    const canvas = state.fabricCanvas;
    if (!canvas) {
      return;
    }
    const snapshot = JSON.parse(jsonText);
    this.convertSnapshotFromProportional(snapshot);
    state.suppressHistory = true;
    await canvas.loadFromJSON(snapshot);
    state.canvasBackgroundFill = this.resolveBackgroundFillFromSnapshot(snapshot);
    state.canvasBackgroundOpacity = snapshot.canvasBackgroundOpacity ?? 1;
    state.canvasBackgroundRect = canvas.backgroundImage as Rect | FabricImage | undefined;
    const restoredPattern = canvas
      .getObjects()
      .find((object) => (object as unknown as { isPattern?: boolean }).isPattern) as FabricImage | undefined;
    state.activePattern = restoredPattern;
    if (restoredPattern) {
      this.applyPatternRotateControl(restoredPattern);
      this.applyDesignAreaClipPath(restoredPattern);
    }
    // Same whole-box-vs-single-panel ambiguity a fresh isGeometricPattern lookup can't resolve on
    // its own (unlike catalog patterns, a custom one can legally be FACE-scoped too - see
    // GeometricPatternImageProps) - identified by its locked movement instead, the one thing
    // addGeometricPatternWholeBox() always sets and fillGeometricPatternPanelAt()'s freely
    // draggable/resizable/rotatable instances never do. Not exact-size matching (an older
    // approach this replaced): a whole-box pattern is diagonal-oversized and centered, not
    // canvas-sized, since it's now rotatable too - see addGeometricPatternWholeBox()'s own comment.
    state.activeGeometricPattern = canvas.getObjects().find((object) => {
      const props = object as unknown as GeometricPatternImageProps;
      return props.isGeometricPattern && object.lockMovementX === true && object.lockMovementY === true;
    }) as FabricImage | undefined;
    if (state.activeGeometricPattern) {
      this.applyPatternRotateControl(state.activeGeometricPattern);
      this.applyDesignAreaClipPath(state.activeGeometricPattern);
    }
    // The AI decal service places a whole-box PATTERN element into backgroundImage, not objects[]
    // (see ContainerDecalDesignService.upsertIntoDesign - every WHOLE_BOX element lands there,
    // same slot setCanvasBackgroundFill() uses for a plain color Rect), so it's never found by the
    // getObjects() scan above and never had a clipPath baked in to begin with. Without this it
    // renders at its full diagonal-covering size straight into the canvasMargin bleed border - see
    // applyDesignAreaClipPath()'s own comment for why that oversizing exists and needs clipping.
    // Not wired into activePattern/lockMovement*/rotate-control here: those all assume a selectable
    // canvas object, and backgroundImage isn't one - interactive recolor/rotate of an AI-placed
    // pattern is a separate gap this doesn't attempt to close.
    const backgroundPattern = canvas.backgroundImage as unknown as { isPattern?: boolean } | undefined;
    if (backgroundPattern?.isPattern) {
      this.applyDesignAreaClipPath(canvas.backgroundImage as FabricImage);
    }
    this.applyGridlinesVisibilityFor(side, canvas);
    canvas.renderAll();
    state.suppressHistory = false;
  }

  // Restores a previously-saved design when this container already has one - design-3d otherwise
  // always starts blank regardless of what's on the backend, so reopening (or reloading) a
  // container that was Saved/Added to Cart earlier would silently show nothing, and then a
  // following Save would overwrite the real content with that empty canvas. getDesign() always
  // resolves (the backend auto-creates a stub Design record - name "My Box", empty objects - the
  // moment box-options hands off to this page, see requestSave()'s own comment), so "real content"
  // is judged by whether the loaded JSON actually has objects, not by the response existing at all.
  private async loadExistingDesign(): Promise<void> {
    await this.userReady;
    const userId = getEffectiveUserId(this.productDetails, this.user);
    let existing: any;
    try {
      existing = await this.designService.getDesign(userId, this.containerID);
    } catch {
      this.showNotFoundModal();
      return;
    }
    if (!existing) {
      return;
    }

    const loads: Promise<void>[] = [];
    let hasRealContent = false;
    if (existing.outside) {
      if (JSON.parse(existing.outside)?.objects?.length > 0) {
        hasRealContent = true;
      }
      loads.push(this.loadExistingDesignFor('outside', existing.outside));
    }
    if ((this.twoSided || this.insideOnly) && existing.inside) {
      if (JSON.parse(existing.inside)?.objects?.length > 0) {
        hasRealContent = true;
      }
      loads.push(this.loadExistingDesignFor('inside', existing.inside));
    }
    await Promise.all(loads);

    // Only a design with real content should skip the name prompt on the next Save - the
    // auto-created stub's placeholder name ("My Box") isn't a name the user actually chose.
    if (hasRealContent) {
      this.hasNamedDesign = true;
      this.designName = existing.name || 'Auto Saved';
    }
  }

  private async loadExistingDesignFor(side: Side, jsonText: string): Promise<void> {
    await this.applyDesignJsonFor(side, jsonText);
    const state = this.sideState[side];
    state.history = [this.serializeSide(side)];
    state.historyIndex = 0;
    if (side === this.activeSide) {
      this.updateHistoryFlags();
    }
  }

  private updateHistoryFlags(): void {
    this.canUndo = this.historyIndex > 0;
    this.canRedo = this.historyIndex < this.history.length - 1;
  }

  // ---- AI Design Chat ----
  //
  // Syncs both canvases to design/outside.txt + design/inside.txt on the container before calling
  // the decal endpoint - those files are otherwise only kept in sync at Add to Cart time (see
  // requestAddToCart()), so asking the AI to add a decal could silently discard whatever the user
  // has drawn since then. No Unity SendMessage round-trip needed here (compare
  // design.component.ts's silentSaveCanvasState()) - serializeSide() already reads the live canvas
  // synchronously.
  private async silentSaveCanvasState(): Promise<void> {
    const uploads: Promise<any>[] = [
      this.imagesService.saveImageToContainer(this.containerID, 'outside', this.serializeSide('outside')),
    ];
    if (this.twoSided || this.insideOnly) {
      uploads.push(this.imagesService.saveImageToContainer(this.containerID, 'inside', this.serializeSide('inside')));
    }
    await Promise.all(uploads);
  }

  private async getContainerDesignFile(filename: string): Promise<any | undefined> {
    try {
      // bustCache: true - this is re-fetched right after the AI decal endpoint rewrites the file
      // server-side, so a browser-cached response here would silently return the pre-decal content.
      const response: any = await this.imagesService.getFileContent(this.containerID, filename, true);
      const content = response?.file?.content;
      const parsed = content ? JSON.parse(content) : undefined;
      // A one-sided box has no inside design, so the file is saved as the literal string "null" -
      // normalize that back to undefined so the reload below simply skips that side.
      return parsed === null ? undefined : parsed;
    } catch {
      // No file yet for this side - treat as empty rather than an error.
      return undefined;
    }
  }

  async onAiDecalPromptSubmitted(prompt: string): Promise<void> {
    this.aiDecalMessages = [...this.aiDecalMessages, { role: 'user', text: prompt }];
    this.aiDecalGenerating = true;

    // An unambiguous "remove a1" / "delete a1" naming a known reference code hits the
    // deterministic DELETE endpoint directly instead of round-tripping through the AI prompt flow.
    // Anything else (including a vaguer "remove the logo from the top") falls through to the
    // normal AI flow below, whose Claude integration has its own REMOVE mechanism for that case.
    const deleteReference = this.matchDeleteCommand(prompt);
    // Consumed here regardless of outcome below - a delete command abandons the pending question
    // rather than answering it, and a fresh question from the merged prompt (handled inside
    // generateAiDecalFromPrompt) should overwrite it, not stack with it.
    const pending = this.pendingClarification;
    this.pendingClarification = undefined;

    try {
      await this.silentSaveCanvasState();
      if (deleteReference) {
        await this.deleteAiDecalElementByReference(deleteReference);
      } else {
        // The backend has no memory of the question it just asked - the follow-up has to restate
        // the original request alongside the user's answer, whether that answer came from a typed
        // reply or an option chip.
        const effectivePrompt = pending ? `${pending.originalPrompt}\n\nTo clarify: ${prompt}` : prompt;
        await this.generateAiDecalFromPrompt(effectivePrompt);
      }
    } catch (error) {
      // Only an AiDecalError carries a message written for the chat user (backend validation
      // text, or "The request was rejected.") - anything else is an unexpected bug, and its raw
      // .message (e.g. a TypeError) is an implementation detail, not something to show as-is.
      const message = error instanceof AiDecalError
        ? error.message
        : `Something went wrong ${deleteReference ? 'removing that element' : 'generating that design'}. Try again.`;
      this.aiDecalMessages = [...this.aiDecalMessages, { role: 'error', text: message }];
    } finally {
      this.aiDecalGenerating = false;
    }
  }

  private async generateAiDecalFromPrompt(prompt: string): Promise<void> {
    const priorGuids = this.extractReferencedGuids(prompt);

    const response: AiDecalResponse = await this.designService.generateAiDecal(
      this.containerID,
      prompt,
      priorGuids,
      this.user?.id
    );

    if (response.clarifyingQuestion) {
      // Nothing was placed or persisted (elements/failures are both guaranteed empty) - so there's
      // nothing to reload or merge into decalElementsByReference. `prompt` here is already whatever
      // was actually sent (original request, or an earlier merged round) - carrying it forward as
      // the new "original" lets a second clarifying question overwrite this one cleanly instead of
      // stacking.
      this.pendingClarification = { originalPrompt: prompt };
      this.aiDecalMessages = [
        ...this.aiDecalMessages,
        {
          role: 'question',
          text: response.clarifyingQuestion.question,
          options: response.clarifyingQuestion.options,
        },
      ];
      return;
    }

    await this.reloadDesignAfterAiDecal();

    // Merged in, not replaced - codes from earlier rounds stay referenceable for the rest of the
    // session. Left untouched on a failed round (caught by the caller) so decals that still exist
    // from earlier successful calls stay referenceable.
    response.elements.forEach((e) => {
      this.decalElementsByReference[e.reference] = {
        objectGuid: e.objectGuid,
        side: response.side,
        face: e.face,
        scope: e.scope,
      };
    });

    const elementMessages: AiDecalChatMessage[] = response.elements.flatMap((e) => {
      const messages: AiDecalChatMessage[] = [{ role: 'assistant', text: e.summary, reference: e.reference }];
      // A note is a non-error caveat about this placement (e.g. a recolor request the backend
      // couldn't apply) - the element still placed normally, so it's shown right after the
      // confirmation above rather than batched with failureMessages below.
      if (e.note) {
        messages.push({ role: 'note', text: e.note });
      }
      return messages;
    });
    const failureMessages: AiDecalChatMessage[] = response.failures.length
      ? [
          {
            role: 'error',
            text:
              `Couldn't generate ${response.failures.length} of the requested element${response.failures.length > 1 ? 's' : ''}: `
              + response.failures.map((f) => f.message).join('; '),
          },
        ]
      : [];
    this.aiDecalMessages = [...this.aiDecalMessages, ...elementMessages, ...failureMessages];
  }

  private async deleteAiDecalElementByReference(reference: string): Promise<void> {
    const objectGuid = this.decalElementsByReference[reference].objectGuid;
    const result = await this.designService.deleteAiDecalElement(this.containerID, objectGuid);
    await this.reloadDesignAfterAiDecal();
    delete this.decalElementsByReference[reference];
    this.aiDecalMessages = [...this.aiDecalMessages, { role: 'assistant', text: result.summary }];
  }

  // Refetches both design files and reloads whichever ones changed, via the same path undo/redo
  // and initial-load use (applyDesignJsonFor) - unlike Unity, there's no WASM SendMessage bridge
  // here to worry about overflowing with a multi-megabyte payload, so no Design-record detour is
  // needed. Shared by the AI-generate and direct-delete flows, since both rewrite the container's
  // design files server-side.
  private async reloadDesignAfterAiDecal(): Promise<void> {
    const [outside, inside] = await Promise.all([
      this.getContainerDesignFile('design/outside.txt'),
      this.getContainerDesignFile('design/inside.txt'),
    ]);

    const sidesReloaded: Side[] = [];
    const reloads: Promise<void>[] = [];
    if (outside) {
      sidesReloaded.push('outside');
      reloads.push(this.applyDesignJsonFor('outside', JSON.stringify(outside)));
    }
    if (inside) {
      sidesReloaded.push('inside');
      reloads.push(this.applyDesignJsonFor('inside', JSON.stringify(inside)));
    }
    await Promise.all(reloads);

    // applyDesignJsonFor() suppresses history while loading (same as undo/redo/initial load), so
    // push the post-edit state as one new entry per side that changed - preserving whatever the
    // user could already undo, rather than resetting history to a single fresh baseline the way
    // loadExistingDesignFor() does for a page load.
    sidesReloaded.forEach((side) => this.pushHistoryFor(side));
  }

  // Scans the prompt for any known short reference code as a whole word (case-insensitive) - only
  // elements actually mentioned by code are sent back as refinement context, so an unrelated
  // follow-up prompt is treated as entirely new rather than defaulting to "refine whatever was
  // placed last".
  private extractReferencedGuids(prompt: string): string[] {
    return Object.entries(this.decalElementsByReference)
      .filter(([reference]) => new RegExp(`\\b${reference}\\b`, 'i').test(prompt))
      .map(([, element]) => element.objectGuid);
  }

  // Matches an unambiguous single-target "remove a1" / "delete a1" command naming a known
  // reference code. Returns undefined for anything else - including a code that isn't currently
  // tracked - so the caller falls through to the normal AI flow rather than misfiring a delete.
  private matchDeleteCommand(prompt: string): string | undefined {
    const match = prompt.trim().match(/^(?:remove|delete)\s+(\S+)$/i);
    if (!match) {
      return undefined;
    }
    return Object.keys(this.decalElementsByReference).find((ref) => ref.toLowerCase() === match[1].toLowerCase());
  }

  // Forgets every reference code placed so far, so even an explicit mention of an old code no
  // longer resolves to anything - clears the targeting without touching the visible chat history.
  startNewDecal(): void {
    this.decalElementsByReference = {};
    this.pendingClarification = undefined;
    this.aiDecalMessages = [...this.aiDecalMessages, { role: 'assistant', text: 'Starting a new decal.' }];
  }

  // Wipes the visible transcript and the decal targeting together - unlike startNewDecal(), this is
  // a full reset, so a follow-up message afterward must not silently refine whatever decal the
  // cleared history was talking about.
  clearAiDecalChat(): void {
    this.aiDecalMessages = [];
    this.decalElementsByReference = {};
    this.pendingClarification = undefined;
  }

  // ---- Bottom toolbar: Outside/Inside + Download ----

  toggleShowingSide(side: Side): void {
    if (side === this.activeSide) {
      return;
    }
    // A crop session's temporary objects belong to whichever side started it - leaving them behind
    // on a switch would strand suppressHistory on and orphan the overlay objects, so cancel first.
    if (this.cropModeActive) {
      this.cancelImageCrop();
    }
    // Deselect on the outgoing canvas before switching - it stays selected internally otherwise
    // (Fabric doesn't know its element just went display:none), so selectedElementType would keep
    // reflecting an object the user can no longer see or act on.
    const outgoing = this.sideState[this.activeSide].fabricCanvas;
    outgoing?.discardActiveObject();
    // Switching sides is not a mouse:out - the pointer may still sit over the same screen position,
    // now showing the other canvas, so the outgoing side's hover outline/tooltip would otherwise be
    // left stuck on screen.
    this.hidePanelHover();
    outgoing?.requestRenderAll();
    this.activeSide = side;
    this.selectedElementType = null;
    this.updateBackgroundGradientInteractivity();
    this.updateHistoryFlags();
  }

  // Exports whichever side is currently active - toSVG() already respects excludeFromExport (the
  // die-line guide, see loadGridlinesOverlayFor()), so the guide never ends up in the download.
  downloadSVG(): void {
    const svg = this.fabricCanvas.toSVG(this.designAreaSVGOptions());
    fileDownload(svg, `design-${this.activeSide}.svg`);
  }

  // Mirrors the old Unity builder's getPDF() (design.component.ts): convert the exported SVG to a
  // PDF server-side, then trigger the browser download via a hidden iframe pointed at the
  // resulting file - convertBasicDesign() is a generic SVG-in/PDF-out endpoint, so the Fabric SVG
  // needs no special shaping to satisfy it.
  downloadPDF(): void {
    const svg = this.fabricCanvas.toSVG(this.designAreaSVGOptions());
    const filename = `design-${this.activeSide}.pdf`;
    this.containerService
      .convertBasicDesign(this.containerID, svg, filename)
      .then((response: any) => {
        this.downloadUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          `${this.environment.apiHost}${this.apiPath}${response.container}/${response.filename}/?download=true&downloadAs=${filename}`,
        );
      })
      .catch(() => {
        this.bsModalService
          .show(ConfirmationModalComponent)
          .content?.showModal(ERROR_BODY, ERROR_TITLE, { showCloseButton: false });
      });
  }

  // ---- Add to Cart ----
  //
  // Mirrors the old Unity builder's addDesignToCart() (design.component.ts): upload the exported
  // design, save a Design record so Back-navigation from review-box can restore it, then hand off
  // to review-box via the host app's DESIGN_3D_REVIEW_HANDOFF provider the same way the old page
  // does. No Unity SendMessage round-trip here - the Fabric canvases and their already-serialized
  // undo history (see pushHistoryFor()) are read directly instead.

  addingToCart = false;

  // Creates or updates the Design record for this container - same get/update-or-create pattern
  // as design.component.ts's saveDesignRecord().
  private saveDesignRecord(userId: number, design: any): Promise<void> {
    return this.designService
      .getDesign(userId, this.containerID)
      .then((response: any) => this.designService.updateDesign(userId, { ...response, ...design }))
      .catch((error: { status: number }) => {
        if (error.status === 404) {
          return this.designService.createDesign(userId, design);
        }
        return undefined;
      });
  }

  // ---- Save ----
  //
  // Mirrors the old Unity builder's requestSave()/saveProject() (design.component.ts): upload the
  // exported SVGs and update the Design record, but stays on the page instead of navigating to
  // /continue-project - this is a mid-design save, not a cart handoff.
  //
  // The backend auto-creates a Design record for a container as soon as box-options hands off to
  // this page (getDesign() already returns a real row - name "My Box", createdBy "anonymous" -
  // before any explicit save), so record existence alone can't signal "first save". hasNamedDesign
  // instead flips true either when loadExistingDesign() finds real (non-stub) content already
  // saved, or the first time the user confirms a name here - so a container reopened mid-project
  // doesn't re-prompt, but a genuinely fresh one does.
  saving = false;
  designName = '';
  private hasNamedDesign = false;

  async requestSave(): Promise<void> {
    if (this.saving) {
      return;
    }

    if (!this.hasNamedDesign) {
      this.designName = this.productDetails.name || 'My Box';
      this.modalRef = this.bsModalService.show(this.nameDesignTemplate);
      return;
    }

    await this.persistDesign(this.designName || 'Auto Saved');
  }

  confirmDesignName(): void {
    this.closeModal();
    this.hasNamedDesign = true;
    void this.persistDesign(this.designName || 'Auto Saved');
  }

  // continue-project's preview-modal.component.ts and review-box.component.ts's Outside/Inside Art
  // thumbnails both read Texture_0.png/Texture_1.png straight off the container (unconditionally -
  // neither has a fallback for a missing file), the same PNG-texture snapshots the 3D preview
  // itself is built from (see applyDesignTexture()/the initial texture setup in loadBox()). Both
  // persistDesign() (the plain Save button) and requestAddToCart() must keep these current, or a
  // design saved without ever going through Add to Cart is stuck showing the container's blank
  // default texture in Preview despite having real artwork on the canvas.
  private pushTextureUploads(uploads: Promise<any>[], outsideState: SideState, insideState: SideState): void {
    if (outsideState.fabricCanvas) {
      const outsideTexturePng = this.renderWithoutOverlayFor(outsideState.fabricCanvas, () =>
        this.toDesignAreaCanvasElementFor(outsideState.fabricCanvas!),
      ).toDataURL('image/png');
      uploads.push(this.containerService.updateFile(this.containerID, 'Texture_0.png', outsideTexturePng));
    }
    if (insideState.fabricCanvas) {
      const insideTexturePng = this.renderWithoutOverlayFor(insideState.fabricCanvas, () =>
        this.toDesignAreaCanvasElementFor(insideState.fabricCanvas!),
      ).toDataURL('image/png');
      uploads.push(this.containerService.updateFile(this.containerID, 'Texture_1.png', insideTexturePng));
    }
  }

  private async persistDesign(name: string): Promise<void> {
    this.saving = true;

    // Reset first, capture second - both synchronous, before anything below gets a chance to
    // await. Matches the old Unity builder's own save flow (design.component.ts's
    // saveCanvasImages()), which likewise snapshots the 3D preview and uploads it as
    // "screenshot.png" via containerService.updateFile().
    this.resetViewToInitialAngle();
    const screenshotPng = this.captureViewportPng();

    // this.user is otherwise only populated when the Images sidebar tab has been opened (see
    // toggleCategory('images')) - without this, a logged-in user who saves without ever visiting
    // Images would get getEffectiveUserId() falling back to 0, silently saving under the wrong
    // account.
    if (!this.user) {
      await this.loadUser();
    }

    const outsideState = this.sideState.outside;
    const insideState = this.sideState.inside;
    const outsideJson = this.serializeSide('outside');
    const insideJson = this.twoSided || this.insideOnly ? this.serializeSide('inside') : '';
    const outsideSVG = outsideState.fabricCanvas?.toSVG() || '';
    const insideSVG = this.twoSided || this.insideOnly ? insideState.fabricCanvas?.toSVG() || '' : '';

    try {
      const uploads: Promise<any>[] = [];
      if (outsideSVG) {
        uploads.push(this.containerService.uploadBasicDesign(this.containerID, outsideSVG, 'online_outside.svg'));
      }
      if (insideSVG) {
        uploads.push(this.containerService.uploadBasicDesign(this.containerID, insideSVG, 'online_inside.svg'));
      }
      uploads.push(this.containerService.updateFile(this.containerID, 'screenshot.png', screenshotPng));
      this.pushTextureUploads(uploads, outsideState, insideState);

      const userId = getEffectiveUserId(this.productDetails, this.user);
      const design = buildDesignRecord({
        user: this.user,
        containerID: this.containerID,
        outsideJson,
        insideJson,
        twoSided: this.twoSided,
        insideOnly: this.insideOnly,
        productDetails: this.productDetails,
        outerMaterial: this.outerMaterial,
        innerMaterial: this.innerMaterial,
        subcategory: this.subcategory,
        luxe: this.luxe,
        name,
      });

      await Promise.all([...uploads, this.saveDesignRecord(userId, design)]);
      this.snackBar.open('Design saved.', undefined, {
        duration: 4000,
        verticalPosition: 'top',
        horizontalPosition: 'right',
        panelClass: ['design-saved-snackbar'],
      });
    } catch {
      this.bsModalService.show(ConfirmationModalComponent).content?.showModal(ERROR_BODY, ERROR_TITLE);
    } finally {
      this.saving = false;
    }
  }

  async requestAddToCart(): Promise<void> {
    if (this.addingToCart) {
      return;
    }
    this.addingToCart = true;

    // Reset first, capture second - both synchronous, before anything below gets a chance to
    // await. Matches persistDesign()'s own screenshot capture (and, before that, the old Unity
    // builder's saveCanvasImages()), so the preview shown on the review/cart pages always frames
    // the box at its initial angle rather than wherever the user last left the camera.
    this.resetViewToInitialAngle();
    const screenshotPng = this.captureViewportPng();

    const outsideState = this.sideState.outside;
    const insideState = this.sideState.inside;
    const outsideJson = this.serializeSide('outside');
    const insideJson = this.twoSided || this.insideOnly ? this.serializeSide('inside') : '';
    const outsideSVG = outsideState.fabricCanvas?.toSVG() || '';
    const insideSVG = this.twoSided || this.insideOnly ? insideState.fabricCanvas?.toSVG() || '' : '';

    try {
      const uploads: Promise<any>[] = [this.containerService.updateFile(this.containerID, 'screenshot.png', screenshotPng)];
      if (outsideSVG) {
        uploads.push(this.containerService.uploadBasicDesign(this.containerID, outsideSVG, 'online_outside.svg'));
      }
      if (insideSVG) {
        uploads.push(this.containerService.uploadBasicDesign(this.containerID, insideSVG, 'online_inside.svg'));
      }
      if (outsideJson) {
        uploads.push(this.imagesService.saveImageToContainer(this.containerID, 'outside', outsideJson));
      }
      if (insideJson) {
        uploads.push(this.imagesService.saveImageToContainer(this.containerID, 'inside', insideJson));
      }
      this.pushTextureUploads(uploads, outsideState, insideState);

      const userId = getEffectiveUserId(this.productDetails, this.user);
      const design = buildDesignRecord({
        user: this.user,
        containerID: this.containerID,
        outsideJson,
        insideJson,
        twoSided: this.twoSided,
        insideOnly: this.insideOnly,
        productDetails: this.productDetails,
        outerMaterial: this.outerMaterial,
        innerMaterial: this.innerMaterial,
        subcategory: this.subcategory,
        luxe: this.luxe,
      });
      await Promise.all([...uploads, this.saveDesignRecord(userId, design)]);
    } catch {
      this.addingToCart = false;
      this.bsModalService.show(ConfirmationModalComponent).content?.showModal(ERROR_BODY, ERROR_TITLE);
      return;
    }

    const updatedProductDetails = {
      Category: this.productDetails.Category,
      Subcategory: this.productDetails.Subcategory,
      Code: this.productDetails.Code,
      BoardStrength: this.productDetails.BoardStrength,
      ContainerName: this.containerID,
      paperType: getPaperTypeID(this.outerMaterial, this.subcategory),
      secondaryPaperType: getPaperTypeID(this.innerMaterial, this.subcategory),
      NewDesignID: 0,
      BoxDimensions: this.productDetails.BoxDimensions,
      Preset: this.productDetails.Preset,
      gloss: this.productDetails.gloss,
      options: this.productDetails.options,
      luxe: this.luxe,
      twoSided: this.twoSided,
      insideOnly: this.insideOnly,
      print: this.printing,
      material: this.productDetails.material,
      finish: this.finish,
      categoryName: this.categoryName,
      quantity: this.productDetails.quantity,
      outerMaterial: this.subcategory !== 'tape' ? this.outerMaterial : '',
      innerMaterial: this.subcategory !== 'tape' ? this.innerMaterial : '',
      shape: this.productDetails.shape,
      rush: this.productDetails.rush,
      editingLineItemId: this.productDetails.editingLineItemId,
    };
    if (this.productDetails.isRemoteAdmin) {
      (updatedProductDetails as any)['isRemoteAdmin'] = true;
    }

    this.reviewHandoff.setReviewPageDetails(updatedProductDetails);
    this.addingToCart = false;
    this.router.navigate(['/review-box', stripUndefinedParams(updatedProductDetails)]);
  }

  // ---- 3D viewer (phase 1) ----

  private initScene(): void {
    const container = this.containerRef.nativeElement;
    const width = container.clientWidth;
    const height = container.clientHeight || 500;

    this.camera = new PerspectiveCamera(5, width / height, 1, 1000);
    this.camera.position.set(4.5, 2, 0);

    // Intensities tuned for three.js's physically-correct light units (default since r155):
    // point-light irradiance falls off with distance^2, so matching the old pre-r155 apparent
    // brightness at this camera distance (~4.5 units) needs a much higher raw intensity than the
    // non-physical 0.2/0.8 values copied from upload-preview.component.ts.
    const ambientLight = new AmbientLight(0xfefefe, 1);
    const pointLight = new PointLight(0xfefefe, 15);
    this.scene.add(ambientLight);
    this.scene.add(this.camera);
    this.camera.add(pointLight);

    this.renderer = new WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(width, height);
    this.renderer.shadowMap.enabled = true;
    this.renderer.setClearColor(0xefefef);
    container.appendChild(this.renderer.domElement);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableZoom = true;
    this.controls.minZoom = 0.5;
    this.controls.maxZoom = 2;
    this.controls.screenSpacePanning = true;
    this.controls.minDistance = 1;
    this.controls.maxDistance = 5;
    // Locked to a single fixed pivot: panning (which would otherwise drag the orbit target off the
    // box) is disabled, and focusPanel() below rotates the camera around this same point rather
    // than re-centering on whichever panel was last clicked.
    this.controls.enablePan = false;
    this.orbitTarget.copy(this.controls.target);
    this.defaultDirection.copy(this.camera.position).sub(this.orbitTarget).normalize();

    // Event Boxes keep their own wider FOV as a deliberate look (more dramatic perspective) - but
    // not their own fixed camera distance/min/maxDistance any more, since fitCameraToAvatar() (in
    // loadBox(), once the real box geometry exists) now derives those for every category from the
    // model's actual size instead of a guess tuned for one nominal box.
    if (this.category === 'Event Boxes') {
      this.controls.minZoom = 1;
      this.controls.maxZoom = 5;
      this.camera.fov = 20;
    }
    this.controls.update();
    this.controls.addEventListener('change', () => {
      this.updateFaceVisibility();
      const panned = this.controls.target.distanceToSquared(this.orbitTarget) > Design3dComponent.PAN_OFFSET_EPSILON_SQ;
      const distance = this.camera.position.distanceTo(this.controls.target);
      const zoomed = Math.abs(distance - this.defaultDistance) > this.defaultDistance * Design3dComponent.ZOOM_OFFSET_EPSILON_RATIO;
      const direction = this.camera.position.clone().sub(this.controls.target).normalize();
      const rotated = direction.distanceToSquared(this.defaultDirection) > Design3dComponent.ROTATE_OFFSET_EPSILON_SQ;
      this.viewChanged = panned || zoomed || rotated;
      this.cameraZoomPercent = percentForDistance(distance, this.controls.minDistance, this.controls.maxDistance, this.cameraZoomPercent);
    });

    // Alt-drag pans instead of rotating - swaps which gesture the left mouse button performs
    // rather than adding a second permanent control, since OrbitControls picks the drag mode once
    // at pointerdown time. This is a real (not screen-space-only) pan: it moves controls.target
    // along with the camera, so it does drag the pivot away from orbitTarget. That's intentional -
    // the next panel-focus click (focusPanel()/faceDefaultPanel()) already resets controls.target
    // back to the fixed orbitTarget, so panning "recenters" on the next panel pick rather than
    // staying permanently off-axis.
    window.addEventListener('keydown', this.updatePanModifierState);
    window.addEventListener('keyup', this.updatePanModifierState);
    window.addEventListener('blur', this.resetPanModifierState);

    this.resizeObserver = new ResizeObserver(() => this.onResize());
    this.resizeObserver.observe(container);
  }

  // Rotate/Pan buttons (template) call this directly; the Alt-drag listeners below call it too, with
  // altOverride forcing pan for as long as the key is held regardless of viewerTool - the same
  // momentary-override behavior this had before viewerTool existed.
  private applyViewerTool(altOverride: boolean): void {
    const pan = altOverride || this.viewerTool === 'pan';
    this.controls.mouseButtons.LEFT = pan ? MOUSE.PAN : MOUSE.ROTATE;
    this.controls.enablePan = pan;
  }

  setViewerTool(tool: 'rotate' | 'pan'): void {
    this.viewerTool = tool;
    this.applyViewerTool(false);
  }

  private readonly updatePanModifierState = (e: KeyboardEvent): void => {
    // Alt is what the design-3d-instructions copy below already promises (modifierKeyLabel + hold
    // down mouse) - Option is Mac's name for the same key, reflected as altKey either way. Not
    // also binding Cmd/metaKey, since the shipped copy never mentioned it.
    this.applyViewerTool(e.altKey);
  };

  private readonly resetPanModifierState = (): void => {
    this.applyViewerTool(false);
  };

  // A fixed camera distance (the old (4.5, 2, 0) / (15, 5, 0) positions) only looks right for the
  // one nominal box size it was eyeballed against - a much larger box just fills more of the same
  // frustum, reading as "too zoomed in". Measuring the loaded model's real bounding sphere and
  // deriving distance from it fits any box size, so no per-category guess is needed for framing.
  //
  // Sets orbitTarget to the sphere's center (was the world origin) - faceDefaultPanel() and
  // focusPanel() both orbit around orbitTarget and read "current distance" off the camera's
  // position relative to it, so running this before them means the fitted distance and center
  // propagate through every later camera move for free.
  private fitCameraToAvatar(avatar: any): void {
    const box = new Box3().setFromObject(avatar);
    const sphere = new Sphere();
    box.getBoundingSphere(sphere);
    if (!Number.isFinite(sphere.radius) || sphere.radius <= 0) {
      return;
    }
    this.avatarBoundingRadius = sphere.radius;

    const distance = fittedDistanceForAspect(this.camera.fov, this.camera.aspect, this.avatarBoundingRadius);
    this.defaultDistance = distance;

    this.orbitTarget.copy(sphere.center);
    const direction = this.camera.position.clone().sub(this.controls.target).normalize();
    this.camera.position.copy(sphere.center).addScaledVector(direction, distance);
    this.controls.target.copy(sphere.center);

    // Replaces the fixed 1..5 / 1..20 ranges - scaled off the fitted distance so the user's zoom
    // range makes sense for this specific box instead of a range tuned for a different size.
    this.controls.minDistance = distance * 0.4;
    this.controls.maxDistance = distance * 2.2;
    this.controls.update();
  }

  // Finds the real material name a Face mesh actually uses, by prefix ("0Outside"/"1Inside") -
  // see loadBox()'s comment on why guessing the exact name from route params isn't reliable. A
  // mesh's .material can be a single material or an array of them (multi-material geometry, same
  // as updateFaceVisibility() already accounts for) - checking only a bare .name would silently
  // miss the match on any mesh using the array form.
  private findMaterialNameByPrefix(avatar: any, prefix: string): string | null {
    let found: string | null = null;
    avatar.traverse((child: any) => {
      if (found || !child.isMesh) {
        return;
      }
      const candidates = Array.isArray(child.material) ? child.material : [child.material];
      const match = candidates.find((m: any) => typeof m?.name === 'string' && m.name.startsWith(prefix));
      if (match) {
        found = match.name;
      }
    });
    return found;
  }

  private loadBox(): Promise<void> {
    return new Promise((resolve) => {
      // Box.dae can reference sub-resources (chiefly baked material textures) that 404 - three.js's
      // loaders swallow that completely by default (no manager means no onError, so a missing
      // texture just leaves the material's map empty with zero feedback anywhere). Routing the
      // loader through our own manager gets us a log for diagnosis, and a hook (onLoad, fired once
      // every started sub-resource has settled) to fall back broken materials to a flat color
      // instead of rendering blank/black - see sweepBrokenTextures().
      const manager = new LoadingManager();
      manager.onError = (url) => {
        console.error(`3D preview: failed to load a resource referenced by Box.dae: ${url}`);
      };
      const colladaLoader = new ColladaLoader(manager);
      const url = `${this.environment.apiHost}${this.apiPath}${this.containerID}/Box.dae`;

      colladaLoader.load(
        url,
        (collada) => {
          if (!collada) {
            this.valid = false;
            this.showNotFoundModal();
            resolve();
            return;
          }
          const avatar = collada.scene;
          const library: any = collada.library;
          const materials = library.materials;
          manager.onLoad = () => this.sweepBrokenTextures(materials);

          // this.subcategory (a route param) doesn't reliably match the DAE's own material naming -
          // observed as e.g. "sece" against a material actually named "0OutsideSECE662", which made
          // '0Outside' + this.subcategory match nothing and fail the whole box as unusable even
          // though a perfectly good material was sitting right there. Reading the name straight off
          // a real Face mesh's material is unambiguous regardless of naming convention.
          const insideName = this.findMaterialNameByPrefix(avatar, '1Inside');
          const outsideName = this.findMaterialNameByPrefix(avatar, '0Outside');
          const insideMaterial = (insideName && materials[insideName]) || null;
          const outsideMaterial = (outsideName && materials[outsideName]) || null;
          this.insideBoxMaterial = insideMaterial;
          this.outsideBoxMaterial = outsideMaterial;

          this.valid = insideMaterial != null && outsideMaterial != null;

          if (!this.valid) {
            const meshMaterialNames = new Set<string>();
            avatar.traverse((child: any) => {
              if (!child.isMesh) {
                return;
              }
              (Array.isArray(child.material) ? child.material : [child.material]).forEach((m: any) => {
                if (typeof m?.name === 'string') {
                  meshMaterialNames.add(m.name);
                }
              });
            });
            console.error(
              `3D preview: could not find a usable outside/inside material. Mesh material names found: ${
                [...meshMaterialNames].join(', ') || '(none)'
              }`,
            );
          }

          if (this.valid) {
            const outsideCanvas = this.sideState.outside.fabricCanvas!;
            const insideCanvas = this.sideState.inside.fabricCanvas!;

            // A fresh <canvas> element's pixel buffer is transparent black until something actually
            // paints it - setting backgroundColor in the Canvas constructor doesn't guarantee that
            // paint has happened synchronously by this point, and initCanvas2dFor() otherwise only
            // renders once the first shape/text/image is added. Force a render now so the box shows
            // the white background immediately instead of reading back an unpainted (black) buffer.
            outsideCanvas.renderAll();
            insideCanvas.renderAll();

            // Both faces get their live Fabric canvas as their texture - a design authored at 1:1
            // needs no crop-in zoom/offset (that only ever compensated for the DAE's baked-in
            // placeholder graphic, which neither face uses anymore).
            this.sideState.outside.designTexture = new CanvasTexture(
              this.renderWithoutOverlayFor(outsideCanvas, () => this.toDesignAreaCanvasElementFor(outsideCanvas)),
            );
            this.sideState.outside.designTexture.colorSpace = SRGBColorSpace;
            outsideMaterial.build.map = this.sideState.outside.designTexture;
            outsideMaterial.build.needsUpdate = true;

            this.sideState.inside.designTexture = new CanvasTexture(
              this.renderWithoutOverlayFor(insideCanvas, () => this.toDesignAreaCanvasElementFor(insideCanvas)),
            );
            this.sideState.inside.designTexture.colorSpace = SRGBColorSpace;
            insideMaterial.build.map = this.sideState.inside.designTexture;
            insideMaterial.build.needsUpdate = true;
            // The inside submesh's own normal/winding faces away from every exterior camera
            // position (it's the coincident back face of the same panel - see
            // [[design3d_inside_face_geometry_coincident]]), so it needs DoubleSide to render at
            // all once updateFaceVisibility() reveals it on non-outward-facing panels.
            insideMaterial.build.side = DoubleSide;

            this.applyBoardColor();
          }

          avatar.name = this.previewName;
          avatar.translateY(this.category === 'Event Boxes' ? -0.4 : -0.05);
          this.scene.add(avatar);
          avatar.updateMatrixWorld(true);

          this.fitCameraToAvatar(avatar);
          this.buildFacePanels(avatar);
          this.faceDefaultPanel();
          this.updateFaceVisibility();

          resolve();
        },
        undefined,
        // ColladaLoader.load() routes ANY exception here - not just a network failure on Box.dae
        // itself, but also one thrown by our own onLoad callback above (it wraps
        // `onLoad(scope.parse(text, path))` in one try/catch) - previously swallowed completely
        // with no logging, so a bug partway through onLoad (e.g. after this.valid was already set
        // true) silently reverted to this hard-failure path with zero trace of why.
        (error: unknown) => {
          console.error('3D preview: Box.dae failed to load or parse.', error);
          this.valid = false;
          this.showNotFoundModal();
          resolve();
        }
      );
    });
  }

  // Runs once every sub-resource Box.dae's parse started (chiefly baked material textures) has
  // either loaded or failed - fires from the LoadingManager passed into loadBox()'s ColladaLoader.
  // The outside/inside materials always get their map overwritten with a live Fabric CanvasTexture
  // (see loadBox() above) regardless of whether their original baked texture loaded, so they never
  // false-positive here. Any other material (e.g. the edges material) left with a failed load has
  // map.image still unset (three.js never assigns it on error) - dropping the map falls that
  // material back to its own base color instead of rendering blank/black.
  private sweepBrokenTextures(materials: Record<string, any>): void {
    Object.entries(materials).forEach(([name, material]) => {
      const map = material?.build?.map;
      if (map && !map.image) {
        console.error(`3D preview: texture for material "${name}" failed to load - falling back to its base color.`);
        material.build.map = null;
        material.build.needsUpdate = true;
      }
    });
  }

  // ---- Occlusion (phase 3b) ----
  //
  // Confirmed by dumping the loaded scene graph: physical panels are Group nodes named "Face0",
  // "Face1", etc, nested at inconsistent depths under purely organizational "FaceGroupN" wrapper
  // groups (not physically meaningful themselves). Each Face group contains exactly 5 meshes: two
  // using material "0OutsideOneUp", one "1InsideOneUp", two "2EdgesOneUp" - and critically, that
  // material is the SAME shared instance across every Face in the box (confirmed empirically in
  // phase 3a: one texture swap lit up the whole box, not one panel), so hiding a panel can't be done
  // by recoloring its material - that would grey out every panel at once. Instead each hidden
  // panel's meshes get temporarily swapped onto one shared grey material, and restored to their
  // original (shared, live-textured) material once visible again.
  //
  // Visibility itself is a normal-vs-camera dot product, not raycasting: for a roughly convex
  // assembled box (true for standard styles), a panel whose outward normal points away from the
  // camera is exactly the panel on the far/hidden side. Raycasting would catch a nearer panel
  // physically occluding a technically-facing one (e.g. an overlapping flap) but that's a
  // refinement to revisit if testing across box styles shows it's actually needed.
  private buildFacePanels(avatar: any): void {
    this.facePanels = [];
    const faceGroups: any[] = [];
    avatar.traverse((obj: any) => {
      if (obj.type === 'Group' && /^Face\d+$/.test(obj.name)) {
        faceGroups.push(obj);
      }
    });

    faceGroups.forEach((group) => {
      const meshes: { mesh: Mesh; originalMaterial: Mesh['material'] }[] = [];
      const outsideMeshes: Mesh[] = [];
      group.traverse((child: any) => {
        if (!child.isMesh) {
          return;
        }
        meshes.push({ mesh: child, originalMaterial: child.material });
        // Prefix match, not an exact-name check - the outside material can be the generic
        // "0OutsideOneUp" or a subcategory-coded name like "0OutsideSECE662" depending on the box
        // style (see loadBox()'s findMaterialNameByPrefix() for why the exact suffix can't be
        // guessed from route params). Array form covers a multi-material mesh, same as
        // updateFaceVisibility().
        const meshMaterials = Array.isArray(child.material) ? child.material : [child.material];
        if (meshMaterials.some((m: any) => typeof m?.name === 'string' && m.name.startsWith('0Outside'))) {
          outsideMeshes.push(child);
        }
      });
      if (outsideMeshes.length === 0 || meshes.length === 0) {
        return;
      }
      // Each Face has two outside mesh pieces (confirmed in the mesh tree dump) - the
      // panel's full printable UV region only comes from unioning both, not just the first one.
      // computeFaceBoundaryUV() handles that same multi-piece union for the true outline too - see
      // its own comment.
      const { normal, centroid } = computeFaceGeometry(outsideMeshes[0]);
      const uvBounds = computeFaceUVBounds(outsideMeshes);
      const boundaryUV = computeFaceBoundaryUV(outsideMeshes);
      this.facePanels.push({ name: group.name, normal, centroid, meshes, uvBounds, boundaryUV });
    });
  }

  // The real inside submesh sits geometrically coincident with its outside twin, facing the
  // opposite direction (see [[design3d_inside_face_geometry_coincident]] - confirmed live, no
  // ajar/open pose in this DAE). So "show the inside design in 3D" can't be a camera trick or a
  // texture swap on the outside mesh (wrong UVs, and visually indistinguishable from actually
  // being outside artwork) - it has to reveal the real inside layer instead.
  //
  // Permanent, not tied to activeSide/the Outside-Inside toggle: a panel whose own outward
  // normal already faces the camera (outsideFacing) is a genuine exterior surface, so it always
  // shows outside content; every other panel - chiefly the ear-lock tab flaps, which already
  // read visually as "the box's open side" since they project outward rather than lying flush -
  // always shows its real inside content instead. Two earlier, toggle-gated versions were tried
  // and rejected: gating the tab flaps' content on activeSide left them blank (grey placeholder)
  // while Outside was selected, and gating the outward panel's content on activeSide made the
  // box's real exterior wall look like it had been reprinted whenever Inside was selected (see
  // [[design3d_inside_face_geometry_coincident]] for the full back-and-forth). Depth testing
  // still naturally occludes whichever panels end up geometrically behind others from the
  // camera's viewpoint. insideBoxMaterial.side is set to DoubleSide in loadBox() so the inside
  // submesh can render regardless of which way its own normal/winding faces relative to the
  // camera.
  private updateFaceVisibility(): void {
    const cameraPosition = this.camera.position;
    this.facePanels.forEach((panel) => {
      const toCamera = new Vector3().subVectors(cameraPosition, panel.centroid).normalize();
      const outsideFacing = panel.normal.dot(toCamera) > 0;
      panel.meshes.forEach(({ mesh, originalMaterial }) => {
        const materialName = Array.isArray(originalMaterial) ? undefined : originalMaterial?.name;
        mesh.material = originalMaterial;
        if (outsideFacing) {
          mesh.visible = materialName?.startsWith('1Inside') ? false : true;
        } else {
          mesh.visible = materialName?.startsWith('0Outside') ? false : true;
        }
      });
    });
  }

  // ---- Panel-click camera turn (phase 4) ----

  // Any click on the 2D canvas - empty space or a design object - is resolved to a panel via UV
  // bounding boxes. scenePoint is always canvas-absolute (real Fabric mouse events already are;
  // callers building a synthetic point pass canvas-absolute fabricImage.left/top too) - the margin
  // is subtracted here to land back in the design area's own 0..canvasWidthPx/canvasHeightPx space
  // that UV bounds are expressed in.
  private handlePanelFocusClick(scenePoint: { x: number; y: number }): void {
    const panel = findPanelAt(
      scenePoint,
      this.canvasWidthPx,
      this.canvasHeightPx,
      this.canvasMargin,
      this.facePanels,
      this.activeSide === 'inside',
    );
    if (panel) {
      this.focusPanel(panel);
    }
  }

  // A "fill" click: drop a shape sized to exactly cover whichever panel was clicked, flood-filled
  // with the pending fill (see setFillValue()) - a plain Rect for solid, a gradient-baked
  // FabricImage for linear/radial (see buildGradientFabricImage()) - no clip needed either way,
  // unlike setShapeFill()'s gradient branch, since a full-panel stamp is already rectangular. Does
  // nothing if the click landed outside every panel (e.g. the margin around the die-line).
  private fillPanelAt(scenePoint: { x: number; y: number }): void {
    const panel = findPanelAt(
      scenePoint,
      this.canvasWidthPx,
      this.canvasHeightPx,
      this.canvasMargin,
      this.facePanels,
      this.activeSide === 'inside',
    );
    if (!panel) {
      return;
    }
    const bounds = getPanelPixelBounds(
      panel,
      this.canvasWidthPx,
      this.canvasHeightPx,
      this.canvasMargin,
      this.activeSide === 'inside',
    );
    const width = bounds.right - bounds.left;
    const height = bounds.bottom - bounds.top;
    const value = this.pendingFillValue;
    // Fabric's default origin is 'center' (not 'left'/'top' - see loadGridlinesOverlay()), so
    // without this the stamp's center - not its corner - lands at (bounds.left, bounds.top),
    // shifting it up-left by half its own size instead of covering the panel.
    const placement = { left: bounds.left, top: bounds.top, originX: 'left' as const, originY: 'top' as const };

    if (value.type === 'solid') {
      const rect = new Rect({ ...placement, width, height, fill: value.hex, strokeWidth: 0 });
      (rect as unknown as { fillCmyk?: CMYKColor }).fillCmyk = value.cmyk;
      this.fabricCanvas.add(rect);
      this.fabricCanvas.setActiveObject(rect);
      this.setFillToolActive(false);
      this.fabricCanvas.requestRenderAll();
      return;
    }

    this.buildGradientFabricImage(value, width, height).then((fabricImage) => {
      fabricImage.set(placement);
      this.fabricCanvas.add(fabricImage);
      this.fabricCanvas.setActiveObject(fabricImage);
      // One-shot tool - deactivate now that a shape has been placed, so the newly-selected object
      // can immediately be moved/resized/recolored via normal selection instead of the next canvas
      // click stamping another fill.
      this.setFillToolActive(false);
      this.fabricCanvas.requestRenderAll();
    });
  }

  // Squared-distance threshold for "has an Alt-drag pushed controls.target off orbitTarget" -
  // large enough to ignore floating-point noise from OrbitControls' own per-frame update(), small
  // enough that any real pan immediately flips viewChanged. Squared so the 'change' listener can
  // compare against distanceToSquared() without a sqrt on every camera move.
  private static readonly PAN_OFFSET_EPSILON_SQ = 1e-6;

  // Fraction of defaultDistance that counts as "has a scroll/pinch zoom moved the camera off its
  // default distance" - relative rather than a fixed world-unit threshold since defaultDistance
  // itself scales with each loaded box's own bounding-sphere radius.
  private static readonly ZOOM_OFFSET_EPSILON_RATIO = 1e-3;

  // Squared-distance threshold (both sides are unit vectors) for "has a rotate-drag turned the
  // camera off defaultDirection" - about 0.6deg of arc, same "ignore per-frame float noise, catch
  // any real drag immediately" role PAN_OFFSET_EPSILON_SQ plays for panning.
  private static readonly ROTATE_OFFSET_EPSILON_SQ = 1e-4;

  private faceDefaultPanel(): void {
    const elevatedDirection = computeDefaultPanelDirection(this.facePanels);
    if (!elevatedDirection) {
      return;
    }
    this.defaultDirection.copy(elevatedDirection);
    const currentDistance = this.camera.position.distanceTo(this.controls.target);
    this.camera.position.copy(this.orbitTarget.clone().add(elevatedDirection.multiplyScalar(currentDistance)));
    this.controls.target.copy(this.orbitTarget);
    this.controls.update();
  }

  // Snaps the camera back to exactly the initial view faceDefaultPanel() computed at load (same
  // panel, same fixed elevation/azimuth, and - unlike faceDefaultPanel() - defaultDistance too, not
  // whatever distance is current). Used before the Save screenshot: instant, not animated like
  // resetView(), so the render right after it is guaranteed to reflect the reset camera rather than
  // a mid-tween frame, and any in-flight tween is cancelled so it can't overwrite this afterward.
  private resetViewToInitialAngle(): void {
    const elevatedDirection = computeDefaultPanelDirection(this.facePanels);
    if (!elevatedDirection) {
      return;
    }
    // Re-baseline in case a focusPanel() turn since load moved defaultDirection to some other
    // panel's normal - this always snaps back to the one true initial angle, so the baseline it
    // leaves behind needs to match, or the very next 'change' event would read this reset as
    // still "rotated".
    this.defaultDirection.copy(elevatedDirection);
    this.camera.position.copy(this.orbitTarget.clone().add(elevatedDirection.multiplyScalar(this.defaultDistance)));
    this.controls.target.copy(this.orbitTarget);
    this.controls.update();
    this.cameraAnimation = undefined;
  }

  // Renders the current camera state and reads it back as a PNG data URL - render() and
  // toDataURL() must run synchronously back-to-back with no await/yield in between, since the
  // browser can clear the drawing buffer once control returns to it (WebGLRenderer isn't
  // constructed with preserveDrawingBuffer, matching the old Unity builder's own screenshot, which
  // this mirrors - see requestSave()'s upload of the result as screenshot.png).
  private captureViewportPng(): string {
    this.renderer.render(this.scene, this.camera);
    return this.renderer.domElement.toDataURL('image/png');
  }

  private focusPanel(panel: FacePanel): void {
    // Approaching from the panel's own outward normal is what makes updateFaceVisibility() class
    // it outsideFacing (camera roughly aligned with the normal) and show its outside content -
    // clicking a panel while designing the inside canvas needs the opposite approach direction
    // instead, so the same visibility check lands on "not outsideFacing" and reveals that panel's
    // inside content (works well for panels that protrude from the shell, like the lock-tab flaps -
    // see updateFaceVisibility()'s own comment; a flush wall panel's inside face may still be
    // occluded by the rest of the box from any exterior camera position, same as it already was).
    const direction = this.activeSide === 'inside' ? panel.normal.clone().negate() : panel.normal;
    if (this.activeSide === 'inside') {
      // Same bearing/elevation decomposition as computeDefaultPanelDirection() - discard the
      // direction's own vertical component to get its horizontal bearing, then rebuild it at that
      // bearing's elevation plus the fixed boost above.
      const horizontal = new Vector3(direction.x, 0, direction.z);
      const bearing = horizontal.lengthSq() > 1e-6 ? horizontal.normalize() : new Vector3(1, 0, 0);
      const elevation = Math.asin(MathUtils.clamp(direction.y, -1, 1)) + INSIDE_FOCUS_ELEVATION_BOOST_RADIANS;
      direction.copy(bearing.multiplyScalar(Math.cos(elevation)).add(new Vector3(0, Math.sin(elevation), 0)));
    }
    // A deliberate, app-driven turn (clicking an element in the 2D editor) rather than a free
    // rotate-drag - re-baselines defaultDirection to this same approach direction so arriving here
    // reads as "not rotated" once the tween below settles, the same way panned/zoomed already
    // treat this as in-bounds rather than something "Reset view" should undo.
    this.defaultDirection.copy(direction).normalize();
    // Preserve the current zoom distance - only orientation should change, not how close the box
    // appears, matching how a "turn to face this panel" gesture should feel. Orbits around the
    // fixed orbitTarget, not the panel's own centroid, so the pivot never drifts panel to panel.
    const currentDistance = this.camera.position.distanceTo(this.controls.target);
    const toPosition = this.orbitTarget.clone().add(direction.clone().multiplyScalar(currentDistance));
    this.cameraAnimation = {
      fromPosition: this.camera.position.clone(),
      toPosition,
      fromTarget: this.controls.target.clone(),
      toTarget: this.orbitTarget.clone(),
      start: performance.now(),
      duration: 600,
    };
  }

  // Undoes an Alt-drag pan, a scroll/pinch zoom, and/or a free rotate-drag: recenters the pivot on
  // orbitTarget and dollies back to defaultDistance along defaultDirection - wherever the app last
  // deliberately pointed the camera (load-time default, or the last panel-focus click), not
  // necessarily the original load-time angle. Reuses the same tween stepCameraAnimation() already
  // runs for focusPanel(), so this is a smooth recenter, not a snap.
  resetView(): void {
    const toPosition = this.orbitTarget.clone().addScaledVector(this.defaultDirection, this.defaultDistance);
    this.cameraAnimation = {
      fromPosition: this.camera.position.clone(),
      toPosition,
      fromTarget: this.controls.target.clone(),
      toTarget: this.orbitTarget.clone(),
      start: performance.now(),
      duration: 600,
    };
  }

  // Dollies the camera directly to the distance the slider's percent represents, keeping the
  // camera's current orientation (same "read direction off the live position" approach as
  // resetView()). controls.update() (called every animate() frame) diffs the new position against
  // its own last-known one and dispatches 'change' on the difference, so this reuses the same
  // viewChanged/updateFaceVisibility/cameraZoomPercent bookkeeping the scroll/pinch path already
  // gets - no separate sync path needed here.
  onZoomSliderInput(percent: number): void {
    const { minDistance, maxDistance } = this.controls;
    if (!Number.isFinite(minDistance) || !Number.isFinite(maxDistance) || maxDistance <= minDistance) {
      return;
    }
    this.cameraAnimation = undefined;
    this.cameraZoomPercent = percent;
    const distance = maxDistance - (percent / 100) * (maxDistance - minDistance);
    const direction = this.camera.position.clone().sub(this.controls.target).normalize();
    this.camera.position.copy(this.controls.target).addScaledVector(direction, distance);
  }

  // The zoom slider's flanking -/+ buttons (template) - a fixed step on the same 0-100 scale the
  // slider itself uses, clamped to its ends, so repeated clicks settle at fully zoomed out/in
  // instead of erroring past them.
  stepCameraZoom(direction: 1 | -1): void {
    const percent = MathUtils.clamp(this.cameraZoomPercent + direction * CAMERA_ZOOM_SLIDER_STEP, 0, 100);
    this.onZoomSliderInput(percent);
  }

  onDividerPointerDown(event: PointerEvent): void {
    if (!this.contentRowRef.nativeElement.clientWidth) {
      return;
    }
    event.preventDefault();
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
    this.isDraggingDivider = true;
    this.dividerDragStartSplit = this.splitRatio;
    this.dividerDragStartDistance = this.camera.position.distanceTo(this.controls.target);
    this.dividerDragStartDefaultDistance = this.defaultDistance;
  }

  // Reads the drag-start baseline (onDividerPointerDown()) fresh on every move rather than
  // stepping incrementally off the previous move - an absolute from/to pair keeps the math exact
  // regardless of how many (or how few) pointermove events the browser coalesces into this drag.
  onDividerPointerMove(event: PointerEvent): void {
    if (!this.isDraggingDivider) {
      return;
    }
    const rowRect = this.contentRowRef.nativeElement.getBoundingClientRect();
    if (!rowRect.width) {
      return;
    }
    const rawPercent = ((event.clientX - rowRect.left) / rowRect.width) * 100;
    const clamped = Math.min(this.SPLIT_MAX, Math.max(this.SPLIT_MIN, rawPercent));
    this.splitRatio = clamped;
    this.compensateZoomForSplit(this.dividerDragStartSplit, clamped, this.dividerDragStartDistance, this.dividerDragStartDefaultDistance, false);
  }

  onDividerPointerUp(event: PointerEvent): void {
    if (!this.isDraggingDivider) {
      return;
    }
    (event.target as HTMLElement).releasePointerCapture(event.pointerId);
    this.isDraggingDivider = false;
    this.localStorageService.setItem(this.SPLIT_STORAGE_KEY, String(this.splitRatio));
  }

  // WCAG separator pattern: Left/Right nudge by one step, Home/End jump straight to an extreme -
  // mirrors resetSplit()'s animated jump rather than onDividerPointerMove()'s instant one, since a
  // keypress is a single discrete step, not a continuous drag.
  onDividerKeydown(event: KeyboardEvent): void {
    const STEP = 5;
    let target: number;
    if (event.key === 'ArrowLeft') {
      target = this.splitRatio - STEP;
    } else if (event.key === 'ArrowRight') {
      target = this.splitRatio + STEP;
    } else if (event.key === 'Home') {
      target = this.SPLIT_MIN;
    } else if (event.key === 'End') {
      target = this.SPLIT_MAX;
    } else {
      return;
    }
    event.preventDefault();
    this.jumpSplitTo(Math.min(this.SPLIT_MAX, Math.max(this.SPLIT_MIN, target)));
  }

  resetSplit(): void {
    this.jumpSplitTo(this.SPLIT_DEFAULT);
  }

  // Drives .design-3d-expand-preview (template): at the default split, expands the 3D preview to
  // SPLIT_MIN's complement (SPLIT_MIN/SPLIT_MAX are already the divider's own drag bounds, so this
  // is the same widest preview a manual drag could reach); away from default - whether from this
  // button or a manual drag - it resets back to SPLIT_DEFAULT instead, same jump as resetSplit().
  togglePreviewExpand(): void {
    if (this.splitRatio === this.SPLIT_DEFAULT) {
      this.jumpSplitTo(100 - this.SPLIT_DEFAULT);
    } else {
      this.resetSplit();
    }
  }

  private jumpSplitTo(target: number): void {
    if (target === this.splitRatio) {
      return;
    }
    const fromSplit = this.splitRatio;
    const fromDistance = this.camera.position.distanceTo(this.controls.target);
    const fromDefaultDistance = this.defaultDistance;
    this.splitRatio = target;
    this.compensateZoomForSplit(fromSplit, target, fromDistance, fromDefaultDistance, true);
    this.localStorageService.setItem(this.SPLIT_STORAGE_KEY, String(target));
  }

  // Compensates for the panel resize this split change is about to cause - without this, only
  // camera.aspect updates (onResize()'s ResizeObserver still fires every frame of a resize), not
  // distance, so a wider container just reveals more horizontal FOV around the same fixed-size box
  // instead of the box growing to match.
  //
  // Deliberately NOT fittedDistanceForAspect()'s tightest-axis-of-the-two logic - that picks
  // whichever of vertical/horizontal FOV is tighter, and which one that IS flips as aspect crosses
  // 1. Ratioing two distances computed on DIFFERENT axes doesn't keep the box's own width a
  // constant fraction of the container's width across that crossover - which is the actual ask
  // ("same ratio to the viewport... as it was before"), not "avoid ever cropping the box".
  //
  // The container's HEIGHT never changes across a split change (.design-2d-canvas-block/
  // .design-3d-column share the row's fixed height - SCSS) and neither does camera.fov (vertical
  // FOV) - so "pixels per radian" here is fixed, and it's the SAME fixed value in both the vertical
  // and (aspect-scaled) horizontal directions, since panelWidthPx = aspect * panelHeightPx grows by
  // exactly the same factor horizontalFov does. That makes the box's on-screen width, as a FRACTION
  // of the container's own width, depend only on distance - aspect cancels out entirely. So to keep
  // that fraction constant, distance only needs to scale by the inverse of how much the container's
  // width is about to change - not a hard reset to some newly-fitted distance, so a user who's
  // manually scroll-zoomed away from the default keeps their relative zoom level instead of
  // snapping back to it on every resize.
  //
  // Deliberately no floor here clamping this to fittedDistanceForAspect()'s own vertical-FOV fit to
  // avoid ever cropping the box - tried that once, and for any aspect that crosses 1 (which a large
  // split change often does), that floor is the SAME value fittedDistanceForAspect() would've
  // picked via its own tightest-axis logic, so it silently overrode the width-preserving distance
  // right back to the very ratio mismatch this method exists to fix. It's also a needlessly
  // pessimistic floor to begin with: it's sized off avatarBoundingRadius (the model's bounding
  // SPHERE - isotropic, same reach in every direction), while a real box is typically wider/flatter
  // than it is tall, so its actual vertical extent - what could really go outside the frame - is
  // smaller than the sphere implies. Matching the requested ratio takes priority over a crop that,
  // in practice, is smaller than this reasoning would suggest and hasn't been reported as its own
  // problem.
  //
  // `animate` picks between the two callers' very different cadences: resetSplit()/
  // onDividerKeydown() fire once per discrete jump and want the same 400ms tween the old
  // panel-swap toggle used (matching the CSS flex-basis transition's own duration), while
  // onDividerPointerMove() fires many times per drag and wants each call to land the camera
  // exactly on that move's target immediately - queuing a fresh 400ms tween on every pointermove
  // would have each new tween interrupt the last one before it settles, reading as lag rather than
  // 1:1 tracking.
  private compensateZoomForSplit(fromSplit: number, toSplit: number, fromDistance: number, fromDefaultDistance: number, animate: boolean): void {
    const from3dShare = 100 - fromSplit;
    const to3dShare = 100 - toSplit;
    if (from3dShare <= 0 || to3dShare <= 0) {
      return;
    }
    this.compensateZoomForWidthRatio(to3dShare / from3dShare, fromDistance, fromDefaultDistance, animate);
  }

  // Mirrors compensateZoomForSplit()'s own approach (see its comment for why aspect alone isn't
  // enough) for a resize the flyout causes directly instead of a splitRatio change: opening/
  // closing it takes/returns a fixed FLYOUT_WIDTH_PX from .design-2d-panels' own width (SCSS),
  // not a percentage share of it, so the width ratio here has to come from the 3D column's own
  // measured width rather than splitRatio's before/after percentages. Below FLYOUT_RESIZE_BREAKPOINT
  // the flyout slides over the panels instead of resizing them (_sidebar.scss), so there's nothing
  // to compensate there - matchMedia guards against dollying the camera for a resize that isn't
  // actually happening.
  private compensateZoomForFlyoutToggle(opening: boolean): void {
    if (!window.matchMedia(this.FLYOUT_RESIZE_BREAKPOINT).matches) {
      return;
    }
    const container = this.containerRef?.nativeElement;
    const fromWidth = container?.clientWidth;
    if (!fromWidth) {
      return;
    }
    // .design-3d-column's own flex-basis (template) is always this fixed share of
    // .design-2d-panels' width - dividing it back out gives that shared width without needing a
    // ref to .design-2d-panels itself.
    const threeDShareFraction = (100 - this.splitRatio) / 100;
    if (threeDShareFraction <= 0) {
      return;
    }
    const panelsWidthBefore = fromWidth / threeDShareFraction;
    const panelsWidthAfter = panelsWidthBefore + (opening ? -this.FLYOUT_WIDTH_PX : this.FLYOUT_WIDTH_PX);
    if (panelsWidthAfter <= 0) {
      return;
    }
    const fromDistance = this.camera.position.distanceTo(this.controls.target);
    this.compensateZoomForWidthRatio(panelsWidthAfter / panelsWidthBefore, fromDistance, this.defaultDistance, true);
  }

  private compensateZoomForWidthRatio(widthRatio: number, fromDistance: number, fromDefaultDistance: number, animate: boolean): void {
    if (this.avatarBoundingRadius <= 0) {
      return;
    }
    const targetDistance = fromDistance / widthRatio;
    const direction = this.camera.position.clone().sub(this.controls.target).normalize();
    const toPosition = this.controls.target.clone().addScaledVector(direction, targetDistance);

    // Rescales the "not zoomed" baseline by the same ratio applied to the live distance above, so
    // a split change alone never flips viewChanged - only a genuine scroll/pinch zoom, relative to
    // whatever baseline this leaves behind, does.
    this.defaultDistance = fromDefaultDistance / widthRatio;

    // Widens (never narrows) the existing zoom-limit range around the new distance so
    // OrbitControls.update() - called every animate() frame - doesn't clamp the change back out
    // before it takes effect.
    this.controls.minDistance = Math.min(this.controls.minDistance, targetDistance * 0.4);
    this.controls.maxDistance = Math.max(this.controls.maxDistance, targetDistance * 2.2);

    if (!animate) {
      this.cameraAnimation = undefined;
      this.camera.position.copy(toPosition);
      return;
    }

    this.cameraAnimation = {
      fromPosition: this.camera.position.clone(),
      toPosition,
      fromTarget: this.controls.target.clone(),
      // Only the distance changes here, not the pivot itself.
      toTarget: this.controls.target.clone(),
      start: performance.now(),
      // Matches .design-2d-canvas-block/.design-3d-column's own 0.4s transition-duration
      // (design-3d.component.scss) so the zoom settles in step with the panel resize.
      duration: 400,
    };
  }

  private stepCameraAnimation(): void {
    if (!this.cameraAnimation) {
      return;
    }
    const { fromPosition, toPosition, fromTarget, toTarget, start, duration } = this.cameraAnimation;
    const elapsed = performance.now() - start;
    const t = Math.min(elapsed / duration, 1);
    const eased = 1 - Math.pow(1 - t, 3);

    this.camera.position.lerpVectors(fromPosition, toPosition, eased);
    this.controls.target.lerpVectors(fromTarget, toTarget, eased);

    if (t >= 1) {
      this.cameraAnimation = undefined;
    }
  }

  private onResize(): void {
    const container = this.containerRef.nativeElement;
    const width = container.clientWidth;
    const height = container.clientHeight;
    if (!width || !height || !this.renderer) {
      return;
    }
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  private animate = (): void => {
    this.animationFrameId = requestAnimationFrame(this.animate);
    this.stepCameraAnimation();
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  };

  // ---- Help: Tutorial ----

  private tutorialSeenKey(): string {
    return `design3d-tutorial-seen-${this.user?.id ?? 0}`;
  }

  openTutorial(): void {
    this.localStorageService.setItem(this.tutorialSeenKey(), 'true');
    this.modalRef = this.bsModalService.show(this.tutorialVideoTemplate);
  }

  // ---- Help: Guided Tour ----
  //
  // Ported from design.component.ts's helpSection state machine (showHelpSection()/advanceTour())
  // - same 9 steps and copy, but every step anchors to a real, always-rendered element in this
  // page's own layout (see the step->anchor table in the plan this was built from), so none of the
  // old page's manually-positioned bubble CSS is needed here.
  helpView = false;
  helpSection = '';

  private static readonly TOUR_STEPS = [
    'elementExplanation',
    'patternExplanation',
    'toolbarExplanation',
    'designAreaExplanation',
    'rulerExplanation',
    'zoomExplanation',
    'customBoxExplanation',
    'viewerExplanation',
    'twoSidedExplanation',
    'addToCartExplanation',
  ];

  showHelpSection(section: string): void {
    this.helpView = true;
    this.helpSection = section;
    // patternExplanation is the only step that needs the flyout open to have something to anchor
    // to (mirrors the old page's advanceTour() opening/closing the pattern menu around this same
    // step) - closed again for every other step so it doesn't linger over later steps' anchors.
    if (section === 'patternExplanation') {
      if (this.activeCategory !== 'patterns') {
        this.toggleCategory('patterns');
      }
    } else if (this.activeCategory) {
      this.toggleCategory(this.activeCategory);
    }
  }

  hideTour(): void {
    this.helpView = false;
    this.helpSection = '';
  }

  // Shared by each step's NEXT/FINISH link and the right-arrow keyboard shortcut so they can't
  // drift out of sync - see handleTourKeydown().
  advanceTour(): void {
    const currentIndex = Design3dComponent.TOUR_STEPS.indexOf(this.helpSection);
    const nextStep = Design3dComponent.TOUR_STEPS[currentIndex + 1];
    if (nextStep) {
      this.showHelpSection(nextStep);
    } else {
      this.hideTour();
    }
  }

  // Left-arrow shortcut only - not exposed as a visible NEXT/EXIT-style link in any step, so
  // there's no click handler to share this with. No-ops on the first step.
  previousTour(): void {
    const currentIndex = Design3dComponent.TOUR_STEPS.indexOf(this.helpSection);
    const previousStep = Design3dComponent.TOUR_STEPS[currentIndex - 1];
    if (previousStep) {
      this.showHelpSection(previousStep);
    }
  }

  // A single @HostListener for window:keydown - Angular fails to attach a second one bound to the
  // same global-target event string, so the tour and toolbar shortcuts share this dispatcher (see
  // design.component.ts's handleKeydown()).
  @HostListener('window:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent): void {
    if (this.helpView) {
      this.handleTourKeydown(event);
    } else {
      this.handleToolbarKeydown(event);
    }
  }

  handleTourKeydown(event: KeyboardEvent): void {
    const key = event.key.toLowerCase();
    if (key === 'x') {
      this.hideTour();
    } else if (event.key === 'ArrowRight') {
      this.advanceTour();
    } else if (event.key === 'ArrowLeft') {
      this.previousTour();
    }
  }

  // Toolbar shortcuts, mirrored from design.component.ts's handleToolbarKeydown() (see its
  // Keyboard Shortcuts help modal). Shift+Delete, Shift+Arrows and Alt+Arrows aren't in that
  // Angular code - they're implemented inside the Unity builder itself - so their step sizes here
  // are new choices, not ports: nudge = 1/16in per press, resize = 2% scale per press, uniform on
  // both axes to avoid distorting the element. Delete is intentionally Option/Alt+Delete here
  // instead of Shift+Delete - that rebind can't be ported back to design.component.ts since its
  // Shift+Delete lives inside the Unity builder, not this Angular code.
  handleToolbarKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
      return;
    }

    // Crop owns the selection (the crop rect, not the image itself - see startImageCrop()) for as
    // long as it's open, so every other shortcut below (delete/duplicate/nudge/reorder) is
    // swallowed rather than acting on that temporary object. Escape is the one addition: crop has
    // no other keyboard way out.
    if (this.cropModeActive) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.cancelImageCrop();
      }
      return;
    }

    if (event.altKey) {
      switch (event.code) {
        case 'KeyD':
          event.preventDefault();
          if (this.selectedElementType && this.selectedElementType !== 'pattern') {
            void this.duplicateSelected();
          }
          break;
        case 'KeyU':
          event.preventDefault();
          this.undo();
          break;
        case 'KeyR':
          event.preventDefault();
          if (event.shiftKey) {
            this.redo();
          } else {
            this.toggleRulers();
          }
          break;
        case 'KeyG':
          event.preventDefault();
          this.toggleGridlines();
          break;
        case 'Equal':
          event.preventDefault();
          this.zoomIn();
          break;
        case 'Minus':
          event.preventDefault();
          this.zoomOut();
          break;
        case 'KeyF':
          event.preventDefault();
          if (this.selectedElementType && this.selectedElementType !== 'pattern') {
            this.moveForward();
          }
          break;
        case 'KeyB':
          event.preventDefault();
          if (this.selectedElementType && this.selectedElementType !== 'pattern') {
            this.moveBackward();
          }
          break;
        case 'KeyX':
          event.preventDefault();
          this.confirmClearAll();
          break;
        case 'Delete':
        case 'Backspace':
          event.preventDefault();
          this.deleteSelected();
          break;
        case 'ArrowUp':
        case 'ArrowRight':
          event.preventDefault();
          this.resizeSelected(1.02);
          break;
        case 'ArrowDown':
        case 'ArrowLeft':
          event.preventDefault();
          this.resizeSelected(1 / 1.02);
          break;
      }
      return;
    }

    if (!event.shiftKey) {
      return;
    }
    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.nudgeSelected(0, -1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.nudgeSelected(0, 1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.nudgeSelected(-1, 0);
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.nudgeSelected(1, 0);
        break;
    }
  }

  private nudgeSelected(dx: number, dy: number): void {
    const active = this.fabricCanvas.getActiveObject();
    if (!active) {
      return;
    }
    const step = this.ppi / 16;
    active.set({
      left: (active.left ?? 0) + dx * step,
      top: (active.top ?? 0) + dy * step,
    });
    active.setCoords();
    this.fabricCanvas.renderAll();
    this.pushHistory();
  }

  private resizeSelected(factor: number): void {
    const active = this.fabricCanvas.getActiveObject();
    if (!active) {
      return;
    }
    active.set({
      scaleX: (active.scaleX ?? 1) * factor,
      scaleY: (active.scaleY ?? 1) * factor,
    });
    active.setCoords();
    this.fabricCanvas.renderAll();
    this.pushHistory();
  }
}
